import { GlossaryTerm } from './config'

const glossaryTerms: GlossaryTerm[] = [
  {
    slug: '8-a',
    title: '8(a) Business Development Program',
    abbreviation: '8(a)',
    category: 'Programs',
    definition: 'An SBA program that helps small businesses owned by socially and economically disadvantaged individuals compete for federal contracts.',
    content: `## Overview

The 8(a) Business Development Program is one of the most well-known pathways into federal contracting for disadvantaged small businesses. Administered by the Small Business Administration (SBA), it provides participants with access to sole-source contracts, mentoring, training, and technical assistance over a nine-year period.

## Why It Matters in GovCon

The 8(a) program is a cornerstone of the federal government's effort to ensure contracting dollars reach underserved communities. For eligible businesses, it removes many of the barriers that make competing for government work difficult — from navigating complex procurement processes to building the past performance record agencies look for.

## Key Details

- **Eligibility**: The business must be at least 51% owned by U.S. citizens who are socially and economically disadvantaged. It must qualify as small under SBA size standards.
- **Duration**: Nine years total — a four-year developmental stage followed by a five-year transition stage.
- **Sole-Source Contracts**: Participants can receive contracts up to $4.5 million for goods and services (or $7 million for manufacturing) without competitive bidding.
- **Mentor-Protégé**: 8(a) firms can form joint ventures with larger mentors to pursue contracts they couldn't handle alone.
- **Graduation**: After nine years, businesses are expected to compete in the open market.

## How GovCon Data Can Help

GovCon Data lets you filter federal and SLED opportunities by set-aside type, making it easy to find contracts specifically reserved for 8(a) participants. The AI coaching tools can also assess your readiness for the program and help you build a stronger application.

## Related Terms

- Small Business Administration (SBA)
- Set-Aside
- Mentor-Protégé Program
- HUBZone
- Service-Disabled Veteran-Owned Small Business (SDVOSB)
- Women-Owned Small Business (WOSB)`
  },
  {
    slug: 'baa-buy-american-act',
    title: 'Buy American Act',
    abbreviation: 'BAA',
    category: 'Regulations',
    definition: 'A federal law requiring the U.S. government to prefer domestic products and materials in its procurement.',
    content: `## Overview

The Buy American Act (BAA), enacted in 1933, requires the federal government to give preference to domestically produced goods when making purchases. It applies to direct federal procurement of supplies and construction materials, ensuring that taxpayer dollars support American manufacturing and jobs.

## Why It Matters in GovCon

If you sell products or materials to the federal government, BAA compliance is not optional — it is a fundamental requirement baked into most solicitations. Failing to meet domestic content thresholds can disqualify your bid entirely. Understanding these rules is critical for manufacturers, distributors, and construction firms.

## Key Details

- **Domestic End Product**: A product must be manufactured in the U.S. and the cost of domestic components must exceed a certain percentage of all components (currently 60%, increasing to 75% by 2029).
- **Price Preference**: Domestic offers receive a price evaluation preference — typically 20% for large businesses and 30% for small businesses — making them more competitive against foreign alternatives.
- **Waivers**: Exceptions exist when domestic products are unavailable, unreasonably expensive, or when a public interest determination is made.
- **Distinct from Trade Agreements Act (TAA)**: BAA applies to purchases below certain thresholds; above those thresholds, the TAA governs and allows products from designated countries.

## Related Terms

- Federal Acquisition Regulation (FAR)
- Trade Agreements Act (TAA)
- Made in America`
  },
  {
    slug: 'best-value',
    title: 'Best Value',
    category: 'Solicitations',
    definition: 'A procurement approach where the government selects the offer that provides the greatest overall benefit, considering factors beyond just price.',
    content: `## Overview

Best value is a source selection methodology where the government evaluates proposals based on a combination of technical merit, past performance, management approach, and price — rather than simply choosing the cheapest option. The goal is to award the contract to the offeror that delivers the greatest overall benefit to the government.

## Why It Matters in GovCon

Understanding whether a solicitation uses best value or lowest price technically acceptable (LPTA) criteria fundamentally changes how you write your proposal. In best value competitions, investing in a compelling technical approach and demonstrating strong past performance can justify a higher price and still win.

## Key Details

- **Evaluation Factors**: Agencies define specific criteria (technical approach, staffing plan, past performance, price) and their relative importance in the solicitation.
- **Tradeoff Process**: The government may select a higher-priced proposal if the technical superiority justifies the additional cost.
- **Source Selection Authority (SSA)**: A designated official makes the final award decision based on the evaluation team's findings.
- **Documentation**: Agencies must document the rationale for their selection, especially when choosing other than the lowest-priced offer.

## How GovCon Data Can Help

GovCon Data's AI proposal generator helps you craft stronger technical volumes tailored to best value criteria. The opportunity analysis tools also highlight evaluation factors so you can prioritize what matters most to the agency.

## Related Terms

- Lowest Price Technically Acceptable (LPTA)
- Source Selection
- Past Performance
- Request for Proposal (RFP)`
  },
  {
    slug: 'bpa-blanket-purchase-agreement',
    title: 'Blanket Purchase Agreement',
    abbreviation: 'BPA',
    category: 'Contracts',
    definition: 'A simplified method of filling anticipated repetitive needs for supplies or services by establishing charge accounts with qualified vendors.',
    content: `## Overview

A Blanket Purchase Agreement (BPA) is a streamlined contracting vehicle that agencies use when they anticipate recurring purchases of similar goods or services. Rather than issuing a new contract each time, the agency sets up a BPA with one or more vendors, then places individual orders against it as needs arise.

## Why It Matters in GovCon

BPAs represent a significant source of recurring revenue for contractors. Once you hold a BPA, individual orders flow with minimal additional competition, making it one of the more efficient ways to do business with the government. Many agencies use BPAs for IT services, office supplies, staffing, and professional services.

## Key Details

- **Established Against Existing Contracts**: BPAs are typically set up against GSA Schedules or other existing contract vehicles.
- **Ordering Procedures**: Individual orders (called "calls") are placed against the BPA. Competition requirements depend on the dollar value and number of BPA holders.
- **Duration**: Usually one year with options to extend, though the underlying contract vehicle may govern the maximum period.
- **No Guaranteed Minimum**: Unlike IDIQ contracts, BPAs typically do not guarantee a minimum order volume.

## How GovCon Data Can Help

Track active BPA opportunities and upcoming recompetitions through GovCon Data's federal opportunity search. The bid management tools help you manage multiple BPA orders and deadlines in one place.

## Related Terms

- GSA Schedule
- Indefinite Delivery/Indefinite Quantity (IDIQ)
- Task Order
- Delivery Order`
  },
  {
    slug: 'bid-bond',
    title: 'Bid Bond',
    category: 'Finance',
    definition: 'A guarantee submitted with a bid that ensures the bidder will enter into a contract if selected and provide required performance and payment bonds.',
    content: `## Overview

A bid bond is a type of surety bond submitted alongside a proposal or bid for a government contract. It guarantees that if the bidder wins the award, they will follow through — signing the contract and furnishing any required performance and payment bonds. If the bidder backs out, the surety company pays the government the difference between the winning bid and the next acceptable offer, up to the bond amount.

## Why It Matters in GovCon

Bid bonds are most common in construction and large service contracts. They protect the government from bidders who submit unrealistically low prices and then refuse to perform. For contractors, having bonding capacity is a prerequisite for competing on many lucrative projects.

## Key Details

- **Typical Amount**: Usually 20% of the bid price, though requirements vary by solicitation.
- **Surety Company**: Bonds must be issued by a surety listed on the Treasury Department's approved list (Circular 570).
- **Forfeit Conditions**: If the winning bidder refuses to execute the contract or fails to provide required bonds, the bid bond is forfeited.
- **Related Bonds**: Performance bonds (guaranteeing completion) and payment bonds (guaranteeing subcontractor/supplier payment) are typically required after award.

## Related Terms

- Performance Bond
- Payment Bond
- Surety
- Miller Act`
  },
  {
    slug: 'clin-contract-line-item-number',
    title: 'Contract Line Item Number',
    abbreviation: 'CLIN',
    category: 'Contracts',
    definition: 'A numbered item in a contract that identifies a specific deliverable, service, or unit of work along with its quantity and price.',
    content: `## Overview

A Contract Line Item Number (CLIN) is the basic building block of a government contract's pricing structure. Each CLIN identifies a distinct deliverable, service, or category of work, along with its quantity, unit of measure, and unit price. CLINs make it possible to track costs, manage deliveries, and process invoices at a granular level.

## Why It Matters in GovCon

CLINs directly affect how you price proposals, invoice for work, and report on contract performance. Mispricing a CLIN or misunderstanding its scope can lead to underbilling, overbilling, or compliance issues. They are also how the government exercises options and modifies contract scope.

## Key Details

- **Structure**: Each CLIN has a four-digit number (e.g., 0001, 0002). Sub-line items (SLINs) add two more digits for finer breakdowns.
- **Types**: CLINs can cover supplies, services, data deliverables, or option periods.
- **Pricing**: Each CLIN specifies a contract type (firm fixed price, cost-reimbursable, etc.) and pricing arrangement.
- **Invoicing**: Contractors invoice against specific CLINs, and the government tracks obligations and expenditures at the CLIN level.

## Related Terms

- Firm Fixed Price (FFP)
- Cost Plus Fixed Fee (CPFF)
- Delivery Order
- Task Order`
  },
  {
    slug: 'co-contracting-officer',
    title: 'Contracting Officer',
    abbreviation: 'CO',
    category: 'Security',
    definition: 'The government official with the authority to enter into, administer, and terminate contracts on behalf of the U.S. government.',
    content: `## Overview

The Contracting Officer (CO) is the only person in the government with the legal authority to obligate federal funds through contracts. They sign awards, approve modifications, issue task orders, and can terminate contracts. No commitment from any other government employee is binding unless the CO authorizes it.

## Why It Matters in GovCon

Understanding the CO's role is essential for every contractor. If someone other than the CO directs you to perform additional work or change scope, you have no contractual obligation — and no guarantee of payment. Always get direction in writing from the CO.

## Key Details

- **Authority**: COs hold a warrant that specifies the dollar limits of their contracting authority.
- **Types**: Procuring Contracting Officers (PCOs) award new contracts; Administrative Contracting Officers (ACOs) manage them post-award; Termination Contracting Officers (TCOs) handle contract closures.
- **Delegation**: COs may designate a Contracting Officer's Representative (COR) to handle day-to-day technical oversight, but the COR cannot authorize changes or obligate funds.
- **Communications**: All formal contract communications should go through or be acknowledged by the CO.

## Related Terms

- Contracting Officer's Representative (COR)
- Administrative Contracting Officer (ACO)
- Contract Modification
- Unauthorized Commitment`
  },
  {
    slug: 'cor-contracting-officers-representative',
    title: "Contracting Officer's Representative",
    abbreviation: 'COR',
    category: 'Security',
    definition: "A government employee designated by the contracting officer to monitor contractor performance and serve as the technical point of contact.",
    content: `## Overview

The Contracting Officer's Representative (COR) is a government employee assigned to be the day-to-day technical liaison between the agency and the contractor. The COR monitors performance, reviews deliverables, and reports issues to the Contracting Officer — but critically, the COR cannot modify the contract or direct changes to scope.

## Why It Matters in GovCon

The COR is usually your most frequent point of contact during contract execution. Building a strong working relationship with the COR leads to smoother performance, quicker issue resolution, and better past performance evaluations. However, always remember that only the CO has binding authority.

## Key Details

- **Appointment**: CORs are formally designated in writing by the CO, with specific duties and limitations outlined.
- **Responsibilities**: Inspecting deliverables, monitoring schedules, reviewing invoices for accuracy, documenting performance, and reporting to the CO.
- **Limitations**: Cannot authorize work beyond the contract scope, approve additional funding, or make binding commitments.
- **Training**: CORs must complete required training and maintain their certification (FAC-COR levels I, II, or III depending on contract complexity).

## Related Terms

- Contracting Officer (CO)
- Quality Assurance Surveillance Plan (QASP)
- Past Performance
- Performance Work Statement (PWS)`
  },
  {
    slug: 'competitive-range',
    title: 'Competitive Range',
    category: 'Solicitations',
    definition: 'The group of offerors selected by the government as having a reasonable chance of being awarded the contract, based on initial proposal evaluations.',
    content: `## Overview

The competitive range is the subset of proposals that the government determines have a reasonable chance of being selected for award after initial evaluations. Only offerors in the competitive range are invited to participate in discussions (negotiations) with the agency. Those outside the competitive range are eliminated from competition.

## Why It Matters in GovCon

Making the competitive range is the critical first hurdle in a negotiated procurement. If your proposal does not clear this bar, you are out — no chance to improve your offer or address weaknesses. This is why initial proposal quality matters so much.

## Key Details

- **Determination**: Based on ratings across all evaluation factors (technical, past performance, price) relative to the solicitation's stated criteria.
- **Discussions**: The government holds discussions with competitive range offerors, pointing out weaknesses and deficiencies so they can submit revised proposals (Final Proposal Revisions).
- **Efficiency**: The CO may limit the competitive range to the greatest number that will permit efficient competition.
- **Notification**: Offerors excluded from the competitive range receive written notice and may request a debriefing.

## How GovCon Data Can Help

GovCon Data's AI proposal tools help you write stronger initial submissions that address evaluation criteria directly, improving your chances of making the competitive range on the first pass.

## Related Terms

- Best Value
- Request for Proposal (RFP)
- Debriefing
- Final Proposal Revision`
  },
  {
    slug: 'cpff-cost-plus-fixed-fee',
    title: 'Cost Plus Fixed Fee',
    abbreviation: 'CPFF',
    category: 'Contracts',
    definition: 'A contract type where the government reimburses the contractor for allowable costs plus a predetermined fixed fee representing profit.',
    content: `## Overview

A Cost Plus Fixed Fee (CPFF) contract reimburses the contractor for all allowable, allocable, and reasonable costs incurred during performance, plus a fixed dollar amount as profit (the "fee"). The fee does not change regardless of actual costs, giving the contractor limited financial risk while keeping the government responsible for cost overruns.

## Why It Matters in GovCon

CPFF contracts are used when the scope of work is too uncertain to establish a firm fixed price. They are common in research and development, complex IT projects, and advisory services where requirements evolve. Understanding cost accounting standards is essential for contractors performing under CPFF arrangements.

## Key Details

- **Allowable Costs**: Only costs that comply with FAR Part 31 cost principles are reimbursable. Unallowable costs (entertainment, lobbying, etc.) are excluded.
- **Fixed Fee**: Negotiated at award and does not change unless the contract scope changes. Typically ranges from 6% to 10% of estimated costs.
- **Variants**: CPFF contracts come in completion form (a defined deliverable) and term form (a level of effort over a period).
- **Auditing**: DCAA audits are common to verify that billed costs are allowable and properly allocated.

## Related Terms

- Firm Fixed Price (FFP)
- Cost Accounting Standards (CAS)
- Defense Contract Audit Agency (DCAA)
- Allowable Costs`
  },
  {
    slug: 'dba-davis-bacon-act',
    title: 'Davis-Bacon Act',
    abbreviation: 'DBA',
    category: 'Regulations',
    definition: 'A federal law requiring contractors on federally funded construction projects to pay workers prevailing local wages.',
    content: `## Overview

The Davis-Bacon Act (1931) requires contractors and subcontractors working on federally funded or assisted construction projects worth over $2,000 to pay their laborers and mechanics no less than the locally prevailing wages and fringe benefits. The Department of Labor determines these prevailing wage rates for each geographic area.

## Why It Matters in GovCon

If you perform construction work for the federal government, Davis-Bacon compliance is mandatory. Violations can result in contract termination, debarment from future federal work, and liability for back wages. Accurately factoring prevailing wages into your bid pricing is critical for both competitiveness and profitability.

## Key Details

- **Covered Projects**: Federally funded or assisted construction, alteration, or repair of public buildings and works exceeding $2,000.
- **Wage Determinations**: Published by the Department of Labor for specific counties and types of construction (building, heavy, highway, residential).
- **Certified Payrolls**: Contractors must submit weekly certified payroll reports demonstrating compliance.
- **Enforcement**: The Department of Labor's Wage and Hour Division investigates complaints and can withhold contract payments to cover underpayments.

## Related Terms

- Prevailing Wage
- Wage Determination
- Service Contract Act
- Miller Act`
  },
  {
    slug: 'dcaa-defense-contract-audit-agency',
    title: 'Defense Contract Audit Agency',
    abbreviation: 'DCAA',
    category: 'Agencies',
    definition: 'The DoD agency responsible for auditing defense contractor costs, pricing, and accounting systems.',
    content: `## Overview

The Defense Contract Audit Agency (DCAA) is the federal agency that audits the financial records and accounting systems of defense contractors. DCAA's mission is to ensure that the Department of Defense pays fair and reasonable prices for goods and services by verifying that contractor costs are allowable, allocable, and reasonable.

## Why It Matters in GovCon

If you hold cost-reimbursement contracts or negotiate prices based on cost data, DCAA will likely audit your books. A clean DCAA audit is a significant competitive advantage — it signals to agencies that your accounting practices are reliable. Conversely, audit findings can delay payments, trigger investigations, and damage your reputation.

## Key Details

- **Types of Audits**: Incurred cost audits (verifying actual costs billed), forward pricing audits (evaluating proposed costs), and accounting system audits (assessing internal controls).
- **Adequate Accounting System**: DCAA evaluates whether your system meets the criteria in DFARS 252.242-7006. An "adequate" determination is required for cost-type contracts.
- **Indirect Rates**: DCAA reviews overhead, G&A, and fringe benefit rates to ensure they are properly calculated and applied.
- **Timeliness**: Incurred cost submissions are due within six months of your fiscal year end.

## Related Terms

- Cost Plus Fixed Fee (CPFF)
- Allowable Costs
- Defense Federal Acquisition Regulation Supplement (DFARS)
- Indirect Rates`
  },
  {
    slug: 'dfars-defense-federal-acquisition-regulation-supplement',
    title: 'Defense Federal Acquisition Regulation Supplement',
    abbreviation: 'DFARS',
    category: 'Regulations',
    definition: 'The supplement to the FAR that contains acquisition regulations specific to the Department of Defense.',
    content: `## Overview

The Defense Federal Acquisition Regulation Supplement (DFARS) extends and supplements the Federal Acquisition Regulation (FAR) with rules and clauses unique to Department of Defense (DoD) procurement. If FAR is the baseline rulebook for all federal acquisitions, DFARS adds the DoD-specific chapters covering everything from cybersecurity requirements to specialty metals restrictions.

## Why It Matters in GovCon

Any contractor working with the DoD must comply with both FAR and DFARS. Key DFARS clauses — like DFARS 252.204-7012 (safeguarding covered defense information) and DFARS 252.225-7001 (Buy American restrictions for defense articles) — carry significant compliance obligations that can affect your eligibility and operations.

## Key Details

- **Structure**: Mirrors FAR's numbering system but uses the 2xx series (e.g., DFARS 215.404 supplements FAR 15.404).
- **Cybersecurity**: DFARS 252.204-7012 requires contractors to implement NIST SP 800-171 security controls and report cyber incidents within 72 hours.
- **Cost Accounting**: Additional requirements for CAS-covered contracts beyond what FAR prescribes.
- **Subcontracting**: Specific flow-down requirements that prime contractors must pass to subcontractors.

## How GovCon Data Can Help

GovCon Data's regulation monitoring keeps you informed when DFARS clauses change, and the compliance tracking features help ensure your contracts meet current DoD requirements.

## Related Terms

- Federal Acquisition Regulation (FAR)
- NIST SP 800-171
- Cybersecurity Maturity Model Certification (CMMC)
- Defense Contract Audit Agency (DCAA)`
  },
  {
    slug: 'do-delivery-order',
    title: 'Delivery Order',
    abbreviation: 'DO',
    category: 'Contracts',
    definition: 'An order placed against an existing contract for the delivery of supplies or materials.',
    content: `## Overview

A delivery order (DO) is an order for supplies or materials placed against an existing indefinite-delivery contract (such as an IDIQ or GSA Schedule). Instead of awarding a new standalone contract, the government issues a delivery order that specifies the items, quantities, delivery locations, and schedule under the terms already established.

## Why It Matters in GovCon

Delivery orders are how much of the government's day-to-day buying actually happens. Winning the underlying contract vehicle is step one — the real revenue comes from competing for and receiving individual delivery orders. Tracking upcoming DOs and responding quickly is essential for maximizing contract value.

## Key Details

- **Issued Under**: IDIQ contracts, BPAs, GSA Schedules, and other indefinite-delivery vehicles.
- **Competition**: Depending on the contract type and order value, DOs may require fair opportunity competition among contract holders or can be issued on a sole-source basis.
- **Funding**: Each DO is separately funded and may span different fiscal years.
- **Distinct from Task Orders**: Delivery orders are for supplies/products; task orders are for services. In practice the terms are often used interchangeably.

## How GovCon Data Can Help

GovCon Data tracks delivery orders across federal contract vehicles, helping you identify ordering activity and compete more effectively for individual orders under your existing contracts.

## Related Terms

- Task Order
- Indefinite Delivery/Indefinite Quantity (IDIQ)
- GSA Schedule
- Blanket Purchase Agreement (BPA)`
  },
  {
    slug: 'edwosb-economically-disadvantaged-women-owned-small-business',
    title: 'Economically Disadvantaged Women-Owned Small Business',
    abbreviation: 'EDWOSB',
    category: 'Programs',
    definition: 'A subcategory of WOSB for women-owned small businesses where the owners are also economically disadvantaged, eligible for additional sole-source contracts.',
    content: `## Overview

The Economically Disadvantaged Women-Owned Small Business (EDWOSB) designation is a subset of the Women-Owned Small Business (WOSB) program. EDWOSBs are eligible for the same set-aside contracts as WOSBs, plus additional sole-source contract opportunities in industries where women are underrepresented. The program aims to level the playing field for women business owners facing both social and financial barriers.

## Why It Matters in GovCon

EDWOSB certification opens doors to contracts that are not available to the general small business population. Federal agencies have specific goals for awarding contracts to WOSBs and EDWOSBs, creating a dedicated pipeline of opportunities for qualifying businesses.

## Key Details

- **Economic Disadvantage**: The woman owner's personal net worth must not exceed $750,000 (excluding ownership in the business and primary residence), with adjusted gross income under $350,000 averaged over three years and total assets under $6 million.
- **Certification**: Must be certified through SBA or an approved third-party certifier.
- **Sole-Source Threshold**: EDWOSBs can receive sole-source contracts up to $4.5 million for services and $7 million for manufacturing.
- **NAICS Codes**: Set-asides are limited to industries where SBA has determined women are substantially underrepresented.

## How GovCon Data Can Help

Filter opportunities by EDWOSB set-aside type to find contracts reserved specifically for your business. GovCon Data's AI coaching can also help you evaluate your certification eligibility.

## Related Terms

- Women-Owned Small Business (WOSB)
- Small Business Administration (SBA)
- Set-Aside
- 8(a) Business Development Program`
  },
  {
    slug: 'evm-earned-value-management',
    title: 'Earned Value Management',
    abbreviation: 'EVM',
    category: 'Management',
    definition: 'A project management methodology that integrates scope, schedule, and cost data to measure project performance and progress objectively.',
    content: `## Overview

Earned Value Management (EVM) is a disciplined approach to measuring project performance by comparing the planned value of work, the earned value of work actually completed, and the actual costs incurred. It answers three fundamental questions: How much work was planned? How much was accomplished? How much did it cost?

## Why It Matters in GovCon

EVM is required on many DoD and civilian agency contracts above certain dollar thresholds. It provides early warning signals when projects are trending over budget or behind schedule, giving both the contractor and the government time to take corrective action before problems become critical.

## Key Details

- **Planned Value (PV)**: The budgeted cost of work scheduled to be completed by a given date.
- **Earned Value (EV)**: The budgeted cost of work actually completed.
- **Actual Cost (AC)**: The real cost incurred for the work completed.
- **Key Metrics**: Cost Performance Index (CPI = EV/AC) and Schedule Performance Index (SPI = EV/PV). Values below 1.0 indicate overruns or delays.
- **ANSI/EIA-748**: The standard that defines the 32 guidelines for a compliant EVM system.

## Related Terms

- Acquisition Program Baseline (APB)
- Work Breakdown Structure (WBS)
- Cost Plus Fixed Fee (CPFF)
- Contract Line Item Number (CLIN)`
  },
  {
    slug: 'far-federal-acquisition-regulation',
    title: 'Federal Acquisition Regulation',
    abbreviation: 'FAR',
    category: 'Regulations',
    definition: 'The primary set of rules governing how the federal government purchases goods and services, covering everything from competition requirements to contract administration.',
    content: `## Overview

The Federal Acquisition Regulation (FAR) is the principal set of rules that governs the federal government's acquisition process. Codified in Title 48 of the Code of Federal Regulations, the FAR covers the entire lifecycle of a contract — from planning and solicitation through award, administration, and closeout. It is jointly issued by the Department of Defense, General Services Administration, and NASA.

## Why It Matters in GovCon

The FAR is the rulebook for federal contracting. Every solicitation, proposal, contract clause, and dispute resolution process traces back to its provisions. Contractors who understand the FAR can write more compliant proposals, negotiate better terms, and avoid costly mistakes. Ignorance of the FAR is not a defense.

## Key Details

- **Structure**: 53 parts organized into eight subchapters covering general provisions, competition, contract types, socioeconomic programs, cost principles, and more.
- **Key Parts**: Part 12 (commercial items), Part 13 (simplified acquisition), Part 15 (negotiated procurement), Part 19 (small business programs), Part 31 (cost principles).
- **Supplements**: Individual agencies publish supplements (DFARS for DoD, GSAM for GSA, etc.) that add agency-specific rules on top of the FAR.
- **Updates**: The FAR is updated through Federal Acquisition Circulars (FACs) that are published periodically.

## How GovCon Data Can Help

GovCon Data's regulation monitoring features track FAR updates and help you understand which clauses apply to your contracts. The AI coaching tools can explain specific FAR references found in solicitations.

## Related Terms

- Defense Federal Acquisition Regulation Supplement (DFARS)
- Contracting Officer (CO)
- Simplified Acquisition Threshold
- Competition in Contracting Act (CICA)`
  },
  {
    slug: 'ffp-firm-fixed-price',
    title: 'Firm Fixed Price',
    abbreviation: 'FFP',
    category: 'Contracts',
    definition: 'A contract type where the price is set at award and does not change regardless of the contractor\'s actual costs, placing maximum risk on the contractor.',
    content: `## Overview

A Firm Fixed Price (FFP) contract establishes a price that remains constant regardless of the contractor's actual costs of performance. The contractor bears full financial responsibility — if costs run over the agreed price, the contractor absorbs the loss; if costs come in under, the contractor keeps the savings. FFP is the government's preferred contract type.

## Why It Matters in GovCon

FFP contracts are the most common type in federal procurement because they offer the government price certainty and minimal administrative burden. For contractors, accurate cost estimation is critical — underpricing an FFP contract can be financially devastating, while overpricing makes you uncompetitive.

## Key Details

- **Risk Allocation**: Maximum risk on the contractor, minimum on the government.
- **When Used**: Appropriate when requirements are well-defined, specifications are clear, and costs can be estimated with reasonable certainty.
- **Profit Incentive**: Contractors are motivated to control costs since any savings go directly to the bottom line.
- **Modifications**: Price changes require a formal contract modification signed by the Contracting Officer, typically due to scope changes rather than cost overruns.
- **FAR Preference**: FAR 16.103 states that FFP contracts shall be used when the risk allows and are preferred over cost-type contracts.

## Related Terms

- Cost Plus Fixed Fee (CPFF)
- Contract Line Item Number (CLIN)
- Contract Modification
- Contracting Officer (CO)`
  },
  {
    slug: 'fss-federal-supply-schedule',
    title: 'Federal Supply Schedule',
    abbreviation: 'FSS',
    category: 'Contracts',
    definition: 'Long-term government-wide contracts with commercial firms that provide federal agencies access to products and services at pre-negotiated prices.',
    content: `## Overview

The Federal Supply Schedule (FSS) program — commonly known as GSA Schedules or the Multiple Award Schedule (MAS) — provides federal agencies with a streamlined way to purchase commercial products and services. GSA negotiates pricing and terms with vendors, who are then listed on the schedule. Agencies can place orders directly without conducting a full competitive procurement.

## Why It Matters in GovCon

Having a GSA Schedule is often described as having a "hunting license" for government sales. It does not guarantee revenue, but it dramatically simplifies the buying process for agencies, making schedule holders the path of least resistance for many purchases. It is one of the most popular entry points into federal contracting.

## Key Details

- **Consolidation**: GSA consolidated dozens of individual schedules into a single Multiple Award Schedule (MAS) in 2019.
- **Contract Length**: 20-year maximum (five-year base period plus three five-year options).
- **Pricing**: Vendors offer their best commercial pricing, which GSA negotiates further. Prices are capped but can be discounted on individual orders.
- **Ordering**: Agencies use GSA Advantage (the online shopping platform) or contact schedule holders directly for larger buys.
- **BPA Leverage**: Agencies frequently establish BPAs against GSA Schedules for recurring needs.

## How GovCon Data Can Help

GovCon Data helps you identify which agencies are buying through GSA Schedules and track individual task and delivery orders issued under schedule contracts.

## Related Terms

- GSA Schedule
- Multiple Award Schedule (MAS)
- Blanket Purchase Agreement (BPA)
- General Services Administration (GSA)`
  },
  {
    slug: 'gao-government-accountability-office',
    title: 'Government Accountability Office',
    abbreviation: 'GAO',
    category: 'Agencies',
    definition: 'The independent congressional agency that investigates federal spending, audits government programs, and adjudicates bid protests.',
    content: `## Overview

The Government Accountability Office (GAO) is an independent, nonpartisan agency that works for Congress. Often called the "congressional watchdog," GAO audits federal agencies, evaluates government programs, and investigates how taxpayer dollars are spent. For contractors, GAO's most direct role is adjudicating bid protests — formal challenges to contract award decisions.

## Why It Matters in GovCon

GAO bid protests are one of the primary mechanisms contractors use to challenge unfair or improper award decisions. If you believe an agency violated procurement regulations in evaluating your proposal or awarding a contract, a GAO protest is a relatively fast and cost-effective remedy. GAO sustains roughly 12-15% of protests on the merits.

## Key Details

- **Bid Protests**: Must be filed within 10 days of knowing the basis for protest (or within 5 days of a debriefing). GAO issues a decision within 100 days.
- **CICA Stay**: When a protest is filed before award or within 10 days after, the agency must suspend contract performance (the "automatic stay") pending GAO's decision.
- **Reports and Audits**: GAO publishes reports on virtually every area of federal spending, providing valuable market intelligence for contractors.
- **Recommendations**: GAO recommendations are not legally binding, but agencies comply with the vast majority.

## Related Terms

- Bid Protest
- Debriefing
- Competition in Contracting Act (CICA)
- Contracting Officer (CO)`
  },
  {
    slug: 'gsa-general-services-administration',
    title: 'General Services Administration',
    abbreviation: 'GSA',
    category: 'Agencies',
    definition: 'The federal agency responsible for managing government buildings, providing products and services to agencies, and administering the GSA Schedule program.',
    content: `## Overview

The General Services Administration (GSA) is the federal agency that serves as the government's landlord, purchasing agent, and shared services provider. GSA manages federal buildings, negotiates government-wide contracts (including the GSA Schedule/MAS program), operates the GSA Advantage online marketplace, and provides acquisition services that other agencies use to buy goods and services efficiently.

## Why It Matters in GovCon

GSA is one of the most important agencies for contractors because it administers the contract vehicles that agencies across the government use to make purchases. Getting on a GSA Schedule opens your products or services to the entire federal marketplace. GSA also sets policy and standards that influence procurement government-wide.

## Key Details

- **GSA Schedules/MAS**: The Multiple Award Schedule program — the single largest vehicle for commercial buying across the federal government.
- **GSA Advantage**: The online ordering platform where agencies browse and purchase from schedule holders.
- **GWAC Vehicles**: GSA administers major government-wide acquisition contracts like Alliant 2 and OASIS+ for IT and professional services.
- **FAS**: The Federal Acquisition Service within GSA handles procurement policy and operations.
- **PBS**: The Public Buildings Service manages over 370 million square feet of federal space.

## How GovCon Data Can Help

GovCon Data tracks opportunities flowing through GSA contract vehicles and helps you monitor which agencies are actively buying through GSA programs.

## Related Terms

- Federal Supply Schedule (FSS)
- Multiple Award Schedule (MAS)
- Government-Wide Acquisition Contract (GWAC)
- GSA Advantage`
  },
  {
    slug: 'gwac-government-wide-acquisition-contract',
    title: 'Government-Wide Acquisition Contract',
    abbreviation: 'GWAC',
    category: 'Contracts',
    definition: 'A pre-competed, multiple-award IDIQ contract that any federal agency can use to purchase IT products and services.',
    content: `## Overview

A Government-Wide Acquisition Contract (GWAC) is a type of IDIQ contract specifically for information technology that is pre-competed and available for use by any federal agency. GWACs eliminate the need for individual agencies to conduct their own IT procurements from scratch, saving time and ensuring competitive pricing.

## Why It Matters in GovCon

GWACs are among the highest-value contract vehicles in the federal IT market. Winning a position on a GWAC gives you access to task orders from every agency in the government. Major GWACs like Alliant 2, OASIS+, and CIO-SP3 collectively represent tens of billions of dollars in annual spending.

## Key Details

- **IT Only**: By regulation, GWACs are limited to information technology acquisitions.
- **OMB Designation**: Only contracts approved by the Office of Management and Budget (OMB) through the GWAC program can use this designation.
- **Administered By**: Typically managed by GSA or NIH, with task orders issued by any federal agency.
- **Competition**: Task orders placed against GWACs follow fair opportunity procedures — all GWAC holders get a chance to compete.
- **Major GWACs**: Alliant 2 (GSA), OASIS+ (GSA), CIO-SP3/CIO-SP4 (NIH), VETS 2 (VA).

## How GovCon Data Can Help

GovCon Data monitors task order solicitations across major GWAC vehicles, helping you spot opportunities and track competitor wins.

## Related Terms

- Indefinite Delivery/Indefinite Quantity (IDIQ)
- Task Order
- General Services Administration (GSA)
- Multiple Award Schedule (MAS)`
  },
  {
    slug: 'hubzone-historically-underutilized-business-zone',
    title: 'Historically Underutilized Business Zone',
    abbreviation: 'HUBZone',
    category: 'Programs',
    definition: 'An SBA program that gives preferential access to federal contracts for small businesses located in economically distressed areas.',
    content: `## Overview

The HUBZone program encourages economic development in historically underutilized business zones by giving certified small businesses preferential access to federal contracting opportunities. To qualify, a business must maintain its principal office in a HUBZone and employ at least 35% of its workers from HUBZone residents.

## Why It Matters in GovCon

HUBZone certification is a powerful competitive advantage. The federal government has a statutory goal of awarding at least 3% of all federal contracting dollars to HUBZone-certified firms. This translates to billions of dollars in set-aside and sole-source opportunities each year.

## Key Details

- **Eligibility**: Small business, principal office in a HUBZone, at least 35% of employees reside in a HUBZone, and owned/controlled by U.S. citizens.
- **HUBZone Map**: SBA maintains an interactive map at maps.certify.sba.gov showing qualified areas (including high unemployment areas, Indian lands, military base closures, and disaster areas).
- **Price Preference**: HUBZone firms receive a 10% price evaluation preference in full and open competitions.
- **Set-Asides**: Contracts can be set aside exclusively for HUBZone firms, and sole-source awards up to $4.5M (services) or $7M (manufacturing) are allowed.
- **Recertification**: Firms must recertify every three years and maintain compliance continuously.

## How GovCon Data Can Help

Use GovCon Data to filter federal opportunities by HUBZone set-aside and track your pipeline of HUBZone-eligible contracts in the bid management dashboard.

## Related Terms

- Small Business Administration (SBA)
- Set-Aside
- 8(a) Business Development Program
- Women-Owned Small Business (WOSB)`
  },
  {
    slug: 'idiq-indefinite-delivery-indefinite-quantity',
    title: 'Indefinite Delivery/Indefinite Quantity',
    abbreviation: 'IDIQ',
    category: 'Contracts',
    definition: 'A contract that provides for an indefinite quantity of supplies or services during a fixed period, with orders placed as needs arise.',
    content: `## Overview

An Indefinite Delivery/Indefinite Quantity (IDIQ) contract establishes a framework for an agency to order supplies or services over a set period without committing to a specific total quantity at award. The contract specifies a minimum and maximum value, and the government places individual task orders (for services) or delivery orders (for supplies) as requirements materialize.

## Why It Matters in GovCon

IDIQs are the backbone of large-scale government procurement. Many of the biggest contract vehicles — GWACs, agency-specific IT contracts, professional services contracts — are structured as IDIQs. Winning an IDIQ position is often the gateway to sustained revenue streams from individual orders over five to ten years.

## Key Details

- **Minimum/Maximum**: Every IDIQ must state a guaranteed minimum (which the government is obligated to order) and a ceiling (the maximum value).
- **Ordering Period**: Typically five years (one base plus four option years), though some extend to ten years.
- **Fair Opportunity**: For multiple-award IDIQs, each order must give all contract holders a fair opportunity to compete, with limited exceptions.
- **Single vs. Multiple Award**: Single-award IDIQs give all orders to one contractor; multiple-award IDIQs distribute orders among several winners.
- **Task Orders vs. Delivery Orders**: Task orders are for services; delivery orders are for supplies.

## How GovCon Data Can Help

GovCon Data tracks IDIQ solicitations and individual task/delivery orders across federal agencies, helping you identify both prime contract opportunities and orders under vehicles you already hold.

## Related Terms

- Task Order
- Delivery Order
- Government-Wide Acquisition Contract (GWAC)
- Blanket Purchase Agreement (BPA)
- Federal Supply Schedule (FSS)`
  },
  {
    slug: 'ifb-invitation-for-bid',
    title: 'Invitation for Bid',
    abbreviation: 'IFB',
    category: 'Solicitations',
    definition: 'A solicitation method where the government awards a contract to the lowest-priced responsive, responsible bidder through sealed bidding.',
    content: `## Overview

An Invitation for Bid (IFB) is the solicitation document used in sealed bidding — the most straightforward method of government procurement. Bids are opened publicly, and the contract is awarded to the lowest-priced bidder whose offer conforms to all solicitation requirements and who is determined to be a responsible contractor. There are no negotiations.

## Why It Matters in GovCon

IFBs are common in construction, commodities, and other well-defined requirements where the government can specify exactly what it needs. Winning an IFB comes down entirely to price (assuming you meet all technical requirements), making accurate cost estimating the critical success factor.

## Key Details

- **Sealed Bidding**: Bids are submitted in sealed envelopes (or electronically) and opened publicly at a specified time. No discussions or negotiations occur.
- **Responsiveness**: A bid must comply with all material terms of the IFB (delivery schedule, specifications, certifications, etc.) to be considered.
- **Responsibility**: The bidder must have adequate financial resources, the ability to perform, a satisfactory record, and required certifications.
- **Award Criteria**: Strictly lowest price among responsive, responsible bidders.
- **FAR Part 14**: Governs the sealed bidding process.

## How GovCon Data Can Help

GovCon Data surfaces IFB opportunities across federal and SLED markets so you can identify sealed bid opportunities matched to your capabilities and NAICS codes.

## Related Terms

- Request for Proposal (RFP)
- Lowest Price Technically Acceptable (LPTA)
- Best Value
- Sealed Bidding`
  },
  {
    slug: 'igce-independent-government-cost-estimate',
    title: 'Independent Government Cost Estimate',
    abbreviation: 'IGCE',
    category: 'Finance',
    definition: 'The government\'s internal estimate of what a procurement should cost, used to evaluate the reasonableness of contractor proposals.',
    content: `## Overview

An Independent Government Cost Estimate (IGCE) is the government's own estimate of the expected cost of a contract, prepared before proposals are received. It serves as a benchmark for evaluating whether contractor pricing is fair and reasonable. The IGCE is developed independently of contractor input and is typically not shared with offerors.

## Why It Matters in GovCon

While you never see the IGCE, it directly influences whether your proposal is considered fairly priced. If your bid is significantly higher or lower than the IGCE, it may trigger additional scrutiny — too high and you look uncompetitive; too low and the government may question whether you understand the requirements.

## Key Details

- **Preparation**: Developed by the requiring activity (the program office requesting the procurement) based on market research, historical data, and engineering estimates.
- **Confidential**: The IGCE is a government-internal document and is not disclosed to offerors.
- **Uses**: Budgeting, evaluating price reasonableness, determining the appropriate contract type, and establishing negotiation positions.
- **Accuracy**: A well-prepared IGCE reflects realistic labor rates, material costs, overhead, and profit margins for the industry.

## Related Terms

- Price Reasonableness
- Market Research
- Cost Plus Fixed Fee (CPFF)
- Best Value`
  },
  {
    slug: 'jv-joint-venture',
    title: 'Joint Venture',
    abbreviation: 'JV',
    category: 'Business',
    definition: 'A business arrangement where two or more companies combine resources to pursue a specific contract or set of contracts while remaining separate entities.',
    content: `## Overview

A joint venture (JV) in government contracting is a formal business entity created by two or more companies to pursue specific contract opportunities. The partners combine their resources, capabilities, and past performance to compete for work that neither could win alone. JVs are especially common among small businesses leveraging socioeconomic set-aside programs.

## Why It Matters in GovCon

Joint ventures let smaller firms punch above their weight. A small business can partner with a larger, more experienced company and still maintain its small business status for set-aside purposes (if structured correctly under SBA rules). This is a powerful strategy for building past performance and accessing larger contracts.

## Key Details

- **SBA Rules**: Under the SBA's mentor-protégé program, an 8(a) or other small business can form a JV with its mentor and remain eligible for small business set-asides.
- **Populated vs. Unpopulated**: A populated JV has its own employees; an unpopulated JV draws staff from the partner companies.
- **3-in-2 Rule**: A small business JV can receive no more than three set-aside contract awards over a two-year period, after which it must recertify.
- **JV Agreement**: Must specify each party's responsibilities, ownership percentages, profit/loss sharing, and management structure.
- **Past Performance**: A JV can leverage the past performance of its individual members for proposal purposes.

## How GovCon Data Can Help

Use GovCon Data to identify opportunities where a JV approach makes strategic sense — the AI analysis can flag contracts that require capabilities or past performance beyond what a single firm can demonstrate.

## Related Terms

- Mentor-Protégé Program
- 8(a) Business Development Program
- Small Business Administration (SBA)
- Teaming Agreement`
  },
  {
    slug: 'key-personnel',
    title: 'Key Personnel',
    category: 'Personnel',
    definition: 'Specific individuals named in a contract proposal whose qualifications are critical to performance and who cannot be replaced without government approval.',
    content: `## Overview

Key personnel are the specific individuals identified in a contract or proposal whose expertise, experience, and qualifications are considered essential to successful contract performance. Once named, these individuals cannot be replaced or reassigned without the Contracting Officer's written approval. Key personnel clauses are standard in professional services, IT, and technical contracts.

## Why It Matters in GovCon

Naming strong key personnel can make or break your proposal. Evaluation teams scrutinize the resumes and qualifications of proposed key personnel closely — especially for the project manager, technical lead, and subject matter experts. After award, losing a key person without an approved replacement of equal caliber can create contract performance issues.

## Key Details

- **Proposal Impact**: Key personnel resumes are typically evaluated as part of the technical/management volume and receive their own evaluation ratings.
- **Consent to Replace**: The contract clause requires written CO approval before substituting key personnel, with the replacement meeting or exceeding the original person's qualifications.
- **Bait and Switch**: Proposing highly qualified individuals you do not intend to use is prohibited and can result in protests, termination, or debarment.
- **Retention Strategy**: Winning contractors need retention plans for key personnel throughout the contract period.

## How GovCon Data Can Help

GovCon Data's AI proposal tools help you structure key personnel sections that align with solicitation evaluation criteria, ensuring your staffing approach scores well.

## Related Terms

- Performance Work Statement (PWS)
- Statement of Work (SOW)
- Best Value
- Past Performance`
  },
  {
    slug: 'lpta-lowest-price-technically-acceptable',
    title: 'Lowest Price Technically Acceptable',
    abbreviation: 'LPTA',
    category: 'Solicitations',
    definition: 'A source selection method where the contract is awarded to the lowest-priced offeror whose proposal meets all technical requirements.',
    content: `## Overview

Lowest Price Technically Acceptable (LPTA) is a source selection process where the government evaluates proposals on a pass/fail basis for technical requirements and then awards to the lowest-priced offeror that passes. Unlike best value, there is no tradeoff — technical excellence above the "acceptable" threshold provides no advantage.

## Why It Matters in GovCon

LPTA procurements are price wars. Your technical proposal must pass every evaluation criterion, but investing in a superior technical approach brings no benefit. Strategy shifts entirely to efficient pricing — how lean can you be while still meeting every requirement? Understanding when LPTA applies helps you decide whether to bid and how to allocate proposal resources.

## Key Details

- **Pass/Fail Technical Evaluation**: Proposals either meet the acceptability standards or they do not. There is no scoring or ranking of technical merit.
- **Price is Decisive**: Among all technically acceptable proposals, the lowest price wins.
- **When Used**: Appropriate when requirements are well-defined, risk is low, and technical approaches among competitors are likely to be similar (e.g., commodity services, commercial items).
- **FAR Restrictions**: FAR 15.101-2 limits LPTA use and requires agencies to document why it is appropriate. LPTA should not be used for complex requirements where innovation matters.

## How GovCon Data Can Help

GovCon Data's opportunity analysis tools identify whether solicitations use LPTA or best value criteria, helping you calibrate your bid/no-bid decisions and pricing strategy.

## Related Terms

- Best Value
- Invitation for Bid (IFB)
- Source Selection
- Competitive Range`
  },
  {
    slug: 'mas-multiple-award-schedule',
    title: 'Multiple Award Schedule',
    abbreviation: 'MAS',
    category: 'Contracts',
    definition: 'GSA\'s consolidated contract vehicle that provides federal agencies with streamlined access to commercial products and services at pre-negotiated prices.',
    content: `## Overview

The Multiple Award Schedule (MAS) is GSA's flagship contract program, consolidating what were once dozens of separate schedules into a single vehicle. MAS contracts give federal, state, and local agencies access to over 11 million commercial products and services from thousands of pre-approved vendors at competitively negotiated prices.

## Why It Matters in GovCon

The MAS program represents one of the largest pools of government spending. In fiscal year 2024, agencies purchased over $40 billion through GSA Schedules. Having a MAS contract positions your business in front of every federal buyer and many state/local agencies that participate in cooperative purchasing.

## Key Details

- **Large Categories**: Products and services are organized under large categories and subcategories with corresponding Special Item Numbers (SINs).
- **20-Year Term**: MAS contracts have a 20-year maximum period (five-year base plus three five-year options).
- **Pricing**: Vendors offer commercial pricing that GSA negotiates. Economic price adjustments are available periodically.
- **Transactional Data Reporting (TDR)**: Contractors report sales data, which GSA uses to ensure pricing remains competitive.
- **State/Local Access**: Through cooperative purchasing programs, state and local governments can also buy from MAS contractors.

## How GovCon Data Can Help

GovCon Data tracks spending trends across MAS categories and helps you identify which agencies are your best prospects for schedule sales.

## Related Terms

- Federal Supply Schedule (FSS)
- General Services Administration (GSA)
- Special Item Number (SIN)
- Blanket Purchase Agreement (BPA)`
  },
  {
    slug: 'mpp-mentor-protege-program',
    title: 'Mentor-Protégé Program',
    abbreviation: 'MPP',
    category: 'Programs',
    definition: 'A program pairing large experienced contractors with small businesses to help them build capabilities through joint ventures, subcontracting, and technical assistance.',
    content: `## Overview

The Mentor-Protégé Program pairs established government contractors (mentors) with small businesses (protégés) to help the smaller firms develop their technical and business capabilities. Mentors provide training, equipment, financial support, and joint venture opportunities. In return, mentors receive subcontracting credit and the ability to form JVs that compete for small business set-asides.

## Why It Matters in GovCon

For small businesses, a mentor-protégé relationship can be transformative — providing access to resources, past performance, and contract opportunities that would otherwise be out of reach. For large firms, the program offers a way to develop reliable small business partners and meet subcontracting goals.

## Key Details

- **SBA Program**: The SBA's All Small Mentor-Protégé Program is open to all small businesses (not just 8(a) firms). The DoD runs a separate program.
- **Joint Ventures**: Mentor-protégé pairs can form joint ventures that qualify as small for set-aside contracts, even if the mentor is a large business.
- **Agreement Duration**: Typically three years, renewable for additional periods.
- **Mentor Benefits**: Subcontracting plan credit, past performance credit from JV work, and strengthened supplier relationships.
- **Protégé Benefits**: Training, access to facilities and equipment, financial assistance, and the ability to compete for larger contracts through JVs.

## How GovCon Data Can Help

GovCon Data's AI coaching can help you evaluate whether a mentor-protégé relationship is right for your business and identify contract opportunities where a JV approach would strengthen your competitive position.

## Related Terms

- Joint Venture (JV)
- 8(a) Business Development Program
- Small Business Administration (SBA)
- Subcontracting Plan`
  },
  {
    slug: 'naics-north-american-industry-classification-system',
    title: 'North American Industry Classification System',
    abbreviation: 'NAICS',
    category: 'Classification',
    definition: 'The standard numerical system used to classify business establishments by industry type, determining size standards and contract eligibility.',
    content: `## Overview

The North American Industry Classification System (NAICS) is the standard framework used by the United States, Canada, and Mexico to classify businesses by their primary economic activity. In government contracting, NAICS codes determine which size standard applies to your business, what set-aside programs you qualify for, and which contract opportunities match your capabilities.

## Why It Matters in GovCon

Every government solicitation is assigned a NAICS code. That code determines whether you qualify as a "small business" for that particular procurement — because size standards vary by industry. Choosing the right primary NAICS code and understanding how secondary codes affect your eligibility is foundational to your contracting strategy.

## Key Details

- **Structure**: Six-digit codes where the first two digits represent the economic sector, and each additional digit provides finer classification.
- **Size Standards**: The SBA assigns a size standard to each NAICS code — either a revenue cap (e.g., $30 million annual receipts) or an employee count (e.g., 500 employees).
- **Multiple Codes**: Businesses can have multiple NAICS codes but designate one as primary for SAM.gov registration.
- **Solicitation Assignment**: The contracting officer assigns the NAICS code that best describes the principal purpose of the procurement.
- **Protests**: Competitors can challenge the NAICS code assignment if they believe the wrong code was used.

## How GovCon Data Can Help

GovCon Data uses your NAICS codes to automatically match you with relevant federal and SLED opportunities. The AI matching engine considers both your primary and secondary codes to surface contracts you might otherwise miss.

## Related Terms

- Small Business Administration (SBA)
- Size Standard
- System for Award Management (SAM)
- Set-Aside`
  },
  {
    slug: 'nist-national-institute-of-standards-and-technology',
    title: 'National Institute of Standards and Technology',
    abbreviation: 'NIST',
    category: 'Agencies',
    definition: 'A federal agency that develops technology standards and guidelines, including the cybersecurity frameworks required for government contractors.',
    content: `## Overview

The National Institute of Standards and Technology (NIST) is a non-regulatory federal agency within the Department of Commerce that develops measurement standards, technology, and guidelines used across government and industry. For government contractors, NIST is most significant for its cybersecurity publications — particularly NIST SP 800-171, which defines the security controls contractors must implement to protect Controlled Unclassified Information (CUI).

## Why It Matters in GovCon

NIST SP 800-171 compliance is a contractual requirement for virtually all DoD contractors handling CUI, and compliance is increasingly required across civilian agencies as well. The Cybersecurity Maturity Model Certification (CMMC) program is built on NIST standards, making them central to contractor eligibility.

## Key Details

- **NIST SP 800-171**: 110 security controls across 14 families that contractors must implement to protect CUI. Required by DFARS clause 252.204-7012.
- **NIST SP 800-53**: A broader set of security controls used by federal agencies themselves and sometimes referenced in contracts.
- **CMMC Alignment**: The CMMC framework maps directly to NIST SP 800-171 controls at Level 2.
- **Self-Assessment**: Contractors must conduct self-assessments and report their score in the Supplier Performance Risk System (SPRS).

## Related Terms

- Defense Federal Acquisition Regulation Supplement (DFARS)
- Cybersecurity Maturity Model Certification (CMMC)
- Controlled Unclassified Information (CUI)
- System Security Plan (SSP)`
  },
  {
    slug: 'option-year',
    title: 'Option Year',
    category: 'Contracts',
    definition: 'An additional contract performance period beyond the base year that the government may exercise at its discretion.',
    content: `## Overview

An option year is an additional period of performance that the government has the right — but not the obligation — to add onto a contract. Most government contracts are structured with a base period (usually one year) followed by one or more option years. The government exercises options by issuing a modification before the current period expires.

## Why It Matters in GovCon

Option years provide continuity and the potential for long-term revenue, but they are not guaranteed. The government can choose not to exercise an option for any reason — budget constraints, poor performance, changing requirements, or simply deciding to recompete. Understanding this risk is important for workforce planning and financial projections.

## Key Details

- **Structure**: Common patterns include 1 base + 4 options (five years total), or 1 base + 2 options (three years total).
- **Exercise Timing**: The government must notify the contractor of its intent to exercise an option within a specified timeframe (typically 30-60 days before the current period ends).
- **Pricing**: Option year pricing is proposed at the time of initial award and can include escalation factors.
- **FAR 17.2**: Governs the use of options, including limitations and conditions for exercising them.
- **Performance-Based**: Strong performance during the base year significantly increases the likelihood of option exercise.

## Related Terms

- Base Year
- Contract Modification
- Period of Performance
- Contracting Officer (CO)`
  },
  {
    slug: 'osdbu-office-of-small-and-disadvantaged-business-utilization',
    title: 'Office of Small and Disadvantaged Business Utilization',
    abbreviation: 'OSDBU',
    category: 'Agencies',
    definition: 'The office within each major federal agency responsible for promoting small business participation in the agency\'s contracting activities.',
    content: `## Overview

Every major federal agency has an Office of Small and Disadvantaged Business Utilization (OSDBU) — a dedicated office mandated by law to promote small business participation in the agency's contracting programs. OSDBUs serve as advocates for small businesses, helping them navigate the agency's procurement process and connecting them with contracting opportunities.

## Why It Matters in GovCon

The OSDBU is your ally inside the agency. These offices host industry days, maintain vendor databases, provide forecasts of upcoming procurements, and can facilitate introductions to program offices. Building a relationship with the OSDBU at your target agencies is one of the most effective business development strategies for small contractors.

## Key Details

- **Mandate**: Required by the Small Business Act and Public Law 95-507 for all federal agencies with procurement authority.
- **Services**: Outreach events, matchmaking, procurement forecasts, subcontracting assistance, and counseling on set-aside programs.
- **Small Business Goals**: OSDBUs track and report on the agency's progress toward government-wide small business contracting goals (currently 23% of prime contract dollars).
- **Subcontracting**: OSDBUs review large prime contractors' subcontracting plans and monitor their small business subcontracting performance.

## How GovCon Data Can Help

GovCon Data complements OSDBU outreach by helping you identify specific upcoming opportunities at target agencies and preparing data-driven capability briefings.

## Related Terms

- Small Business Administration (SBA)
- Set-Aside
- Subcontracting Plan
- Procurement Technical Assistance Center (PTAC)`
  },
  {
    slug: 'ppirs-past-performance-information-retrieval-system',
    title: 'Past Performance Information Retrieval System',
    abbreviation: 'PPIRS',
    category: 'Agencies',
    definition: 'The federal database (now part of CPARS) where agencies record and retrieve contractor performance evaluations for use in source selection.',
    content: `## Overview

The Past Performance Information Retrieval System (PPIRS) was the government's centralized database for collecting, storing, and retrieving contractor performance evaluations. It has been largely integrated into the Contractor Performance Assessment Reporting System (CPARS), which now serves as the single portal for performance reporting. Agencies use this data when evaluating proposals to assess whether an offeror has a track record of delivering quality work.

## Why It Matters in GovCon

Past performance is one of the most heavily weighted evaluation factors in best value procurements. Your CPARS/PPIRS record follows you — positive ratings build credibility and win work; negative ratings can disqualify you from competitions. Actively managing your performance record is as important as writing good proposals.

## Key Details

- **CPARS Integration**: PPIRS functionality now resides within CPARS at cpars.gov, which is the primary system for recording and retrieving evaluations.
- **Evaluation Factors**: Ratings cover quality, schedule, cost control, management, and small business subcontracting. Ratings range from Exceptional to Unsatisfactory.
- **Contractor Response**: Contractors have 30 days to review and comment on evaluations before they become final.
- **Relevance**: Agencies assess how relevant your past performance is to the current requirement — similar scope, size, and complexity carry more weight.

## How GovCon Data Can Help

GovCon Data's bid management tools help you track contract performance milestones so you maintain the strong delivery record that translates into positive CPARS ratings.

## Related Terms

- Past Performance
- Best Value
- Source Selection
- Contractor Performance Assessment Reporting System (CPARS)`
  },
  {
    slug: 'ptac-procurement-technical-assistance-center',
    title: 'Procurement Technical Assistance Center',
    abbreviation: 'PTAC',
    category: 'Agencies',
    definition: 'Federally funded local centers that provide free counseling and training to businesses seeking government contracts.',
    content: `## Overview

Procurement Technical Assistance Centers (PTACs) are a nationwide network of locally operated centers funded in part by the Defense Logistics Agency (DLA). They provide free or low-cost counseling, training, and technical assistance to businesses pursuing government contracts at the federal, state, and local levels. PTACs are one of the most valuable — and underused — resources for new and small contractors.

## Why It Matters in GovCon

PTACs help businesses with everything from SAM.gov registration and NAICS code selection to proposal writing and compliance. They are staffed by procurement professionals who understand the government buying process and can provide one-on-one guidance tailored to your business. There are nearly 300 PTACs across the country.

## Key Details

- **Services**: SAM registration assistance, bid matching, proposal review, contract compliance guidance, GSA Schedule application support, and training workshops.
- **Cost**: Most services are free; some centers charge nominal fees.
- **Eligibility**: Open to any business seeking government contracts, regardless of size or industry.
- **Network**: Managed by the Association of Procurement Technical Assistance Centers (APTAC) with centers in every state.
- **Funding**: Cooperative agreements between the DLA and local host organizations (universities, economic development agencies, etc.).

## How GovCon Data Can Help

While PTACs provide hands-on counseling, GovCon Data complements that support with AI-powered opportunity discovery, automated bid matching, and proposal generation tools that accelerate your contracting journey.

## Related Terms

- System for Award Management (SAM)
- Small Business Administration (SBA)
- NAICS Codes
- Office of Small and Disadvantaged Business Utilization (OSDBU)`
  },
  {
    slug: 'pws-performance-work-statement',
    title: 'Performance Work Statement',
    abbreviation: 'PWS',
    category: 'Solicitations',
    definition: 'A document that describes required contract outcomes and performance standards rather than prescribing how the work should be done.',
    content: `## Overview

A Performance Work Statement (PWS) defines what the government needs accomplished in terms of measurable outcomes and performance standards, rather than dictating the specific methods or processes the contractor must use. This approach gives contractors the flexibility to innovate and find the most efficient way to deliver results.

## Why It Matters in GovCon

PWSs are increasingly preferred over traditional Statements of Work (SOWs) because they focus on results rather than procedures. For contractors, this means more freedom to propose creative solutions — but also more responsibility for figuring out how to achieve the required outcomes. Your approach becomes a key differentiator in the proposal.

## Key Details

- **Outcome-Focused**: Specifies what must be achieved (e.g., "maintain 99.9% system uptime") rather than how (e.g., "assign three system administrators").
- **Performance Standards**: Includes measurable criteria and acceptable quality levels (AQLs) that the contractor must meet.
- **QASP**: Accompanied by a Quality Assurance Surveillance Plan that defines how the government will monitor and evaluate contractor performance.
- **Incentives/Deductions**: Performance-based contracts may include incentive fees for exceeding standards or deductions for falling short.

## How GovCon Data Can Help

GovCon Data's AI proposal generator can help you translate PWS requirements into compelling technical approaches that demonstrate both understanding of the outcomes and a practical plan for achieving them.

## Related Terms

- Statement of Work (SOW)
- Quality Assurance Surveillance Plan (QASP)
- Performance-Based Contracting
- Key Personnel`
  },
  {
    slug: 'qasp-quality-assurance-surveillance-plan',
    title: 'Quality Assurance Surveillance Plan',
    abbreviation: 'QASP',
    category: 'Management',
    definition: 'The government\'s plan for monitoring and evaluating contractor performance against the standards defined in the contract.',
    content: `## Overview

A Quality Assurance Surveillance Plan (QASP) is the government's roadmap for overseeing contractor performance on a service contract. It defines what will be monitored, how it will be measured, how often evaluations will occur, and what happens when performance falls below acceptable levels. The QASP translates the Performance Work Statement into actionable oversight procedures.

## Why It Matters in GovCon

The QASP tells you exactly how the government will judge your performance. Smart contractors study the QASP carefully during proposal preparation and build their quality control plans to align with it. During execution, the QASP determines what gets measured — and what gets measured gets managed.

## Key Details

- **Surveillance Methods**: Random sampling, periodic inspections, 100% inspection, customer complaints, and automated monitoring.
- **Performance Standards**: Linked to the PWS, specifying acceptable quality levels (e.g., "95% of help desk tickets resolved within 4 hours").
- **Remedies**: Graduated responses to poor performance — from re-performance and deductions to cure notices and termination.
- **COR Role**: The Contracting Officer's Representative typically executes the QASP on a day-to-day basis.

## Related Terms

- Performance Work Statement (PWS)
- Contracting Officer's Representative (COR)
- Performance-Based Contracting
- Acceptable Quality Level (AQL)`
  },
  {
    slug: 'rfi-request-for-information',
    title: 'Request for Information',
    abbreviation: 'RFI',
    category: 'Solicitations',
    definition: 'A pre-solicitation document where the government seeks industry input on requirements, capabilities, and market conditions before issuing a formal solicitation.',
    content: `## Overview

A Request for Information (RFI) is a market research tool the government uses to gather information from industry before issuing a formal solicitation. RFIs are not solicitations — they do not result in contract awards. Instead, they help agencies refine requirements, assess available capabilities, and determine the best procurement approach. Responding to RFIs is voluntary.

## Why It Matters in GovCon

RFIs are early signals of upcoming opportunities. Responding thoughtfully helps shape the eventual solicitation in your favor by educating the agency about your capabilities and the market landscape. It also puts your company on the agency's radar as a potential competitor before the formal solicitation drops.

## Key Details

- **Not Binding**: RFI responses do not create any obligation for the government or the respondent.
- **Content**: Agencies may ask about technical capabilities, past experience, pricing models, staffing approaches, or interest level in a potential requirement.
- **Timing**: RFIs typically appear months before the actual solicitation, giving you advance notice to prepare.
- **Strategy**: Use RFIs to build relationships with the agency and influence requirements toward your strengths.

## How GovCon Data Can Help

GovCon Data aggregates RFIs alongside active solicitations so you can track upcoming requirements early in the pipeline. The AI tools help you craft RFI responses that position your capabilities effectively.

## Related Terms

- Sources Sought Notice
- Request for Proposal (RFP)
- Market Research
- Request for Quotation (RFQ)`
  },
  {
    slug: 'rfp-request-for-proposal',
    title: 'Request for Proposal',
    abbreviation: 'RFP',
    category: 'Solicitations',
    definition: 'A formal solicitation document inviting contractors to submit proposals that will be evaluated on technical merit, past performance, and price.',
    content: `## Overview

A Request for Proposal (RFP) is the government's formal invitation for contractors to submit proposals for a specific requirement. Unlike sealed bids (IFBs), RFPs use a negotiated procurement process where proposals are evaluated on multiple factors — typically technical approach, past performance, management, and price. The government may conduct discussions with offerors before making a final selection.

## Why It Matters in GovCon

RFPs are where most of the competitive action happens in professional services, IT, and complex acquisitions. Winning an RFP requires a well-crafted proposal that tells a compelling story about your technical approach, demonstrates relevant experience, and offers competitive pricing — all while being fully compliant with the solicitation's instructions.

## Key Details

- **Sections**: RFPs typically include Sections A-M covering schedule, supplies/services, descriptions, clauses, representations, instructions, and evaluation criteria.
- **Evaluation Factors**: Stated in Section M, these define what matters most (and their relative importance) in the government's award decision.
- **Discussions**: The government may establish a competitive range and hold discussions before requesting final proposal revisions.
- **Award**: Can be made without discussions if the solicitation states the government intends to do so.
- **FAR Part 15**: Governs the negotiated procurement process used for RFPs.

## How GovCon Data Can Help

GovCon Data surfaces RFP opportunities matched to your NAICS codes and capabilities. The AI proposal generator helps you create compliant, compelling proposals that address evaluation criteria systematically.

## Related Terms

- Request for Quotation (RFQ)
- Invitation for Bid (IFB)
- Best Value
- Competitive Range
- Request for Information (RFI)`
  },
  {
    slug: 'rfq-request-for-quotation',
    title: 'Request for Quotation',
    abbreviation: 'RFQ',
    category: 'Solicitations',
    definition: 'A solicitation used for simplified acquisitions where the government requests price quotes for clearly defined commercial supplies or services.',
    content: `## Overview

A Request for Quotation (RFQ) is a streamlined solicitation method used primarily for simplified acquisitions and purchases of commercial items. The government describes what it needs and asks vendors to submit price quotes. RFQs involve less administrative overhead than RFPs, making them common for smaller purchases and well-defined commercial requirements.

## Why It Matters in GovCon

RFQs represent a high-volume, lower-dollar segment of the contracting market that many small businesses can access relatively easily. They typically have shorter turnaround times and simpler submission requirements than RFPs, making them excellent opportunities for building past performance and establishing agency relationships.

## Key Details

- **Simplified Acquisition**: Most RFQs fall under the Simplified Acquisition Threshold (currently $250,000), governed by FAR Part 13.
- **Commercial Items**: RFQs are frequently used under FAR Part 12 for commercial products and services.
- **Response Format**: Usually a straightforward price quote rather than a multi-volume technical proposal.
- **Award Basis**: Typically awarded to the lowest-priced quote that meets the requirement, though best value approaches are also used.
- **GSA Schedules**: Agencies often issue RFQs to GSA Schedule holders for streamlined purchasing.

## How GovCon Data Can Help

GovCon Data surfaces RFQ opportunities alongside larger solicitations, helping you build a pipeline that includes quick-turn, accessible opportunities for revenue and past performance.

## Related Terms

- Request for Proposal (RFP)
- Simplified Acquisition Threshold
- Federal Supply Schedule (FSS)
- Invitation for Bid (IFB)`
  },
  {
    slug: 'sam-system-for-award-management',
    title: 'System for Award Management',
    abbreviation: 'SAM',
    category: 'Agencies',
    definition: 'The official government website where businesses register to do business with the federal government and where contract opportunities are posted.',
    content: `## Overview

The System for Award Management (SAM.gov) is the federal government's primary platform for vendor registration, entity validation, and contract opportunity posting. Every business that wants to receive federal contracts, grants, or assistance awards must register in SAM.gov and keep their registration active. It is also where agencies post solicitations and contract award data.

## Why It Matters in GovCon

SAM.gov registration is the absolute first step for any business entering federal contracting — without it, you cannot receive a contract award. Your SAM profile includes your NAICS codes, business size, socioeconomic certifications, and capabilities, which agencies use for market research and to determine your eligibility for set-asides.

## Key Details

- **Registration**: Free, but requires a Unique Entity Identifier (UEI), NAICS codes, banking information, and various representations and certifications.
- **Renewal**: Must be renewed annually — an expired registration makes you ineligible for awards.
- **Contract Opportunities**: SAM.gov hosts all federal solicitations (formerly on FBO/beta.SAM.gov).
- **Entity Validation**: SAM validates your business entity information through a multi-step process that can take 7-10 business days.
- **Exclusions**: SAM also maintains the list of excluded parties (debarred/suspended contractors and individuals).

## How GovCon Data Can Help

GovCon Data pulls opportunities directly from SAM.gov and enriches them with AI analysis, matching scores, and estimated values — giving you a more powerful way to search and evaluate the same opportunities with less manual effort.

## Related Terms

- Unique Entity Identifier (UEI)
- NAICS Codes
- Federal Supply Schedule (FSS)
- Contracting Officer (CO)`
  },
  {
    slug: 'sba-small-business-administration',
    title: 'Small Business Administration',
    abbreviation: 'SBA',
    category: 'Agencies',
    definition: 'The federal agency that supports small businesses through contracting programs, certifications, loans, and technical assistance.',
    content: `## Overview

The Small Business Administration (SBA) is the federal agency responsible for supporting and protecting the interests of small businesses. In government contracting, the SBA administers the major socioeconomic programs — 8(a), HUBZone, WOSB/EDWOSB — sets size standards, manages the mentor-protégé program, and advocates for small business participation across all federal agencies.

## Why It Matters in GovCon

The SBA is the gatekeeper for small business certifications that unlock billions of dollars in set-aside contracts. It sets the size standards that determine whether you qualify as "small" for a given NAICS code, and it works with agencies to meet the government-wide goal of awarding at least 23% of prime contract dollars to small businesses.

## Key Details

- **Size Standards**: SBA defines the revenue or employee thresholds for each NAICS code that determine small business eligibility.
- **Certification Programs**: 8(a), HUBZone, WOSB/EDWOSB certifications are managed through SBA's online portal.
- **Government-Wide Goals**: 23% small business, 5% small disadvantaged business, 3% HUBZone, 3% SDVOSB, 5% WOSB.
- **Mentor-Protégé**: The SBA's All Small Mentor-Protégé Program enables JV partnerships between large and small firms.
- **Resources**: Small Business Development Centers (SBDCs), SCORE mentors, and PTACs provide free counseling and training.

## How GovCon Data Can Help

GovCon Data integrates with SBA certification data to help you filter opportunities by set-aside type, track your eligibility, and find contracts matched to your specific certifications.

## Related Terms

- 8(a) Business Development Program
- HUBZone
- Women-Owned Small Business (WOSB)
- Service-Disabled Veteran-Owned Small Business (SDVOSB)
- NAICS Codes`
  },
  {
    slug: 'sdvosb-service-disabled-veteran-owned-small-business',
    title: 'Service-Disabled Veteran-Owned Small Business',
    abbreviation: 'SDVOSB',
    category: 'Programs',
    definition: 'A small business at least 51% owned and controlled by one or more veterans with a service-connected disability, eligible for federal set-aside contracts.',
    content: `## Overview

The Service-Disabled Veteran-Owned Small Business (SDVOSB) program provides federal contracting preferences to small businesses owned by veterans with service-connected disabilities. The federal government has a statutory goal of awarding at least 3% of all contracting dollars to SDVOSBs, creating a substantial pool of set-aside and sole-source opportunities.

## Why It Matters in GovCon

SDVOSB certification is a significant competitive advantage across all federal agencies, and it is particularly valuable at the Department of Veterans Affairs (VA), which has additional set-aside and sole-source authority under the Veterans First Contracting Program. SDVOSB status can be combined with other certifications for even more targeted opportunities.

## Key Details

- **Eligibility**: At least 51% owned and controlled by one or more service-disabled veterans. The disability must be service-connected as determined by the VA or DoD.
- **Certification**: Must be certified by SBA through the Veteran Small Business Certification (VetCert) program.
- **Sole-Source**: Agencies can award sole-source contracts up to $4.5 million for services and $7 million for manufacturing.
- **VA Veterans First**: The VA gives priority to SDVOSBs and VOSBs before other small business categories.
- **3% Goal**: The government-wide goal is 3% of prime contracting dollars to SDVOSBs.

## How GovCon Data Can Help

GovCon Data lets you filter opportunities by SDVOSB set-aside across all federal agencies and tracks VA-specific Veterans First opportunities in a dedicated view.

## Related Terms

- Small Business Administration (SBA)
- Set-Aside
- 8(a) Business Development Program
- HUBZone
- Women-Owned Small Business (WOSB)`
  },
  {
    slug: 'set-aside',
    title: 'Set-Aside',
    category: 'Solicitations',
    definition: 'A contracting practice where competitions are restricted to specific categories of small businesses, such as 8(a), HUBZone, SDVOSB, or WOSB firms.',
    content: `## Overview

A set-aside is a contracting technique where the government restricts competition for a contract to a specific category of businesses — most commonly small businesses or specific socioeconomic subcategories. Only businesses that qualify under the designated category can compete, reducing competition and increasing the chances of award for eligible firms.

## Why It Matters in GovCon

Set-asides are the primary mechanism through which the federal government meets its small business contracting goals. If your business holds an applicable certification, set-asides dramatically narrow the competitive field. Understanding which set-aside categories apply to your business is fundamental to building a viable opportunity pipeline.

## Key Details

- **Total Small Business Set-Aside**: Open to all certified small businesses under the assigned NAICS code.
- **Partial Set-Aside**: A portion of the requirement is set aside for small businesses while the remainder is competed as full and open.
- **Socioeconomic Set-Asides**: Restricted to specific categories — 8(a), HUBZone, SDVOSB, WOSB/EDWOSB.
- **Rule of Two**: A contracting officer must set aside a procurement for small businesses if there is a reasonable expectation that at least two responsible small businesses will submit competitive offers.
- **Self-Certification**: For most categories, businesses self-certify their size status; SBA certifications are required for 8(a), HUBZone, WOSB, and SDVOSB.

## How GovCon Data Can Help

GovCon Data's opportunity filters let you instantly narrow results by set-aside type, ensuring you only see contracts you are eligible to compete for. The AI matching engine prioritizes set-aside opportunities that align with your certifications.

## Related Terms

- 8(a) Business Development Program
- HUBZone
- Service-Disabled Veteran-Owned Small Business (SDVOSB)
- Women-Owned Small Business (WOSB)
- Small Business Administration (SBA)`
  },
  {
    slug: 'sow-statement-of-work',
    title: 'Statement of Work',
    abbreviation: 'SOW',
    category: 'Solicitations',
    definition: 'A contract document that describes the specific tasks, deliverables, timelines, and standards the contractor must meet.',
    content: `## Overview

A Statement of Work (SOW) is a detailed document that defines the work a contractor must perform under a contract. Unlike a Performance Work Statement (PWS), which focuses on outcomes, an SOW prescribes specific tasks, methodologies, deliverables, schedules, and standards. The government uses SOWs when it wants to control how the work is done, not just the results.

## Why It Matters in GovCon

The SOW is the contract's blueprint. Your entire proposal, staffing plan, and pricing should map directly to its requirements. Missing a SOW requirement in your proposal is a compliance gap that can eliminate you from competition. During performance, the SOW defines what "done" looks like.

## Key Details

- **Prescriptive**: Tells the contractor what to do, how to do it, and when. Leaves less room for innovation than a PWS.
- **Deliverables**: Specifies each deliverable, its format, due date, and acceptance criteria.
- **Work Breakdown Structure**: SOWs often follow a WBS format, organizing work into tasks, subtasks, and milestones.
- **Government Responsibility**: When the government prescribes methods, it assumes more risk if those methods prove inadequate.
- **Trend**: The government is increasingly moving from SOWs to PWSs to leverage contractor innovation.

## How GovCon Data Can Help

GovCon Data's AI proposal generator analyzes SOW requirements and helps you build a proposal structure that addresses every task and deliverable systematically.

## Related Terms

- Performance Work Statement (PWS)
- Work Breakdown Structure (WBS)
- Contract Line Item Number (CLIN)
- Quality Assurance Surveillance Plan (QASP)`
  },
  {
    slug: 'to-task-order',
    title: 'Task Order',
    abbreviation: 'TO',
    category: 'Contracts',
    definition: 'An order placed against an existing contract for the performance of services, specifying the tasks, period, and funding.',
    content: `## Overview

A task order (TO) is an order for services placed against an existing indefinite-delivery contract such as an IDIQ, GWAC, or GSA Schedule. Each task order defines a specific scope of work, performance period, deliverables, and funding amount. Task orders are the mechanism through which the government activates work on large contract vehicles.

## Why It Matters in GovCon

For many contractors, especially in IT and professional services, the real competition happens at the task order level rather than the prime contract level. Winning a position on an IDIQ is just the starting line — you then compete for individual task orders over the life of the contract, and that is where revenue materializes.

## Key Details

- **Fair Opportunity**: Under multiple-award contracts, all contract holders must receive a fair opportunity to compete for each task order (with limited exceptions).
- **Scope**: Must fall within the scope of the underlying contract; out-of-scope orders can be protested.
- **Funding**: Each task order is independently funded and may use different appropriations.
- **Protests**: GAO has jurisdiction over task order protests above $10 million for civilian agencies and $25 million for defense.
- **Distinct from Delivery Orders**: Task orders are for services; delivery orders are for supplies.

## How GovCon Data Can Help

GovCon Data monitors task order solicitations across major contract vehicles, alerting you to new opportunities under your existing IDIQs and helping you track competitor task order wins.

## Related Terms

- Delivery Order
- Indefinite Delivery/Indefinite Quantity (IDIQ)
- Government-Wide Acquisition Contract (GWAC)
- Fair Opportunity`
  },
  {
    slug: 'wosb-women-owned-small-business',
    title: 'Women-Owned Small Business',
    abbreviation: 'WOSB',
    category: 'Programs',
    definition: 'A small business at least 51% owned and controlled by one or more women, eligible for federal set-aside contracts in underrepresented industries.',
    content: `## Overview

The Women-Owned Small Business (WOSB) Federal Contracting Program authorizes contracting officers to set aside contracts for competition exclusively among WOSBs in industries where women are underrepresented or substantially underrepresented. The program aims to expand opportunities for women business owners in the federal marketplace.

## Why It Matters in GovCon

The federal government has a goal of awarding 5% of all prime contracting dollars to WOSBs. Certification opens access to both competitive set-asides and sole-source contracts. For women-owned businesses, this certification is one of the most direct paths to federal contracting opportunities.

## Key Details

- **Eligibility**: At least 51% owned and unconditionally controlled by one or more women who are U.S. citizens. The business must be small under the applicable NAICS code size standard.
- **Certification**: Must be certified through SBA or an SBA-approved third-party certifier.
- **NAICS Restrictions**: Set-asides are limited to industries identified by SBA as underrepresented for women.
- **Sole-Source**: Available for WOSBs up to $4.5 million for services and $7 million for manufacturing in substantially underrepresented industries.
- **EDWOSB**: A subset with additional sole-source authority for economically disadvantaged women owners.
- **5% Goal**: Government-wide target for WOSB prime contract awards.

## How GovCon Data Can Help

Filter federal opportunities by WOSB set-aside to build a targeted pipeline. GovCon Data shows which NAICS codes are eligible for WOSB set-asides so you know exactly where to compete.

## Related Terms

- Economically Disadvantaged Women-Owned Small Business (EDWOSB)
- Small Business Administration (SBA)
- Set-Aside
- 8(a) Business Development Program`
  },
  {
    slug: 'epa-economic-price-adjustment',
    title: 'Economic Price Adjustment',
    abbreviation: 'EPA',
    category: 'Contracts',
    definition: 'A contract clause that allows price changes based on specified economic indicators or market conditions.',
    content: `## Overview

Economic Price Adjustment (EPA) clauses permit contract prices to rise or fall during performance based on predefined formulas, indexes (such as labor or material indices), or market benchmarks. They protect both the government and contractor from volatile cost swings in long-term contracts.

## Why It Matters in GovCon

EPA clauses are common in multi-year contracts where material costs, labor rates, or fuel prices may shift significantly. Understanding how EPA is structured helps contractors bid accurately and manage profitability over the contract life.

## Key Details

- **Fixed EPA**: Uses published indices (e.g., Bureau of Labor Statistics) to adjust prices automatically.
- **Negotiated EPA**: Requires bilateral agreement on price changes when conditions warrant.
- **Limits**: Most EPA clauses include caps on upward or downward adjustments.
- **FAR Guidance**: FAR 16.203 addresses economic price adjustment in fixed-price contracts.

## Related Terms

- Fixed Price Contract
- Contract Modifications
- Escalation Clause
- FAR Part 16`
  },
  {
    slug: 'eft-electronic-funds-transfer',
    title: 'Electronic Funds Transfer',
    abbreviation: 'EFT',
    category: 'Acquisition',
    definition: 'A payment method where the government transfers funds electronically to contractor bank accounts.',
    content: `## Overview

Electronic Funds Transfer (EFT) is the standard method for federal contract payments. The government deposits funds directly into a contractor's designated bank account rather than issuing paper checks, accelerating payment and reducing fraud risk.

## Why It Matters in GovCon

Contractors must register in SAM.gov with correct banking information for EFT. Incorrect or outdated banking data can delay or block payment. Prompt payment depends on compliant EFT setup.

## Key Details

- **SAM.gov Requirement**: Bank account details must be current in the System for Award Management.
- **Treasury Rules**: 31 CFR Part 210 governs federal EFT requirements.
- **Payment Timing**: Prompt Payment Act and contract terms specify payment deadlines; EFT speeds receipt.
- **Invoice Compliance**: Proper invoice submission triggers EFT release per contract payment provisions.

## Related Terms

- System for Award Management (SAM)
- Prompt Payment Act
- Invoice
- Contract Financing`
  },
  {
    slug: 'eis-enterprise-infrastructure-solutions',
    title: 'Enterprise Infrastructure Solutions',
    abbreviation: 'EIS',
    category: 'Contracts',
    definition: 'A GSA vehicle for federal agencies to acquire telecommunications and IT infrastructure services.',
    content: `## Overview

Enterprise Infrastructure Solutions (EIS) is a government-wide contract vehicle managed by GSA that replaces the legacy Networx and Regional contracts. It provides agencies with IT and telecommunications services including network transport, managed services, and cloud connectivity.

## Why It Matters in GovCon

EIS represents billions in annual spending. IT and telecom firms pursuing federal work often need EIS as either a prime contractor or subcontractor. Agencies are required to transition from legacy vehicles to EIS.

## Key Details

- **Scope**: Network transport, managed network services, cybersecurity, cloud connectivity, and related professional services.
- **Period**: Multiple award contracts running through 2032.
- **Fair Opportunity**: Task orders compete among EIS contract holders.
- **Transition**: Agencies must complete migration from Networx and Regional contracts by specified deadlines.

## How GovCon Data Can Help

Search for EIS task order opportunities and track agency transitions. GovCon Data filters by contract vehicle and NAICS to surface relevant solicitations.

## Related Terms

- GSA Schedule
- Networx
- Contract Vehicle
- IT Infrastructure`
  },
  {
    slug: 'erp-enterprise-resource-planning',
    title: 'Enterprise Resource Planning',
    abbreviation: 'ERP',
    category: 'Management',
    definition: 'Integrated software systems that manage business processes such as finance, procurement, HR, and project management.',
    content: `## Overview

Enterprise Resource Planning (ERP) systems consolidate core business functions into a single platform — accounting, supply chain, human resources, procurement, and project management. Many government contractors use ERPs to meet complex compliance and reporting demands.

## Why It Matters in GovCon

Federal contractors often need ERP capabilities to comply with FAR cost principles, track labor by contract, support DCAA audits, and produce required financial reports. ERP selection and implementation are significant for growth-stage contractors.

## Key Details

- **Compliance**: ERP systems must support FAR Part 31 cost accounting, timekeeping, and indirect rate structures.
- **DCAA Audits**: Defense contractors require systems that satisfy Defense Contract Audit Agency requirements.
- **Integration**: ERP modules typically integrate with project management, CRM, and proposal tools.
- **Cost**: Implementation ranges from mid-market solutions to enterprise platforms (e.g., SAP, Oracle).

## Related Terms

- Indirect Cost
- DCAA
- Cost Accounting Standards (CAS)
- General and Administrative Expenses (G&A)`
  },
  {
    slug: 'epa-environmental-protection-agency',
    title: 'Environmental Protection Agency',
    abbreviation: 'EPA',
    category: 'Agencies',
    definition: 'The federal agency responsible for protecting human health and the environment through regulation and enforcement.',
    content: `## Overview

The Environmental Protection Agency (EPA) develops and enforces environmental regulations, conducts research, and provides grants and contracts to advance environmental goals. EPA contracting covers scientific research, technical support, cleanup services, and environmental compliance.

## Why It Matters in GovCon

EPA awards billions annually in contracts for environmental monitoring, laboratory services, remediation, consulting, and program support. Contractors in environmental services, engineering, and science often target EPA as a key client.

## Key Details

- **Acquisition**: EPA uses standard FAR-based procurement; environmental NAICS codes apply.
- **Set-Asides**: EPA participates in small business and sustainability initiatives.
- **EPA-Specific**: Some solicitations reference EPA policies and environmental standards.
- **Location**: Headquarters in Washington, D.C., with regional offices nationwide.

## How GovCon Data Can Help

Filter opportunities by EPA as the awarding agency. GovCon Data helps environmental contractors build a targeted pipeline of EPA solicitations and track set-aside eligibility.

## Related Terms

- Federal Acquisition Regulation (FAR)
- NAICS
- Set-Aside
- Environmental Compliance`
  },
  {
    slug: 'eeo-equal-employment-opportunity',
    title: 'Equal Employment Opportunity',
    abbreviation: 'EEO',
    category: 'Business Development',
    definition: 'Laws and policies requiring fair treatment in employment without discrimination based on protected characteristics.',
    content: `## Overview

Equal Employment Opportunity (EEO) encompasses federal and state laws prohibiting employment discrimination based on race, color, religion, sex, national origin, age, disability, or veteran status. Many government contracts require EEO compliance and reporting.

## Why It Matters in GovCon

Federal contractors with contracts above $10,000 must comply with Executive Order 11246 and related requirements. Failure to maintain EEO compliance can result in contract termination and debarment.

## Key Details

- **Executive Order 11246**: Requires affirmative action plans for contractors above threshold.
- **OFCCP**: Office of Federal Contract Compliance Programs enforces EEO obligations.
- **EEO-1 Report**: Annual filing for employers meeting size and contract criteria.
- **Contract Clauses**: FAR mandates EEO clauses in covered contracts.

## Related Terms

- OFCCP
- Affirmative Action
- Executive Order
- Debarment`
  },
  {
    slug: 'etc-estimate-to-complete',
    title: 'Estimate to Complete',
    abbreviation: 'ETC',
    category: 'Finance',
    definition: 'The projected cost to finish remaining work on a contract or project based on current status.',
    content: `## Overview

Estimate to Complete (ETC) is a forward-looking metric that predicts the cost required to complete all remaining work. Combined with actual costs incurred, ETC supports earned value management and contract financial forecasting.

## Why It Matters in GovCon

Government contractors use ETC for internal planning, IPT reviews, and customer reporting. On cost-type contracts, ETC informs funding requests and budget baselines. Accurate ETC supports contract performance and financial health.

## Key Details

- **Formula**: EAC (Estimate at Completion) = Actual Cost + ETC.
- **Earned Value**: ETC is integral to CPI and EAC calculations in EVM.
- **Updates**: ETC should be revised as work progresses and conditions change.
- **Reporting**: Often required in contract status reports (e.g., CPR, CFSR).

## Related Terms

- Estimate at Completion (EAC)
- Earned Value Management (EVM)
- Cost Performance Index (CPI)
- Contract Funds Status Report (CFSR)`
  },
  {
    slug: 'eo-executive-order',
    title: 'Executive Order',
    abbreviation: 'EO',
    category: 'Personnel',
    definition: 'A directive issued by the President that carries the force of law for the executive branch.',
    content: `## Overview

Executive Orders (EOs) are presidential directives that manage operations of the federal government. They can establish policies, create task forces, or direct agency actions. Many EOs directly affect federal contracting and labor requirements.

## Why It Matters in GovCon

Several EOs impose obligations on federal contractors — from minimum wage (EO 14026) to paid sick leave and EEO/affirmative action. Contractors must track and comply with applicable EOs to remain eligible.

## Key Details

- **Legal Basis**: EOs derive from the Constitution and congressional delegations.
- **Contract Impact**: FAR incorporates EOs into contract clauses where required.
- **Examples**: EO 11246 (EEO), EO 13495 (Nondisplacement of Workers), EO 14026 (Minimum Wage).
- **Enforcement**: Agencies and OFCCP enforce contractor compliance with EO requirements.

## Related Terms

- Federal Acquisition Regulation (FAR)
- Equal Employment Opportunity (EEO)
- Affirmative Action
- OFCCP`
  },
  {
    slug: 'fasa-federal-acquisition-streamlining-act',
    title: 'Federal Acquisition Streamlining Act',
    abbreviation: 'FASA',
    category: 'Acquisition',
    definition: 'A 1994 law that simplified federal procurement by raising thresholds, reducing paperwork, and promoting commercial item acquisition.',
    content: `## Overview

The Federal Acquisition Streamlining Act (FASA) of 1994 overhauled federal procurement to reduce administrative burden, increase use of commercial products, and streamline small purchases. It raised micro-purchase and simplified acquisition thresholds and encouraged electronic commerce.

## Why It Matters in GovCon

FASA shaped modern procurement by making smaller buys faster and encouraging agencies to buy commercial items. Understanding FASA helps contractors know when simplified procedures apply versus full competitive requirements.

## Key Details

- **Simplified Acquisition Threshold**: Raised to allow streamlined procedures for purchases below a designated dollar amount.
- **Commercial Items**: FASA promoted acquisition of commercial products and services.
- **Electronic Commerce**: Paved the way for systems like GSA Advantage and SAM.gov.
- **Small Business**: Included provisions to expand small business participation.

## Related Terms

- Simplified Acquisition
- Federal Acquisition Regulation (FAR)
- Commercial Item
- Micro-Purchase`
  },
  {
    slug: 'fbo-federal-business-opportunities',
    title: 'Federal Business Opportunities',
    abbreviation: 'FBO',
    category: 'Acquisition',
    definition: 'The predecessor to SAM.gov; the legacy system where federal solicitations were posted (replaced by beta.SAM.gov).',
    content: `## Overview

Federal Business Opportunities (FBO) was the government's primary portal for posting solicitations, amendments, and award notices. It was replaced by beta.SAM.gov (now integrated into SAM.gov), but the term "FBO" is still used colloquially when referring to federal opportunity posting.

## Why It Matters in GovCon

Contractors who have been in the industry for years often still say "FBO" when they mean the current SAM.gov opportunity search. Understanding the transition helps when discussing systems and training staff on where to find solicitations.

## Key Details

- **Transition**: FBO was retired; SAM.gov and beta.SAM.gov now host opportunity listings.
- **Function**: Posted pre-solicitation notices, solicitations, amendments, and award information.
- **Search**: SAM.gov provides improved search, filtering, and saved searches.
- **Legacy References**: Older documents and training materials may reference FBO.

## How GovCon Data Can Help

GovCon Data aggregates and enriches federal opportunities from SAM.gov with AI-powered search, set-aside filters, and opportunity matching — replacing manual FBO-style searching with smarter pipeline building.

## Related Terms

- SAM.gov
- beta.SAM.gov
- Solicitation
- Contract Opportunities`
  },
  {
    slug: 'fips-federal-information-processing-standards',
    title: 'Federal Information Processing Standards',
    abbreviation: 'FIPS',
    category: 'Contracts',
    definition: 'Technical standards developed by NIST for federal computer systems and information security.',
    content: `## Overview

Federal Information Processing Standards (FIPS) are publications issued by the National Institute of Standards and Technology (NIST) that define requirements for federal IT systems. They cover cryptography, data formats, and security controls used across the government.

## Why It Matters in GovCon

IT contractors building or integrating systems for federal agencies must comply with applicable FIPS. Solicitations often cite FIPS requirements (e.g., FIPS 140 for cryptographic modules, FIPS 199 for security categorization).

## Key Details

- **FIPS 140**: Security requirements for cryptographic modules.
- **FIPS 199**: Standards for categorizing information and systems by impact level.
- **FIPS 200**: Minimum security requirements for federal information systems.
- **NIST**: FIPS are developed through NIST in coordination with federal stakeholders.

## Related Terms

- NIST
- FISMA
- FedRAMP
- Information Security`
  },
  {
    slug: 'fisma-federal-information-security-management-act',
    title: 'Federal Information Security Management Act',
    abbreviation: 'FISMA',
    category: 'Security',
    definition: 'Federal law establishing a framework for securing federal information systems and protecting government data.',
    content: `## Overview

The Federal Information Security Management Act (FISMA) requires federal agencies to develop, document, and implement agency-wide information security programs. It mandates risk-based approaches, continuous monitoring, and compliance with NIST standards.

## Why It Matters in GovCon

Contractors that operate federal systems or handle federal data must support agency FISMA compliance. IT solicitations routinely require FISMA-aligned controls, especially for system integrators and managed service providers.

## Key Details

- **NIST SP 800-53**: Primary control framework referenced for FISMA implementation.
- **Authorization**: Systems undergo assessment and authorization (A&A) before operation.
- **Continuous Monitoring**: Ongoing assessment replaces static, point-in-time audits.
- **OMB Oversight**: Office of Management and Budget provides policy and reporting requirements.

## Related Terms

- NIST
- FedRAMP
- FIPS
- Information Security`
  },
  {
    slug: 'fpds-federal-procurement-data-system',
    title: 'Federal Procurement Data System',
    abbreviation: 'FPDS',
    category: 'Contracts',
    definition: 'The government database that captures and reports federal contract awards and spending data.',
    content: `## Overview

The Federal Procurement Data System (FPDS) is the authoritative source for federal contract award data. It records contract actions, dollar amounts, awardees, NAICS codes, set-asides, and other procurement metadata used for oversight and analysis.

## Why It Matters in GovCon

FPDS data powers market intelligence, competitive analysis, and past performance research. Contractors use FPDS (via USASpending.gov or FPDS.gov) to identify incumbents, analyze agency spending patterns, and find subcontracting opportunities.

## Key Details

- **USASpending.gov**: Public-facing portal that draws from FPDS for contract and grant data.
- **Reportable Actions**: Awards, modifications, and exercised options are recorded.
- **Data Elements**: Includes contracting agency, NAICS, PSC, set-aside, place of performance.
- **Historical Data**: FPDS maintains years of procurement history for trend analysis.

## How GovCon Data Can Help

GovCon Data leverages FPDS and related data to enrich opportunity profiles, show incumbent holders, and support competitive intelligence. Use it to research agency buying patterns and past awards before bidding.

## Related Terms

- USASpending.gov
- NAICS
- Past Performance
- Contract Award`
  },
  {
    slug: 'fedramp',
    title: 'FedRAMP',
    category: 'Security',
    definition: 'A government-wide program that provides a standardized approach to security assessment and authorization for cloud services.',
    content: `## Overview

FedRAMP (Federal Risk and Authorization Management Program) is a standardized process for assessing, authorizing, and monitoring cloud products and services used by federal agencies. It enables reuse of security authorizations across the government.

## Why It Matters in GovCon

Cloud service providers selling to federal agencies must achieve FedRAMP authorization. Agencies can reuse existing authorizations rather than conducting their own assessments, accelerating cloud adoption while maintaining security.

## Key Details

- **Authorization Levels**: FedRAMP defines Low, Moderate, and High baselines based on impact levels.
- **Third-Party Assessment**: A FedRAMP-accredited Third-Party Assessment Organization (3PAO) conducts the assessment.
- **Authorizing Official**: A federal agency grants the Authority to Operate (ATO).
- **Continuous Monitoring**: Ongoing assessment and annual assessments are required.

## Related Terms

- FISMA
- Authority to Operate (ATO)
- NIST SP 800-53
- Cloud Security`
  },
  {
    slug: 'fpr-final-proposal-revision',
    title: 'Final Proposal Revision',
    abbreviation: 'FPR',
    category: 'Contracts',
    definition: 'The last opportunity for offerors to revise their proposals during a negotiated procurement, typically after discussions.',
    content: `## Overview

A Final Proposal Revision (FPR) is the closing submission in a negotiated procurement. After the government holds discussions and identifies deficiencies or weaknesses, offerors submit a FPR incorporating improvements. The government then evaluates FPRs and makes the award decision.

## Why It Matters in GovCon

FPRs are critical — they are the final chance to strengthen your proposal before award. Strategic revisions based on discussion feedback can shift the competitive outcome. Missing the FPR deadline typically results in exclusion.

## Key Details

- **Timing**: FPR is due after discussions; the solicitation specifies the deadline.
- **Scope**: Offerors may revise technical, past performance, or price volumes as allowed.
- **No Further Revisions**: After FPR submission, the evaluation is typically based solely on the FPR.
- **Clarifications vs. Discussions**: FPR follows discussions; clarification-only procurements may not have an FPR.

## Related Terms

- Discussions
- Best and Final Offer (BAFO)
- Source Selection
- Request for Proposal (RFP)`
  },
  {
    slug: 'fy-fiscal-year',
    title: 'Fiscal Year',
    abbreviation: 'FY',
    category: 'Management',
    definition: 'The 12-month period used by the government for budgeting and appropriations; the federal fiscal year runs October 1–September 30.',
    content: `## Overview

The federal fiscal year (FY) begins October 1 and ends September 30. Congress passes appropriations by FY, and agencies plan spending, contract awards, and budget execution within this cycle.

## Why It Matters in GovCon

Contractors must align with the federal budget cycle. End-of-fiscal-year (September) often sees a surge in contract awards as agencies obligate remaining funds. Understanding FY timing helps with pipeline forecasting and proposal planning.

## Key Details

- **FY 2025**: Runs from October 1, 2024, through September 30, 2025.
- **Appropriations**: Budget authority is provided by fiscal year.
- **Use-It-or-Lose-It**: Unexpended obligational authority often must be used before FY end.
- **Planning**: Agencies plan major acquisitions across multiple FYs.

## How GovCon Data Can Help

GovCon Data lets you filter opportunities by fiscal year and track agency spending cycles. Plan your bid calendar around typical FY award patterns and budget milestones.

## Related Terms

- Appropriations
- Budget Authority
- Obligation
- Contract Award`
  },
  {
    slug: 'fpi-fixed-price-incentive',
    title: 'Fixed Price Incentive',
    abbreviation: 'FPI',
    category: 'Contracts',
    definition: 'A contract type that combines a fixed-price base with incentive provisions based on cost, schedule, or performance.',
    content: `## Overview

Fixed Price Incentive (FPI) contracts establish a target cost, target profit, and share ratio. If the contractor performs below target cost, they share in the savings; if they exceed target cost, they share in the overrun up to a ceiling price.

## Why It Matters in GovCon

FPI balances risk between the government and contractor while motivating efficiency. Contractors have incentive to control costs without bearing unlimited risk. Understanding FPI structure is essential when negotiating and managing such contracts.

## Key Details

- **Target Cost/Profit**: Establishes baseline for incentive calculation.
- **Share Ratio**: Defines how savings or overruns are split (e.g., 80/20).
- **Ceiling Price**: Maximum government liability; contractor absorbs cost above ceiling.
- **FPI (Firm Target)**: Fixed share ratio; no adjustment to target.
- **FPIF**: FPI Firm Target is a common variant.

## Related Terms

- Fixed-Price Contract
- Cost-Plus-Incentive-Fee (CPIF)
- Incentive Contract
- FAR Part 16`
  },
  {
    slug: 'fouo-for-official-use-only',
    title: 'For Official Use Only',
    abbreviation: 'FOUO',
    category: 'Security',
    definition: 'A sensitivity designation for unclassified information that requires protection from unauthorized disclosure.',
    content: `## Overview

For Official Use Only (FOUO) is a marking applied to unclassified information that is sensitive and should not be released to the public. It is used across civilian and defense agencies to protect information that could cause harm if disclosed.

## Why It Matters in GovCon

Contractors routinely receive FOUO materials — solicitations, technical documentation, or program information. mishandling FOUO can result in contract issues, security violations, and loss of trust. Proper safeguarding and disposal are required.

## Key Details

- **Unclassified**: FOUO is not classified; it is a handling control, not a security classification.
- **Safeguarding**: Must be protected from unauthorized access; not for public release.
- **Disposition**: Must be properly destroyed or returned when no longer needed.
- **Varies by Agency**: DoD may use FOUO; other agencies use similar controls (e.g., SBU, CUI).

## Related Terms

- Controlled Unclassified Information (CUI)
- Classified Information
- Sensitive But Unclassified (SBU)
- Security Clearance`
  },
  {
    slug: 'fms-foreign-military-sales',
    title: 'Foreign Military Sales',
    abbreviation: 'FMS',
    category: 'Programs',
    definition: 'A government-to-government program where the U.S. sells defense articles and services to allied nations.',
    content: `## Overview

Foreign Military Sales (FMS) are administered by the Defense Security Cooperation Agency (DSCA). The U.S. government acts as an intermediary, procuring defense items from U.S. contractors and transferring them to allied foreign governments under formal agreements.

## Why It Matters in GovCon

FMS creates significant contracting opportunities for defense contractors. While the customer is a foreign government, the contract is with the U.S. government. FMS contracts follow standard DoD procurement but with unique funding and administrative requirements.

## Key Details

- **Government-to-Government**: Transaction is between the U.S. and foreign government.
- **DSCA Role**: DSCA manages FMS cases and coordinates with military services.
- **Letter of Offer and Acceptance (LOA)**: Formal agreement defining the FMS sale.
- **Direct Commercial Sales (DCS)**: Alternative where contractors sell directly to foreign customers with export approval.

## Related Terms

- Defense Security Cooperation Agency (DSCA)
- Letter of Offer and Acceptance (LOA)
- ITAR
- Direct Commercial Sales (DCS)`
  },
  {
    slug: 'foia-freedom-of-information-act',
    title: 'Freedom of Information Act',
    abbreviation: 'FOIA',
    category: 'Regulations',
    definition: 'A federal law that gives the public the right to request access to federal agency records, subject to exemptions.',
    content: `## Overview

The Freedom of Information Act (FOIA) enables any person to request copies of federal agency records. Agencies must disclose information unless it falls under specific exemptions (e.g., national security, trade secrets, personal privacy).

## Why It Matters in GovCon

Contractors may use FOIA to obtain released solicitations, award information, or agency documents that inform competitive strategy. Proprietary and confidential business information submitted to the government can be protected from FOIA release under Exemption 4.

## Key Details

- **Exemption 4**: Protects trade secrets and confidential business information.
- **Reverse FOIA**: Contractors can sue to prevent release of their information.
- **Response Time**: Agencies typically have 20 business days (extensions possible).
- **Fees**: Requesters may be charged for search and duplication in some cases.

## Related Terms

- Trade Secrets
- Proprietary Information
- Solicitation
- Contract Award`
  },
  {
    slug: 'ffrdc-federally-funded-research-and-development-center',
    title: 'Federally Funded Research and Development Center',
    abbreviation: 'FFRDC',
    category: 'Business',
    definition: 'A federally funded R&D organization operated by a contractor to support long-term government missions.',
    content: `## Overview

Federally Funded Research and Development Centers (FFRDCs) are independent, nonprofit or university-affiliated entities that conduct research and analysis for sponsoring agencies. They are barred from competing for most government work but can support agencies as trusted, conflict-free advisors.

## Why It Matters in GovCon

FFRDCs are significant players in defense, energy, and science contracting. Contractors may partner with FFRDCs, support them as subcontractors, or compete in adjacent markets. Understanding FFRDC roles helps in strategic positioning.

## Key Details

- **Sponsorship**: Each FFRDC has a sponsoring agency (e.g., DoD, DOE, NASA).
- **Conflict of Interest**: FFRDCs generally cannot compete with industry for contracts.
- **Examples**: MITRE, Lincoln Lab, RAND, Sandia, Los Alamos.
- **Tasking**: Work is directed by the sponsor; FFRDCs do not seek commercial business.

## Related Terms

- MITRE
- RAND Corporation
- Research and Development
- Contract Vehicle`
  },
  {
    slug: 'g-a-general-and-administrative-expenses',
    title: 'General and Administrative Expenses',
    abbreviation: 'G&A',
    category: 'Finance',
    definition: 'Overhead costs that support the overall organization rather than direct contract performance.',
    content: `## Overview

General and Administrative (G&A) expenses are indirect costs required to run a business — executive salaries, legal, accounting, human resources, facilities, and other support functions. They are allocated to contracts via an established rate base (e.g., total cost input or value-added).

## Why It Matters in GovCon

G&A rates are negotiated with the government (often DCAA) and must be consistently applied. Incorrect G&A allocation can lead to unallowable costs, audit findings, and contract disputes. Understanding rate structure is critical for pricing and compliance.

## Key Details

- **Rate Base**: G&A can be applied to total cost input, value-added, or single-element bases.
- **Allowability**: FAR Part 31 defines allowability; unallowable costs must be excluded from the base.
- **Forward Pricing**: G&A rates are proposed and negotiated for future periods.
- **Multiple Pools**: Some contractors have separate G&A and overhead pools.

## Related Terms

- Indirect Cost
- Overhead
- DCAA
- Forward Pricing Rate Agreement (FPRA)`
  },
  {
    slug: 'gfe-government-furnished-equipment',
    title: 'Government Furnished Equipment',
    abbreviation: 'GFE',
    category: 'Contracts',
    definition: 'Equipment provided by the government to the contractor for use in contract performance.',
    content: `## Overview

Government Furnished Equipment (GFE) is property the government provides to the contractor under the contract. The contractor uses GFE to perform the work but does not own it. Proper use, care, and return are contractually required.

## Why It Matters in GovCon

GFE reduces contractor capital requirements but imposes accountability. Contractors must track GFE, use it only for contract purposes, and return or dispose of it per contract terms. Loss or damage can result in liability.

## Key Details

- **Property Clause**: FAR 52.245-1 (or alternate) governs government property.
- **Accountability**: Contractors maintain property records and submit reports.
- **Care and Use**: Contractors must protect and maintain GFE.
- **GFE vs. GFP**: GFE is a subset of Government Furnished Property (GFP); GFP includes materials and facilities.

## Related Terms

- Government Furnished Property (GFP)
- Government Property
- FAR 52.245
- Contractor Acquired Property`
  },
  {
    slug: 'gfp-government-furnished-property',
    title: 'Government Furnished Property',
    abbreviation: 'GFP',
    category: 'Contracts',
    definition: 'Property (equipment, materials, or facilities) provided by the government to the contractor for contract performance.',
    content: `## Overview

Government Furnished Property (GFP) encompasses all government-owned property provided to the contractor — equipment (GFE), materials, tooling, and real property. Contractors use GFP to perform the contract but must safeguard and account for it.

## Why It Matters in GovCon

GFP terms are in many solicitations, especially for maintenance, sustainment, and facilities contracts. Contractors must understand property clauses, reporting requirements, and liability for loss or damage to avoid contract default.

## Key Details

- **FAR 52.245**: Standard clauses for government property.
- **Property System**: Contractors need an adequate property management system when handling GFP.
- **Audit**: DCAA and other auditors may review property controls.
- **Disposition**: Contract specifies return, transfer, or disposal of GFP at contract end.

## Related Terms

- Government Furnished Equipment (GFE)
- Government Property
- FAR Part 45
- Contractor Acquired Property`
  },
  {
    slug: 'gsa-advantage',
    title: 'GSA Advantage',
    category: 'Contracts',
    definition: 'GSA\'s online catalog where federal buyers can search and order from GSA Schedule contracts.',
    content: `## Overview

GSA Advantage is the online shopping and ordering platform for GSA Schedule holders. Federal purchasers search for products and services, compare pricing, and place orders directly against Schedule contracts — streamlining procurement for routine needs.

## Why It Matters in GovCon

Contractors with GSA Schedules must maintain their catalog on GSA Advantage to receive orders. Accurate product listings, competitive pricing, and timely updates drive order volume. Many Schedule contractors derive significant revenue through Advantage orders.

## Key Details

- **Ordering**: Federal buyers order through GSA Advantage; orders flow to contractors.
- **Pricing**: Contractors submit and maintain pricing in the system.
- **Competition**: Buyers can compare multiple Schedule holders.
- **Reporting**: Sales through Advantage are reported and count toward Schedule maintenance.

## How GovCon Data Can Help

GovCon Data helps you identify agencies actively buying in your NAICS and product categories. Use it to target marketing and capture more GSA Advantage orders.

## Related Terms

- GSA Schedule
- GSA
- Federal Supply Schedule
- eBuy`
  },
  {
    slug: 'gsa-schedule',
    title: 'GSA Schedule',
    category: 'Contracts',
    definition: 'A pre-negotiated contract vehicle that allows federal agencies to buy commercial products and services from approved vendors.',
    content: `## Overview

The GSA Schedule (officially Multiple Award Schedule or MAS) is a government-wide contract vehicle. Vendors apply, undergo evaluation, and receive a Schedule contract with pre-negotiated pricing and terms. Agencies then order directly without full competition.

## Why It Matters in GovCon

GSA Schedules are one of the most common pathways to federal sales. They simplify ordering for agencies and provide contractors with access to thousands of buyers. Schedule holders must maintain compliance and competitive pricing to retain and grow business.

## Key Details

- **MAS Consolidation**: GSA consolidated numerous Schedules into a single MAS structure.
- **SINs**: Special Item Numbers categorize products and services within Schedules.
- **eBuy**: GSA's request-for-quote system for complex or higher-value Schedule orders.
- **Open to All**: Any qualified vendor can apply; no limit on number of Schedule holders.

## How GovCon Data Can Help

Find Schedule opportunities and task orders. GovCon Data filters by contract vehicle and NAICS to surface relevant GSA Schedule buys and recompetes.

## Related Terms

- GSA Advantage
- Multiple Award Schedule (MAS)
- Special Item Number (SIN)
- eBuy`
  },
  {
    slug: 'hca-head-of-contracting-activity',
    title: 'Head of Contracting Activity',
    abbreviation: 'HCA',
    category: 'Agencies',
    definition: 'The senior official designated to manage contracting within an agency or component.',
    content: `## Overview

The Head of Contracting Activity (HCA) is the official responsible for the contracting function within an agency or major component. HCAs establish policy, delegate authority, and ensure compliance with acquisition regulations.

## Why It Matters in GovCon

HCAs approve waivers, deviations, and other acquisition decisions. Understanding who the HCA is for an agency or component helps when pursuing high-value or complex opportunities — especially when unique approvals may be needed.

## Key Details

- **Designation**: Appointed by the agency head or component leadership.
- **Delegation**: HCAs delegate authority to contracting officers; delegation limits are documented.
- **Waivers**: HCAs can approve FAR deviations and policy exceptions.
- **Oversight**: HCAs oversee contracting workforce and acquisition operations.

## Related Terms

- Contracting Officer
- Warrant
- Federal Acquisition Regulation (FAR)
- Agency Head`
  },
  {
    slug: 'hsar-homeland-security-acquisition-regulation',
    title: 'Homeland Security Acquisition Regulation',
    abbreviation: 'HSAR',
    category: 'Regulations',
    definition: 'The acquisition regulation that supplements the FAR for the Department of Homeland Security.',
    content: `## Overview

The Homeland Security Acquisition Regulation (HSAR) is the DHS-specific regulation that supplements and, where authorized, deviates from the Federal Acquisition Regulation (FAR). It addresses DHS-unique requirements and organizational structure.

## Why It Matters in GovCon

Contractors pursuing DHS work must comply with both FAR and HSAR. HSAR clauses and procedures appear in DHS solicitations and contracts. Familiarity with HSAR helps in proposal preparation and contract administration.

## Key Details

- **48 CFR Chapter 30**: HSAR is codified in Chapter 30 of the CFR.
- **FAR Supplement**: Follows the same structure as FAR; only deviates where necessary.
- **DHS Components**: CBP, FEMA, TSA, ICE, USCIS, and others follow HSAR.
- **Clauses**: DHS-specific clauses are in HSAR Part 3052.

## Related Terms

- Federal Acquisition Regulation (FAR)
- DFARS
- Department of Homeland Security (DHS)
- FAR Supplement`
  },
  {
    slug: 'iam-identity-and-access-management',
    title: 'Identity and Access Management',
    abbreviation: 'IAM',
    category: 'Security',
    definition: 'Policies and technologies that control who can access systems and data and what they can do.',
    content: `## Overview

Identity and Access Management (IAM) encompasses the processes and systems used to identify users, authenticate them, and authorize access to resources. IAM is foundational to federal cybersecurity and is required for FISMA and FedRAMP compliance.

## Why It Matters in GovCon

IT contractors building or managing federal systems must implement IAM that meets NIST and agency requirements. Solicitations often specify IAM capabilities such as multifactor authentication, single sign-on, and role-based access control.

## Key Details

- **Authentication**: Verifying user identity (e.g., passwords, MFA, PKI).
- **Authorization**: Determining what authenticated users can access.
- **NIST SP 800-63**: Guidelines for digital identity and authentication.
- **Zero Trust**: Modern frameworks emphasize continuous verification and least privilege.

## Related Terms

- FISMA
- FedRAMP
- Multifactor Authentication (MFA)
- Zero Trust`
  },
  {
    slug: 'ir-d-independent-research-and-development',
    title: 'Independent Research and Development',
    abbreviation: 'IR&D',
    category: 'Contracts',
    definition: 'Company-funded R&D that contractors may recover as allowable indirect costs on defense contracts.',
    content: `## Overview

Independent Research and Development (IR&D) is research and development undertaken at the contractor's discretion, not under a specific contract. Under DoD rules, qualifying IR&D costs can be allocated as allowable indirect costs and recovered through overhead on defense contracts.

## Why It Matters in GovCon

IR&D enables defense contractors to invest in technology and innovation while recouping a portion of costs through the overhead allocation to DoD contracts. Strict technical and cost accounting rules apply; noncompliance can result in cost disallowance.

## Key Details

- **DFARS 231.205-18**: Governs allowability and allocability of IR&D.
- **Technical Interchange**: Contractors must engage in technical interchange with DoD before costs are allocable.
- **B&P**: Bid and Proposal (B&P) costs have separate rules but are often discussed with IR&D.
- **Audit**: DCAA reviews IR&D for allowability and allocability.

## Related Terms

- Bid and Proposal (B&P)
- Indirect Cost
- DFARS
- DCAA`
  },
  {
    slug: 'idc-indirect-cost',
    title: 'Indirect Cost',
    abbreviation: 'IDC',
    category: 'Finance',
    definition: 'Costs that cannot be traced to a single contract but are allocated across projects via indirect rates.',
    content: `## Overview

Indirect costs are expenses that benefit multiple contracts or the organization as a whole — facilities, administrative salaries, depreciation, and support functions. They are pooled and allocated to contracts using predetermined rates (overhead, G&A, etc.).

## Why It Matters in GovCon

Indirect cost accounting is central to government contracting. Rates are negotiated with the government, and misapplication can lead to unallowable costs, audit findings, and contract disputes. FAR Part 31 and Cost Accounting Standards govern.

## Key Details

- **Pools**: Common pools include overhead (OH) and General and Administrative (G&A).
- **Base**: Allocation bases (e.g., direct labor, total cost input) determine how pools are applied.
- **Forward Pricing**: Rates are proposed and negotiated for future periods (FPRA/FPCE).
- **Audit**: DCAA and other auditors review indirect cost proposals and incurred cost submissions.

## Related Terms

- Overhead
- General and Administrative (G&A)
- Forward Pricing Rate Agreement (FPRA)
- Cost Accounting Standards (CAS)`
  },
  {
    slug: 'industry-day',
    title: 'Industry Day',
    category: 'Agencies',
    definition: 'An event where agencies present upcoming procurements and engage with potential contractors before a solicitation is released.',
    content: `## Overview

An Industry Day is a pre-solicitation engagement where agencies describe their requirements, acquisition strategy, and timelines to interested vendors. It provides an opportunity for dialogue, questions, and early relationship building.

## Why It Matters in GovCon

Industry Days are valuable for market research and positioning. Attending helps contractors understand requirements, identify competitors, and tailor their approach before the RFP drops. Some agencies record sessions or publish slides for those who cannot attend.

## Key Details

- **Pre-Solicitation**: Usually held before the draft or final RFP is released.
- **Format**: Can be in-person, virtual, or hybrid.
- **One-on-Ones**: Some Industry Days include optional one-on-one sessions with the agency.
- **No Promises**: Information shared does not bind the government; solicitations may differ.

## How GovCon Data Can Help

GovCon Data tracks upcoming solicitations and can help you identify Industry Day announcements. Use it to build a calendar of events relevant to your capabilities and prepare for key opportunities.

## Related Terms

- Pre-Solicitation
- Sources Sought
- Request for Information (RFI)
- Market Research`
  },
  {
    slug: 'infosec-information-security',
    title: 'Information Security',
    abbreviation: 'INFOSEC',
    category: 'Security',
    definition: 'Practices and controls that protect information and information systems from unauthorized access or harm.',
    content: `## Overview

Information Security (INFOSEC) encompasses the policies, processes, and technologies used to protect the confidentiality, integrity, and availability of information. In the federal context, INFOSEC is governed by FISMA, NIST standards, and agency-specific requirements.

## Why It Matters in GovCon

Contractors handling federal data or operating federal systems must comply with INFOSEC requirements. Solicitations specify security controls, certifications, and incident response expectations. INFOSEC is a core competency for IT and cybersecurity contractors.

## Key Details

- **CIA Triad**: Confidentiality, integrity, availability as foundational goals.
- **NIST Framework**: NIST SP 800-53 and related publications define control baselines.
- **Incident Response**: Contractors must report and respond to security incidents per contract terms.
- **Training**: Personnel with system access often need security awareness training.

## Related Terms

- FISMA
- FedRAMP
- Cybersecurity
- NIST`
  },
  {
    slug: 'ims-integrated-master-schedule',
    title: 'Integrated Master Schedule',
    abbreviation: 'IMS',
    category: 'Contracts',
    definition: 'A project schedule that integrates all contract tasks, milestones, and dependencies into a single timeline.',
    content: `## Overview

The Integrated Master Schedule (IMS) is a comprehensive schedule that ties together all work packages, milestones, and interdependencies for a program. It supports program management, earned value reporting, and customer visibility into progress.

## Why It Matters in GovCon

Many DoD and complex programs require an IMS as a contract deliverable. The IMS must align with the contract work breakdown structure (WBS) and support the integrated baseline review (IBR). Maintaining a credible IMS is essential for contract compliance.

## Key Details

- **WBS Alignment**: IMS activities map to the work breakdown structure.
- **Earned Value**: IMS supports Earned Value Management System (EVMS) compliance.
- **Critical Path**: Identifies sequence of activities that drive program completion.
- **Updates**: IMS is typically updated monthly and submitted per contract requirements.

## Related Terms

- Work Breakdown Structure (WBS)
- Earned Value Management (EVM)
- Integrated Baseline Review (IBR)
- Master Schedule`
  },
  {
    slug: 'ipt-integrated-product-team',
    title: 'Integrated Product Team',
    abbreviation: 'IPT',
    category: 'Products',
    definition: 'A cross-functional team that manages a product, subsystem, or program element from development through delivery.',
    content: `## Overview

An Integrated Product Team (IPT) brings together representatives from engineering, program management, quality, contracts, and other functions to manage a product or subsystem. IPTs enable coordinated decision-making and accountability throughout the lifecycle.

## Why It Matters in GovCon

IPTs are standard in DoD and aerospace programs. Contractors often organize their program structure around IPTs and interact with government IPT counterparts. Understanding IPT roles and meetings helps in program execution and customer interface.

## Key Details

- **Cross-Functional**: IPT members represent different disciplines and stakeholders.
- **IPT Lead**: Typically a program manager or technical lead who owns the IPT charter.
- **Reviews**: IPTs conduct periodic reviews (design, program, risk) and report up.
- **Government IPTs**: Many programs have government IPTs that interface with contractor teams.

## Related Terms

- Program Management
- Work Breakdown Structure (WBS)
- Design Review
- Systems Engineering`
  },
  {
    slug: 'iso-international-organization-for-standardization',
    title: 'International Organization for Standardization',
    abbreviation: 'ISO',
    category: 'Classification',
    definition: 'An international body that develops voluntary standards for quality, safety, and processes across industries.',
    content: `## Overview

The International Organization for Standardization (ISO) develops and publishes international standards used worldwide. ISO 9001 (quality management), ISO 27001 (information security), and ISO 14001 (environmental management) are commonly referenced in federal contracting.

## Why It Matters in GovCon

Federal solicitations sometimes require or prefer ISO certifications. ISO 9001 demonstrates quality management systems; ISO 27001 aligns with information security expectations. Contractors with ISO certifications may have a competitive advantage.

## Key Details

- **ISO 9001**: Quality management systems — widely required or preferred.
- **ISO 27001**: Information security management systems — relevant for IT and cybersecurity.
- **ISO 14001**: Environmental management — relevant for sustainability and environmental contracts.
- **Certification**: Third-party accredited bodies perform certification audits.

## Related Terms

- Quality Management
- CMMI
- FedRAMP
- Certification`
  },
  {
    slug: 'itar-international-traffic-in-arms-regulations',
    title: 'International Traffic in Arms Regulations',
    abbreviation: 'ITAR',
    category: 'Regulations',
    definition: 'U.S. regulations that control the export and import of defense articles, services, and technical data.',
    content: `## Overview

The International Traffic in Arms Regulations (ITAR) implement the Arms Export Control Act. ITAR governs the export of defense articles, defense services, and related technical data. Violations can result in civil and criminal penalties, including debarment.

## Why It Matters in GovCon

Defense contractors handling controlled items or technical data must comply with ITAR. Registration with the Directorate of Defense Trade Controls (DDTC), export authorizations, and compliance programs are mandatory for many defense contractors.

## Key Details

- **USML**: The United States Munitions List defines defense articles subject to ITAR.
- **DDTC**: Part of the State Department; administers ITAR and issues licenses.
- **Registration**: Companies that manufacture or export defense articles must register.
- **Technical Data**: Export of technical data (including to foreign persons in the U.S.) is controlled.

## Related Terms

- Export Control
- DDTC
- EAR (Export Administration Regulations)
- Defense Article`
  },
  {
    slug: 'ig-inspector-general',
    title: 'Inspector General',
    abbreviation: 'IG',
    category: 'Agencies',
    definition: 'An independent official within an agency who audits programs, investigates fraud and waste, and promotes accountability.',
    content: `## Overview

Inspectors General (IGs) are statutory officials who conduct audits, inspections, and investigations to promote economy, efficiency, and integrity in agency operations. Each major agency has an IG; they report to Congress and the agency head.

## Why It Matters in GovCon

IG audits and investigations can impact contractors — from contract audits to fraud investigations. Contractors may interact with IG offices during audits or when responding to subpoenas. Understanding IG roles helps in compliance and response.

## Key Details

- **Statutory Creation**: IGs are created by the Inspector General Act of 1978.
- **Independence**: IGs operate independently; agency heads cannot prevent or delay audits.
- **Congressional Reporting**: Semiannual reports to Congress summarize findings and recommendations.
- **Contract Audits**: Some IG offices perform or oversee contract audits.

## Related Terms

- DCAA
- Audit
- Fraud
- Congressional Oversight`
  },
  {
    slug: 'joc-job-order-contracting',
    title: 'Job Order Contracting',
    abbreviation: 'JOC',
    category: 'Contracts',
    definition: 'A construction contracting method where pre-priced unit costs are used to quickly issue work orders for repair, alteration, and minor construction without full design.',
    content: `## Overview

Job Order Contracting (JOC) is a competitively awarded indefinite delivery contract where agencies pre-negotiate unit prices with a contractor for a catalog of construction tasks. When maintenance or repair work is needed, the agency issues a job order that references those pre-priced line items, reducing procurement lead time and administrative overhead.

## Why It Matters in GovCon

JOC streamlines small to mid-size construction projects — from facility repairs to renovations — allowing agencies to quickly address maintenance needs without issuing a full solicitation each time. For contractors, JOC provides predictable work volume and simplified billing under a pre-established pricing structure.

## Key Details

- **Pre-Priced Catalog**: Unit costs for labor, materials, and equipment are negotiated at contract award.
- **Job Orders**: Individual work orders are issued as needed; contractors multiply quantities by unit prices.
- **Common Uses**: Facility repair, alteration, maintenance, minor construction, demolition.
- **Time Savings**: Reduces design-bid-build cycle from months to weeks for routine work.
- **Multiple Contractors**: Some agencies award JOCs to multiple firms for different zones or trade specialties.

## How GovCon Data Can Help

Search for JOC solicitations and recompetitions in GovCon Data's opportunity pipeline. Filter by construction NAICS codes and contract type to find JOC opportunities that match your capabilities.

## Related Terms

- Indefinite Delivery/Indefinite Quantity (IDIQ)
- Time and Materials (T&M)
- Task Order
- Facility Maintenance`
  },
  {
    slug: 'j-a-justification-and-approval',
    title: 'Justification and Approval',
    abbreviation: 'J&A',
    category: 'Contracts',
    definition: 'A document that justifies and authorizes a contract award without full and open competition under one or more statutory exceptions.',
    content: `## Overview

A Justification and Approval (J&A) is a formal document that contracting officers must prepare when awarding a contract using an exception to full and open competition. It explains why competition is impractical or contrary to the government's interests and must be approved by designated officials before the award can proceed.

## Why It Matters in GovCon

J&As enable sole-source awards, limited-source competitions, and other non-competitive actions. Contractors pursuing sole-source work must ensure their capabilities align with the stated justification — and understand that J&As can be protested if they contain factual errors or inadequate rationale.

## Key Details

- **Required by FAR 6.303**: Must include description of needs, rationale for exception, market research summary, and approval signature.
- **Approval Levels**: Dollar thresholds determine whether the contracting officer, competition advocate, or higher authority must approve.
- **Posting**: J&As for sole-source contracts above $250K are posted to FedBizOpps/GSAM for transparency.
- **Protest Risk**: Weak or inaccurate J&As can be successfully challenged at GAO or the Court of Federal Claims.

## Related Terms

- Sole Source
- Federal Acquisition Regulation (FAR)
- Full and Open Competition
- Limited Sources Justification`
  },
  {
    slug: 'kdp-key-decision-point',
    title: 'Key Decision Point',
    abbreviation: 'KDP',
    category: 'Acquisition',
    definition: 'A formal review gate in the acquisition process where senior leadership decides whether a program may proceed to the next phase, requires changes, or should be terminated.',
    content: `## Overview

Key Decision Points (KDPs) are formal milestones in the defense and major acquisition life cycle where designated authorities — often the Milestone Decision Authority (MDA) — review program status, risk, and affordability before allowing a program to advance. Proceeding past a KDP typically requires documentation that technical, schedule, and cost baselines are executable.

## Why It Matters in GovCon

Contractors on major programs must meet KDP exit criteria to avoid program delays or restructuring. Understanding where your project sits in relation to upcoming KDPs helps you prioritize deliverables, manage stakeholder expectations, and anticipate contract modifications or recompetes tied to milestone decisions.

## Key Details

- **Phased Gate Reviews**: Common in DoD 5000-series; programs proceed through Milestones A, B, and C.
- **Decision Options**: Approve to proceed, conditional approval with specific actions, or no-go (restructure or terminate).
- **Documentation**: Program managers submit acquisition strategy, risk assessment, and independent cost estimates.
- **Industry Role**: Contractors provide technical data, test results, and cost proposals to support KDP packages.

## Related Terms

- Milestone Decision Authority (MDA)
- Major Defense Acquisition Program (MDAP)
- Acquisition Program Baseline (APB)
- Defense Acquisition System`
  },
  {
    slug: 'kpi-key-performance-indicator',
    title: 'Key Performance Indicator',
    abbreviation: 'KPI',
    category: 'Management',
    definition: 'A measurable value used to evaluate how effectively a contractor or program is achieving strategic objectives and contract requirements.',
    content: `## Overview

Key Performance Indicators (KPIs) are quantifiable metrics that organizations use to track progress toward goals. In government contracting, KPIs appear in contract performance plans, earned value management systems, and service-level agreements — defining what "success" looks like and how it will be measured.

## Why It Matters in GovCon

Contract performance is increasingly evaluated through KPIs. Proposals must define clear, measurable KPIs that align with the solicitation's evaluation criteria. During contract execution, missed KPIs can trigger deductions, contract termination, or loss of award fees.

## Key Details

- **Contract-Specific**: KPIs are tailored to the work; examples include schedule adherence, quality scores, customer satisfaction, and cost variance.
- **Tied to Incentives**: Many contracts link fee or award payments to KPI attainment.
- **Reporting**: Contractors typically report KPIs in monthly or quarterly Contractor Performance Assessment Reports (CPARs).
- **Baseline Setting**: KPIs should be achievable but challenging; agencies often require historical or benchmarking data to justify targets.

## How GovCon Data Can Help

Use GovCon Data's proposal tools to align your KPI proposals with typical agency expectations for similar contracts. The compliance tracking features help you monitor KPI performance across your portfolio.

## Related Terms

- Contractor Performance Assessment Reporting System (CPARS)
- Service Level Agreement (SLA)
- Earned Value Management (EVM)
- Past Performance`
  },
  {
    slug: 'ksa-knowledge-skills-and-abilities',
    title: 'Knowledge, Skills, and Abilities',
    abbreviation: 'KSA',
    category: 'Acquisition',
    definition: 'Written narrative responses that describe a candidate or contractor capability in specific areas, historically used in federal hiring and some proposal evaluations.',
    content: `## Overview

Knowledge, Skills, and Abilities (KSAs) are structured narrative statements that demonstrate how an individual or organization meets specific competency requirements. Originally used in federal civil service hiring, the concept was adopted for contractor capability assessments and some proposal formats, where offerors write narratives addressing each KSA factor.

## Why It Matters in GovCon

Although many agencies have moved to simplified past performance and experience formats, KSAs still appear in solicitations — particularly for professional services, staffing, and positions requiring clear technical competencies. Strong KSA responses use the STAR method (Situation, Task, Action, Result) and cite relevant experience.

## Key Details

- **Factor-Based**: Each KSA corresponds to a stated requirement; responses should directly address the factor and provide evidence.
- **Format Evolution**: Traditional KSA narratives have been largely replaced by USAStaffing assessments for hiring; proposal KSAs vary by agency.
- **Page Limits**: Solicitations often impose strict page or word limits per KSA.
- **Evaluation**: KSAs are typically scored against criteria such as relevance, depth of experience, and clarity.

## How GovCon Data Can Help

GovCon Data's AI proposal assistant helps draft KSA-style narratives by pulling from your past performance database and tailoring language to the solicitation's evaluation criteria.

## Related Terms

- Past Performance
- Technical Proposal
- Source Selection
- Request for Proposal (RFP)`
  },
  {
    slug: 'lh-labor-hour',
    title: 'Labor Hour',
    abbreviation: 'LH',
    category: 'Contracts',
    definition: 'A contract type where the government pays for labor hours expended at negotiated hourly rates, with materials and other costs billed separately.',
    content: `## Overview

A Labor Hour (LH) contract is a variation of a time-and-materials (T&M) arrangement. The government pays a fixed hourly rate for each labor category plus actual cost for materials and other direct costs. Unlike a T&M contract with a ceiling, LH contracts may have different structures for labor vs. materials; the defining feature is payment based on hours worked.

## Why It Matters in GovCon

LH contracts are used when the government cannot accurately predict the level of effort required — such as maintenance, help desk support, or undefined research. They offer flexibility but require careful timekeeping and documentation. Contractors must track labor by category and ensure billing aligns with negotiated rates.

## Key Details

- **Hourly Rates**: Labor categories (e.g., Sr. Engineer, Program Manager) have fixed negotiated rates.
- **Materials/ODCs**: Pass-through or mark-up; typically billed at cost or with a small fee.
- **Ceiling**: Many LH contracts include a ceiling value to cap government exposure.
- **Audit Risk**: Improper labor charging is a common audit finding; robust timekeeping systems are essential.

## Related Terms

- Time and Materials (T&M)
- Level of Effort (LOE)
- Cost-Plus-Fixed-Fee (CPFF)
- Labor Category`
  },
  {
    slug: 'loe-level-of-effort',
    title: 'Level of Effort',
    abbreviation: 'LOE',
    category: 'Contracts',
    definition: 'A contract task or deliverable defined by a specified amount of labor (e.g., hours or staff-months) rather than discrete outputs or outcomes.',
    content: `## Overview

Level of Effort (LOE) describes contract work where the government pays for a predetermined quantity of labor — such as 12 staff-months or 2,000 hours — regardless of the specific deliverables produced. The contractor commits to providing the labor; exact outputs may not be defined in advance.

## Why It Matters in GovCon

LOE is common for advisory support, studies, program management, and tasks where scope is difficult to define upfront. It provides flexibility but can lead to disputes if the government expects specific deliverables while the contractor interprets the task as labor-only. Clear contract language is critical.

## Key Details

- **Staff-Months or Hours**: LOE is typically expressed as labor units (e.g., 24 FTE-months).
- **No Deliverable Guarantee**: Payment is tied to effort expended, not outcomes achieved.
- **Ceiling**: Contracts usually cap the total LOE; exceeding it requires a modification.
- **Documentation**: Timesheets and labor distribution reports support LOE billing.

## Related Terms

- Labor Hour (LH)
- Time and Materials (T&M)
- Indefinite Delivery/Indefinite Quantity (IDIQ)
- Task Order`
  },
  {
    slug: 'lcc-life-cycle-cost',
    title: 'Life Cycle Cost',
    abbreviation: 'LCC',
    category: 'Finance',
    definition: 'The total cost of owning, operating, maintaining, and disposing of a system or product over its entire useful life.',
    content: `## Overview

Life Cycle Cost (LCC) is the sum of all costs associated with a system from acquisition through disposal — including research, development, production, sustainment, upgrades, and decommissioning. The government uses LCC analysis to evaluate alternatives and make procurement decisions that consider long-term affordability, not just upfront price.

## Why It Matters in GovCon

For complex systems — aircraft, ships, IT infrastructure — sustainment costs often exceed acquisition costs. Proposals that emphasize low life cycle cost through reliability, maintainability, and supportability can win over lower-priced competitors with higher operational costs. LCC is a common evaluation factor in major acquisitions.

## Key Details

- **Cost Elements**: Development, acquisition, operations, maintenance, training, logistics, disposal.
- **Time Value of Money**: LCC models typically use net present value or similar techniques to compare costs over time.
- **DoD Emphasis**: DoD 5000 series and many agency guides require LCC analysis for major programs.
- **Contract Implications**: Some contracts tie award fee or incentives to meeting LCC targets.

## Related Terms

- Total Cost of Ownership (TCO)
- Cost-Plus Contract
- Fixed-Price Contract
- Major Defense Acquisition Program (MDAP)`
  },
  {
    slug: 'mro-maintenance-repair-and-operations',
    title: 'Maintenance, Repair, and Operations',
    abbreviation: 'MRO',
    category: 'Acquisition',
    definition: 'Goods and services used to maintain, repair, and operate facilities and equipment — a major category of government procurement.',
    content: `## Overview

Maintenance, Repair, and Operations (MRO) encompasses the supplies, parts, and services needed to keep buildings, vehicles, machinery, and infrastructure functioning. This includes everything from spare parts and lubricants to janitorial services, HVAC maintenance, and equipment repairs.

## Why It Matters in GovCon

MRO represents a substantial share of government spending — especially for DoD, GSA, and agencies with large real property portfolios. Contracts are often awarded through BPAs, GSA Schedules, or IDIQs. Understanding MRO procurement channels helps suppliers and service providers position for recurring business.

## Key Details

- **Broad Scope**: Includes consumables, replacement parts, facility services, and repair labor.
- **Common Vehicles**: GSA Multiple Award Schedules, BPAs, and agency-specific IDIQs.
- **Standards**: Many MRO items require NSNs or compliance with federal specifications.
- **Small Business**: MRO contracts are frequently set aside for small businesses.

## How GovCon Data Can Help

Filter opportunities by MRO-related NAICS codes and contract types. GovCon Data helps you identify recurring MRO solicitations and track which agencies and contract vehicles are active in your product or service area.

## Related Terms

- National Stock Number (NSN)
- Blanket Purchase Agreement (BPA)
- GSA Schedule
- Indefinite Delivery/Indefinite Quantity (IDIQ)`
  },
  {
    slug: 'mdap-major-defense-acquisition-program',
    title: 'Major Defense Acquisition Program',
    abbreviation: 'MDAP',
    category: 'Programs',
    definition: 'A DoD program for a weapon system or automated information system that exceeds designated cost thresholds and receives enhanced oversight.',
    content: `## Overview

A Major Defense Acquisition Program (MDAP) is a Defense Department acquisition that meets or exceeds cost thresholds established in statute — currently $480 million in RDT&E or $2.79 billion in procurement (in FY2020 base year dollars). MDAPs receive heightened oversight, mandatory milestone reviews, and reporting to Congress.

## Why It Matters in GovCon

MDAPs represent the largest and most complex defense contracts. Prime contractors and major subcontractors must navigate rigorous acquisition regulations, Earned Value Management requirements, and frequent program reviews. Understanding the MDAP structure is essential for firms pursuing or supporting these programs.

## Key Details

- **Cost Thresholds**: Adjusted annually; includes RDT&E and procurement totals.
- **Milestone Reviews**: Must pass Milestones A, B, and C with MDA approval.
- **Congressional Reporting**: Selected Acquisition Reports (SARs) and other disclosures required.
- **EVM**: Earned Value Management systems are mandatory for MDAPs.

## Related Terms

- Milestone Decision Authority (MDA)
- Key Decision Point (KDP)
- Defense Acquisition System
- Earned Value Management (EVM)`
  },
  {
    slug: 'mou-memorandum-of-understanding',
    title: 'Memorandum of Understanding',
    abbreviation: 'MOU',
    category: 'Acquisition',
    definition: 'A non-binding agreement between parties that outlines intentions, roles, and expectations for collaboration or coordination.',
    content: `## Overview

A Memorandum of Understanding (MOU) is a written document that expresses the parties' intent to cooperate or coordinate on a matter. Unlike a contract, an MOU typically does not create legally binding obligations — it establishes a framework for future agreements or joint efforts. Agencies use MOUs to formalize interagency agreements, partnerships with industry, or cross-border arrangements.

## Why It Matters in GovCon

MOUs often precede formal contracts. A contractor may sign an MOU with an agency to explore a technology partnership, pilot program, or joint venture. Understanding that MOUs are generally non-binding helps contractors manage expectations and negotiate language that protects their interests without over-committing.

## Key Details

- **Non-Binding**: Unless explicitly stated otherwise, MOUs express intent, not enforceable promises.
- **Scope**: Often used for information sharing, pilot projects, or coordination of resources.
- **Follow-On Contracts**: Successful MOU collaboration can lead to sole-source or competitive follow-on awards.
- **Distinct from MOA**: A Memorandum of Agreement (MOA) may be used when more formal commitment is intended.

## Related Terms

- Teaming Agreement
- Memorandum of Agreement (MOA)
- Interagency Agreement
- Other Transaction (OT)`
  },
  {
    slug: 'mda-milestone-decision-authority',
    title: 'Milestone Decision Authority',
    abbreviation: 'MDA',
    category: 'Programs',
    definition: "The designated individual with authority to approve a program's advancement past key acquisition milestones, including Milestones A, B, and C.",
    content: `## Overview

The Milestone Decision Authority (MDA) is the senior official — typically a Service Acquisition Executive, Defense Agency Director, or their delegate — who has the authority to approve or withhold approval for a major acquisition program to proceed through its life cycle. The MDA reviews program status at Key Decision Points and makes go/no-go decisions.

## Why It Matters in GovCon

The MDA's decisions directly affect program schedule, funding, and contract flow. Contractors must understand who holds MDA authority for their program, what evidence is required at each milestone, and how to support the program office in preparing for MDA reviews. Delays or restructuring at milestones can impact contract modifications and future competitions.

## Key Details

- **Delegation**: MDA authority can be delegated but is typically held at senior levels.
- **Milestones A, B, C**: Gate reviews for technology development, engineering development, and production.
- **Conditional Approval**: MDAs may approve with specific conditions (e.g., reduce scope, address risk).
- **Congressional Interface**: For MDAPs, MDA decisions may trigger congressional notifications.

## Related Terms

- Key Decision Point (KDP)
- Major Defense Acquisition Program (MDAP)
- Acquisition Program Baseline (APB)
- Defense Acquisition System`
  },
  {
    slug: 'mwbe-minority-and-women-owned-business-enterprise',
    title: 'Minority and Women-Owned Business Enterprise',
    abbreviation: 'MWBE',
    category: 'Programs',
    definition: 'A business that is at least 51% owned and controlled by minorities or women, often eligible for set-asides and subcontracting goals in government contracting.',
    content: `## Overview

Minority and Women-Owned Business Enterprise (MWBE) is a designation that combines minority-owned (MBE) and women-owned (WBE) status. Used primarily in state and local procurement, MWBE certification unlocks access to set-aside contracts, subcontracting goals, and diversity programs. Federal equivalents include 8(a), WOSB, SDVOSB, and HUBZone.

## Why It Matters in GovCon

SLED (state, local, education) agencies frequently have MWBE participation goals or set-asides. Prime contractors on large projects must often meet MWBE subcontracting targets. Certification through state or third-party bodies is required; requirements vary by jurisdiction.

## Key Details

- **Ownership**: At least 51% owned and controlled by minorities and/or women.
- **Certification Bodies**: State agencies, city agencies, or national certifiers like NMSDC or WBENC.
- **SLED Focus**: MWBE programs are most prevalent in state and local contracting.
- **Federal Distinction**: Federal small business programs use separate certifications (8(a), WOSB, etc.).

## How GovCon Data Can Help

Filter SLED opportunities by MWBE set-aside or diversity participation requirements. GovCon Data helps you identify which state and local solicitations have MWBE goals so you can pursue certification and target the right opportunities.

## Related Terms

- Minority Business Enterprise (MBE)
- Women-Owned Small Business (WOSB)
- Set-Aside
- 8(a) Business Development Program`
  },
  {
    slug: 'mbe-minority-business-enterprise',
    title: 'Minority Business Enterprise',
    abbreviation: 'MBE',
    category: 'Programs',
    definition: 'A for-profit business at least 51% owned and controlled by U.S. citizens who are members of designated minority groups, eligible for diversity programs and set-asides.',
    content: `## Overview

A Minority Business Enterprise (MBE) is a business at least 51% owned and controlled by individuals who are members of designated minority groups — including Black, Hispanic, Asian American, Native American, and other groups as defined by certifying bodies. MBE certification is used in state, local, and private sector diversity programs.

## Why It Matters in GovCon

MBE certification opens doors to set-aside contracts and subcontracting opportunities, particularly in SLED procurement. Primes on large projects often have MBE participation goals. Federal contracting uses the 8(a) program for disadvantaged businesses; MBE is the more common term in non-federal markets.

## Key Details

- **Designated Groups**: Varies by certifier; typically includes Black, Hispanic, Asian, Native American, and others.
- **Ownership and Control**: Must be 51%+ owned and day-to-day operations controlled by qualifying individuals.
- **Certification**: Through state agencies, NMSDC, or other approved bodies.
- **Subcontracting**: Primes may seek MBEs to meet diversity goals on federal and SLED contracts.

## Related Terms

- Minority and Women-Owned Business Enterprise (MWBE)
- 8(a) Business Development Program
- Disadvantaged Business Enterprise (DBE)
- Set-Aside`
  },
  {
    slug: 'mac-multiple-award-contract',
    title: 'Multiple Award Contract',
    abbreviation: 'MAC',
    category: 'Contracts',
    definition: 'A contract awarded to multiple vendors for similar supplies or services, allowing the government to compete individual orders among the holders.',
    content: `## Overview

A Multiple Award Contract (MAC) is a contract vehicle under which the government awards contracts to two or more vendors for the same or similar scope. When the government has a need, it competes a task order or delivery order among the MAC holders rather than conducting a new full and open competition. MACs are a subset of IDIQ contracts.

## Why It Matters in GovCon

MACs dominate federal procurement for services and IT. Winning a MAC position gives you the right to compete for orders; the real revenue comes from winning those orders. Understanding the MAC structure — fair opportunity, scope, and ordering procedures — is essential for capture and proposal strategy.

## Key Details

- **Fair Opportunity**: With limited exceptions, all MAC holders must receive a fair opportunity to compete for each order.
- **Scope**: Orders must fall within the scope of the underlying contract.
- **Examples**: GSA OASIS, NIH CIO-SP3, NASA SEWP, and many agency-specific IDIQs.
- **Protest**: Task order protests to GAO are limited by dollar thresholds.

## How GovCon Data Can Help

GovCon Data tracks MAC task order solicitations and helps you identify opportunities under your contract vehicles. Filter by contract name, NAICS, or agency to build a pipeline of order-level competitions.

## Related Terms

- Indefinite Delivery/Indefinite Quantity (IDIQ)
- Multiple Award Task Order Contract (MATOC)
- Fair Opportunity
- Task Order`
  },
  {
    slug: 'matoc-multiple-award-task-order-contract',
    title: 'Multiple Award Task Order Contract',
    abbreviation: 'MATOC',
    category: 'Contracts',
    definition: 'A multiple-award IDIQ contract specifically for services, under which the government issues task orders to multiple pre-qualified contractors.',
    content: `## Overview

A Multiple Award Task Order Contract (MATOC) is an indefinite delivery contract awarded to multiple vendors for services. The government competes individual task orders among MATOC holders, who have already demonstrated their qualifications during the base contract competition. MATOCs are common for construction, design-build, professional services, and facility support.

## Why It Matters in GovCon

MATOCs streamline ordering for agencies with recurring service needs. Once you hold a MATOC position, you compete for task orders — often with shorter proposal cycles than full solicitations. Construction and professional services firms frequently pursue MATOCs as a primary business development strategy.

## Key Details

- **Services Only**: MATOCs are for services; MADOcs (Multiple Award Delivery Order Contracts) are for supplies.
- **Pre-Qualification**: Contractors qualify at award; task order competitions focus on approach and price.
- **Fair Opportunity**: All holders typically receive a fair opportunity to compete.
- **Duration**: Base contract plus options; task orders can extend beyond base period.

## How GovCon Data Can Help

Search for MATOC solicitations and task order releases. GovCon Data's opportunity alerts can notify you when new task orders drop under MATOCs you hold, so you never miss a chance to compete.

## Related Terms

- Multiple Award Contract (MAC)
- Task Order
- Indefinite Delivery/Indefinite Quantity (IDIQ)
- Fair Opportunity`
  },
  {
    slug: 'nasa-sewp',
    title: 'NASA SEWP',
    category: 'Contracts',
    definition: 'A government-wide acquisition contract (GWAC) for IT products and services, managed by NASA and widely used across federal agencies.',
    content: `## Overview

NASA SEWP (Solutions for Enterprise-Wide Procurement) is one of the largest government-wide acquisition contracts for information technology. Managed by NASA's Goddard Space Flight Center, it provides federal agencies with a streamlined way to purchase IT hardware, software, cloud services, and related professional services from pre-vetted contractors.

## Why It Matters in GovCon

NASA SEWP is a major channel for federal IT dollars. Agencies order through SEWP to avoid running their own full competitions — meaning contractors on SEWP receive order requests directly. IT firms seeking federal sales should consider SEWP as a key vehicle for reaching multiple agencies.

## Key Details

- **GWAC**: Available for use by any federal agency.
- **Scope**: IT products, software, cloud, cybersecurity, and integration services.
- **Multiple Awards**: SEWP V has hundreds of contract holders across multiple groups.
- **Ordering**: Agencies issue delivery orders or task orders directly to SEWP contractors.
- **Low Fees**: SEWP charges minimal overhead compared to some GWACs.

## How GovCon Data Can Help

Track NASA SEWP task order opportunities and identify which agencies are active buyers. GovCon Data helps you monitor SEWP-related awards and plan your capture strategy for upcoming recompetes.

## Related Terms

- Government-Wide Acquisition Contract (GWAC)
- NASA
- OASIS+
- Multiple Award Contract (MAC)`
  },
  {
    slug: 'nasa',
    title: 'National Aeronautics and Space Administration',
    abbreviation: 'NASA',
    category: 'Agencies',
    definition: 'The U.S. federal agency responsible for civilian space exploration, aeronautics research, and scientific discovery, and a significant procurer of technology and research services.',
    content: `## Overview

The National Aeronautics and Space Administration (NASA) conducts space exploration, Earth science, and aeronautics research. Beyond its mission-driven work, NASA is a major buyer of engineering services, IT, construction, and research support. It also manages NASA SEWP, a widely used GWAC for federal IT procurement.

## Why It Matters in GovCon

NASA awards billions in contracts annually for everything from launch services to facility maintenance. The agency uses set-asides, GWACs, and specialized acquisition approaches. Understanding NASA's mission, centers, and procurement culture is essential for contractors in aerospace, IT, and professional services.

## Key Details

- **Centers**: Multiple field centers (Kennedy, Johnson, Goddard, etc.) with decentralized procurement.
- **NASA SEWP**: IT GWAC used government-wide.
- **Mission Support**: Significant spending on professional services, engineering, and construction.
- **SBIR/STTR**: Active in small business innovation programs.

## Related Terms

- NASA SEWP
- Government-Wide Acquisition Contract (GWAC)
- Small Business Innovation Research (SBIR)
- Space Technology`
  },
  {
    slug: 'naspo',
    title: 'National Association of State Procurement Officials',
    abbreviation: 'NASPO',
    category: 'Acquisition',
    definition: 'A cooperative that facilitates multistate cooperative purchasing agreements, allowing states to leverage collective buying power for common goods and services.',
    content: `## Overview

The National Association of State Procurement Officials (NASPO) is a nonprofit that helps state procurement directors collaborate and coordinate. NASPO ValuePoint is its cooperative purchasing program — multistate contracts that allow any participating state to order from pre-awarded vendors without running a new competition.

## Why It Matters in GovCon

NASPO ValuePoint contracts open access to state government markets across the U.S. Once you win a NASPO contract, states can "piggyback" onto it, dramatically expanding your addressable market. Vendors in IT, office supplies, fleet, and professional services frequently pursue NASPO as a SLED strategy.

## Key Details

- **ValuePoint**: Multistate cooperative contracts; "lead state" conducts competition, others join.
- **Participating States**: Most states participate in some NASPO contracts.
- **Categories**: IT, fleet, office supplies, facilities, professional services, and more.
- **No Guarantees**: NASPO awards do not guarantee orders; states still must choose to use the contract.

## How GovCon Data Can Help

Track NASPO solicitations and ValuePoint contract awards. GovCon Data's SLED opportunity search helps you find NASPO-related opportunities and understand which states are active in cooperative purchasing.

## Related Terms

- NASPO ValuePoint
- Cooperative Purchasing
- State and Local Government
- Set-Aside`
  },
  {
    slug: 'nispom',
    title: 'National Industrial Security Program Operating Manual',
    abbreviation: 'NISPOM',
    category: 'Security',
    definition: 'The DoD manual that establishes requirements for contractors who safeguard classified information under the National Industrial Security Program.',
    content: `## Overview

The National Industrial Security Program Operating Manual (NISPOM) is the primary document governing the protection of classified information in the hands of cleared contractors. It defines facility security clearance requirements, personnel clearance procedures, and physical and cybersecurity measures that contractors must implement.

## Why It Matters in GovCon

If your company holds or seeks a facility security clearance (FCL) to perform classified work, NISPOM compliance is mandatory. Failure to meet NISPOM requirements can result in loss of clearance, contract termination, and debarment. Understanding NISPOM is essential for defense and intelligence contractors.

## Key Details

- **Administered by DCSA**: The Defense Counterintelligence and Security Agency oversees the NISP.
- **Classification Levels**: Confidential, Secret, Top Secret — each with corresponding requirements.
- **FCL and PCL**: Facility Clearance (FCL) and Personnel Clearance (PCL) are distinct processes.
- **Updates**: NISPOM is periodically updated; contractors must stay current.

## Related Terms

- Facility Security Clearance (FCL)
- Defense Counterintelligence and Security Agency (DCSA)
- Classified Information
- National Industrial Security Program`
  },
  {
    slug: 'nih-national-institutes-of-health',
    title: 'National Institutes of Health',
    abbreviation: 'NIH',
    category: 'Agencies',
    definition: 'The primary federal agency for medical and health research, and a major procurer of research services, biomedical equipment, and IT.',
    content: `## Overview

The National Institutes of Health (NIH) is part of the Department of Health and Human Services and supports biomedical research to improve public health. NIH awards contracts for research support, lab equipment, IT systems, facility operations, and professional services. It also manages NIH CIO-SP3 and other GWACs.

## Why It Matters in GovCon

NIH is a major buyer of research and professional services. Its procurement spans IT GWACs, IDIQs for research support, and set-asides for small businesses. Life sciences, IT, and professional services firms frequently pursue NIH opportunities.

## Key Details

- **GWACs**: NIH CIO-SP3, CIO-SP4 for IT and professional services.
- **Research Support**: Contracts for lab management, animal care, clinical research support.
- **Set-Asides**: Active user of small business set-asides.
- **Institutes and Centers**: Multiple institutes (NCI, NIAID, etc.) with decentralized buying.

## Related Terms

- NIH CIO-SP3
- Government-Wide Acquisition Contract (GWAC)
- Health and Human Services (HHS)
- Research and Development`
  },
  {
    slug: 'nsn-national-stock-number',
    title: 'National Stock Number',
    abbreviation: 'NSN',
    category: 'Classification',
    definition: 'A 13-digit numeric code used by the federal government to identify and catalog supplies and materials in a standardized way.',
    content: `## Overview

A National Stock Number (NSN) is a 13-digit identifier assigned to each item in the federal supply system. It enables consistent identification of parts, supplies, and equipment across the military and civilian agencies. The NSN is used for cataloging, procurement, inventory management, and logistics.

## Why It Matters in GovCon

Many government solicitations — especially for supplies, spare parts, and MRO — reference NSNs. Contractors must be able to match their products to the correct NSN or obtain an NSN for new items. NSN assignment is managed through the Federal Logistics Information System (FLIS).

## Key Details

- **Structure**: 4-digit Federal Supply Class (FSC) + 9-digit National Item Identification Number (NIIN).
- **Assignment**: Defense Logistics Agency (DLA) manages NSN assignments.
- **Cataloging**: Items with NSNs appear in federal catalog systems used for procurement.
- **Replacement**: NSNs can be superseded when items are modified or replaced.

## Related Terms

- Federal Supply Class (FSC)
- Defense Logistics Agency (DLA)
- National Item Identification Number (NIIN)
- Maintenance, Repair, and Operations (MRO)`
  },
  {
    slug: 'nda-non-disclosure-agreement',
    title: 'Non-Disclosure Agreement',
    abbreviation: 'NDA',
    category: 'Contracts',
    definition: 'A legal agreement that restricts one or both parties from disclosing confidential information shared during business discussions or performance.',
    content: `## Overview

A Non-Disclosure Agreement (NDA) is a contract that protects confidential information from unauthorized disclosure. In government contracting, NDAs are commonly used in teaming discussions, during proposal preparation, and when the government shares proprietary or sensitive information with contractors.

## Why It Matters in GovCon

NDAs are routine in capture and proposal activities. Before sharing past performance data, pricing strategies, or technical approaches with potential teammates or subcontractors, parties often sign NDAs. Government-issued NDAs may be required when accessing classified or sensitive unclassified information.

## Key Details

- **Mutual vs. One-Way**: Mutual NDAs protect both parties' information; one-way NDAs protect only one party's information.
- **Duration**: NDA terms typically extend beyond the collaboration period.
- **Exclusions**: Information that is publicly available or independently developed is usually excluded.
- **Government NDAs**: SF 312 and other forms may be used for classified or sensitive data.

## Related Terms

- Teaming Agreement
- Organizational Conflict of Interest (OCI)
- Proprietary Information
- Memorandum of Understanding (MOU)`
  },
  {
    slug: 'nte-not-to-exceed',
    title: 'Not to Exceed',
    abbreviation: 'NTE',
    category: 'Contracts',
    definition: 'A ceiling or cap on the amount the government will pay under a contract, task order, or individual order.',
    content: `## Overview

Not to Exceed (NTE) refers to a maximum value limit placed on a contract, order, or line item. The contractor cannot bill above the NTE amount without a contract modification. NTE ceilings protect the government from cost overruns and help both parties manage budget expectations.

## Why It Matters in GovCon

NTE values appear in task orders, delivery orders, time-and-materials contracts, and labor-hour arrangements. Exceeding the NTE without proper modification can lead to unallowable costs and audit issues. Contractors must track burn rate against NTE and request modifications before reaching the ceiling.

## Key Details

- **Common in T&M and LH**: Time-and-materials and labor-hour contracts nearly always have an NTE.
- **Order-Level**: Individual task orders may have their own NTE separate from the base contract ceiling.
- **Modification Required**: To exceed NTE, the government must issue a modification increasing the ceiling.
- **Funding**: NTE is typically tied to obligated funds; exceeding NTE may violate the Anti-Deficiency Act.

## Related Terms

- Time and Materials (T&M)
- Labor Hour (LH)
- Ceiling Price
- Contract Modification`
  },
  {
    slug: 'noa-notice-of-award',
    title: 'Notice of Award',
    abbreviation: 'NOA',
    category: 'Solicitations',
    definition: 'A formal notification to the successful offeror that the government has selected them for contract award.',
    content: `## Overview

A Notice of Award (NOA) is the government's official communication to the winning bidder that their proposal has been accepted and a contract will be awarded. The NOA may be a letter, email, or formal document that instructs the contractor on next steps — such as signing the contract, providing bonds, or submitting additional documentation.

## Why It Matters in GovCon

Receiving an NOA is a major milestone, but it does not always mean the contract is final. Protests can stay performance, and the government may withdraw the award under certain circumstances. Contractors should carefully review the NOA for any conditions or deadlines and avoid publicizing the win until the protest period has passed.

## Key Details

- **Precedes Contract**: The NOA typically precedes the formal contract document.
- **Protest Period**: GAO protests must generally be filed within 10 days of award knowledge.
- **Debriefing**: Unsuccessful offerors may request a debriefing before the protest clock runs.
- **Public Notice**: Awards are often posted on SAM.gov and agency websites.

## Related Terms

- Contract Award
- Debriefing
- Protest
- Contract Execution`
  },
  {
    slug: 'omb-office-of-management-and-budget',
    title: 'Office of Management and Budget',
    abbreviation: 'OMB',
    category: 'Agencies',
    definition: 'The Executive Office agency that oversees federal budgeting, procurement policy, and implementation of presidential priorities across the government.',
    content: `## Overview

The Office of Management and Budget (OMB) assists the President in preparing the federal budget and supervises agency budgeting and management. OMB issues policy guidance that affects procurement — including circulars on cost principles, grants management, and regulatory review. It plays a central role in government-wide acquisition policy.

## Why It Matters in GovCon

OMB Circular A-76 influences outsourcing decisions. OMB cost principles and grant guidance affect how contractors account for costs. OMB's budget and policy priorities can shift spending toward or away from certain programs and acquisition approaches. Contractors tracking federal trends watch OMB for policy signals.

## Key Details

- **Circulars**: A-76 (competitive sourcing), A-123 (internal control), A-94 (discount rates).
- **Budget**: Coordinates agency budget submissions and administers apportionment.
- **Regulatory**: Reviews significant regulations before publication.
- **E.O. 14042**: OMB has issued guidance on federal contractor vaccine requirements and related matters.

## Related Terms

- Federal Acquisition Regulation (FAR)
- Cost Accounting Standards (CAS)
- Circular A-76
- Federal Budget`
  },
  {
    slug: 'opm-office-of-personnel-management',
    title: 'Office of Personnel Management',
    abbreviation: 'OPM',
    category: 'Agencies',
    definition: 'The federal agency responsible for human resources policy, background investigations, and workforce management across the government.',
    content: `## Overview

The Office of Personnel Management (OPM) manages the federal civilian workforce — from hiring and pay to retirement and benefits. OPM issues policies that affect federal employees and, indirectly, contractors. It also conducts background investigations for security clearances and manages USAJobs.

## Why It Matters in GovCon

OPM's role in clearances affects contractors doing classified work. OPM policies on telework, pay, and benefits can influence labor rates and workforce availability. Contractors providing HR services or supporting federal HR systems interact with OPM as a customer and policy body.

## Key Details

- **Clearances**: OPM (now DCSA) conducted background investigations for security clearances.
- **USAStaffing**: OPM manages the federal hiring platform.
- **Policy**: Federal leave, pay scales, and benefits are shaped by OPM guidance.
- **Contracting**: OPM awards contracts for IT, HR services, and support.

## Related Terms

- Defense Counterintelligence and Security Agency (DCSA)
- Security Clearance
- Federal Workforce
- USAJobs`
  },
  {
    slug: 'oasis',
    title: 'OASIS+',
    category: 'Contracts',
    definition: "A GSA multiple-award contract for professional services spanning management consulting, engineering, logistics, and other complex service domains.",
    content: `## Overview

OASIS+ is the GSA's next-generation multiple-award contract for professional services. It succeeds OASIS and provides federal agencies with a streamlined vehicle for complex service acquisitions — including management consulting, engineering, financial services, logistics, and more. OASIS+ is structured in domains and pools to match capabilities to needs.

## Why It Matters in GovCon

OASIS (and now OASIS+) has been one of the largest federal contract vehicles by task order volume. Professional services firms across consulting, engineering, and technical disciplines pursue OASIS+ as a primary channel for federal business. Winning a position opens access to task order competitions across many agencies.

## Key Details

- **Domains**: Multiple capability domains (e.g., management consulting, engineering, logistics).
- **Pools**: Contractors qualify for specific pools based on experience and past performance.
- **GWAC**: Available for use by any federal agency.
- **Fair Opportunity**: Task orders are competed among pool holders.

## How GovCon Data Can Help

Track OASIS+ task order solicitations and identify which domains and pools are most active. GovCon Data helps you monitor awards, plan for recompetes, and build a capture pipeline for OASIS+ opportunities.

## Related Terms

- GSA Schedule
- Government-Wide Acquisition Contract (GWAC)
- Multiple Award Contract (MAC)
- Task Order`
  },
  {
    slug: 'oci-organizational-conflict-of-interest',
    title: 'Organizational Conflict of Interest',
    abbreviation: 'OCI',
    category: 'Contracts',
    definition: "A situation where a contractor's other activities or relationships could impair its objectivity or create an unfair competitive advantage on a government contract.",
    content: `## Overview

An Organizational Conflict of Interest (OCI) arises when a contractor's involvement in one government activity could bias its judgment or give it an unfair advantage in another. Common scenarios include: a contractor helping draft a solicitation and then bidding on it; holding proprietary source selection information; or having financial interests that could affect impartiality.

## Why It Matters in GovCon

OCIs can disqualify an offeror from competing or performing. Agencies identify potential OCIs during source selection and may exclude offerors or require mitigation (e.g., firewall, divestiture). Contractors must disclose potential conflicts and implement mitigation plans when required.

## Key Details

- **FAR 9.5**: Governs identification and mitigation of OCIs.
- **Types**: Unequal access to information; biased ground rules; impaired objectivity.
- **Mitigation**: Organizational firewalls, divestiture, or recusal; not always sufficient.
- **Subcontractors**: Prime contractors must flow down OCI awareness to subs; sub involvement can create prime OCI.

## Related Terms

- Non-Disclosure Agreement (NDA)
- Source Selection
- Fair Competition
- Impaired Objectivity`
  },
  {
    slug: 'oem-original-equipment-manufacturer',
    title: 'Original Equipment Manufacturer',
    abbreviation: 'OEM',
    category: 'Products',
    definition: "A company that manufactures products or components that are marketed under another company's brand or incorporated into another company's product.",
    content: `## Overview

An Original Equipment Manufacturer (OEM) is a firm that produces equipment, components, or products that are sold under another entity's brand or used as parts of another product. In government contracting, OEM status can affect intellectual property rights, warranty obligations, and eligibility for certain contracts (e.g., sole-source for OEM parts).

## Why It Matters in GovCon

Agencies often require OEM parts for maintenance, compatibility, or warranty reasons. OEMs may have sole-source leverage for spare parts and upgrades. Distributors and resellers must understand OEM authorization requirements — many solicitations require OEM-approved or authorized distributors.

## Key Details

- **Sole-Source**: OEM parts may be procured sole-source when no acceptable alternative exists.
- **Authorization**: Reseller contracts may require OEM authorization or certification.
- **IP**: OEMs retain intellectual property rights; licensing affects government use and reprocurement.
- **Aftermarket**: Non-OEM (aftermarket) parts may be acceptable depending on solicitation terms.

## Related Terms

- Sole Source
- Authorized Distributor
- Spare Parts
- National Stock Number (NSN)`
  },
  {
    slug: 'odc-other-direct-cost',
    title: 'Other Direct Cost',
    abbreviation: 'ODC',
    category: 'Finance',
    definition: 'Costs that can be identified specifically with a contract or project but are not labor or materials — such as travel, subcontracts, or specialized services.',
    content: `## Overview

Other Direct Costs (ODCs) are costs that are directly attributable to a specific contract or project but do not fall into the labor or materials categories. Examples include travel, consultants, subcontracts, computer rental, and specialized testing. ODCs are charged to the contract and typically require supporting documentation.

## Why It Matters in GovCon

ODCs appear in cost-type and T&M contracts. Proposals must accurately estimate ODCs and ensure they are allowable under FAR cost principles. Inadequate ODC documentation is a common audit finding. Contractors must maintain receipts, timesheets, and allocation records.

## Key Details

- **Allowability**: ODCs must be allowable under FAR Part 31 or contract-specific cost principles.
- **Documentation**: Travel vouchers, subcontract invoices, rental agreements support ODC charges.
- **Allocability**: Must be allocable to the contract — shared costs require an allocation base.
- **Audit**: DCAA or other auditors review ODC reasonableness and support.

## Related Terms

- Direct Cost
- Indirect Cost
- Time and Materials (T&M)
- Cost-Plus Contract`
  },
  {
    slug: 'ot-other-transaction',
    title: 'Other Transaction',
    abbreviation: 'OT',
    category: 'Contracts',
    definition: 'A flexible agreement that is not a contract, grant, or cooperative agreement — used by DoD and other agencies to access commercial innovation with fewer procurement regulations.',
    content: `## Overview

An Other Transaction (OT) is a legally binding agreement that is specifically exempt from most federal procurement laws and the FAR. DoD and other agencies use OTs to prototype technologies, conduct research, and engage with commercial firms that might otherwise avoid government work. OTs offer flexibility in terms, IP, and payment structures.

## Why It Matters in GovCon

OTs have become a major pathway for non-traditional contractors — especially tech firms — to do business with DoD. They offer streamlined procedures, flexible IP arrangements, and access to projects that might not fit traditional contracting. Understanding OT authority and limits helps contractors evaluate whether to pursue OT opportunities.

## Key Details

- **Statutory Basis**: 10 U.S.C. 2371 (research), 2371b (prototype), 2373 (production follow-on).
- **Exempt from FAR**: OTs are not procurement contracts; many FAR provisions do not apply.
- **Types**: Research OTs, prototype OTs, production OTs (with limitations).
- **Consortia**: Often awarded through consortia (e.g., consortium managers facilitate member awards).

## Related Terms

- Other Transaction Authority (OTA)
- Prototype
- Research and Development
- Non-Traditional Contractor`
  },
  {
    slug: 'ota-other-transaction-authority',
    title: 'Other Transaction Authority',
    abbreviation: 'OTA',
    category: 'Contracts',
    definition: 'The statutory authority that allows agencies to enter into Other Transactions — flexible agreements outside standard procurement rules.',
    content: `## Overview

Other Transaction Authority (OTA) is the legal authority — granted by Congress to specific agencies — to enter into Other Transactions. DoD has the broadest OTA; NASA, DHS, DOE, and others have limited OTA. OTA enables agencies to use flexible agreements for research, prototyping, and production in circumstances where traditional contracting would be impractical.

## Why It Matters in GovCon

OTA has reshaped how DoD engages with commercial technology firms. Companies that would not consider a FAR-based contract may find OTA arrangements palatable. For traditional contractors, OTA represents both competition (new entrants) and opportunity (consortium participation, prototype projects).

## Key Details

- **DoD**: Broad authority for research (2371) and prototype (2371b) OTs; production (2373) has conditions.
- **Other Agencies**: NASA, DHS, DOE have OTA with varying scope.
- **Consortia**: DIU, MD5, and others manage OTA consortia; membership can open OT opportunities.
- **Follow-On Production**: Prototype OTs can lead to follow-on production without further competition under certain conditions.

## How GovCon Data Can Help

Search for OT and OTA opportunities alongside traditional solicitations. GovCon Data helps you identify consortium-managed opportunities and track which agencies are actively using OTA for your technology domain.

## Related Terms

- Other Transaction (OT)
- Defense Innovation Unit (DIU)
- Prototype
- Federal Acquisition Regulation (FAR)`
  },
  {
    slug: 'p-card',
    title: 'Purchase Card',
    abbreviation: 'P-Card',
    category: 'Acquisition',
    definition: 'A government-issued credit card used for micropurchases and low-dollar acquisitions, streamlining small procurements without formal contracting procedures.',
    content: `## Overview

The Purchase Card (P-Card) is a commercial credit card issued to authorized government personnel for making small purchases directly from vendors. It streamlines procurement for low-value acquisitions by bypassing the formal solicitation and contract award process, enabling faster procurement of routine supplies and services.

## Why It Matters in GovCon

P-Card spending represents billions in annual government purchases, typically under the micropurchase threshold ($10,000 for most agencies). Vendors who accept standard credit cards and register with common government purchasing systems can capture this revenue stream. Understanding P-Card limits and agency card programs helps contractors position for spot purchases.

## Key Details

- **Thresholds**: Most P-Card purchases fall under the micropurchase threshold; some agencies allow higher limits for specific categories.
- **No Formal Competition**: P-Card purchases bypass the normal competition requirements of the FAR.
- **Commercial Practices**: Agencies often use GSA Advantage, Amazon Business, or other pre-approved purchasing channels.
- **Spending Data**: P-Card transaction data can reveal agency buying patterns and preferences.

## Related Terms

- Micropurchase
- Simplified Acquisition Procedures (SAP)
- General Services Administration (GSA)
- Blanket Purchase Agreement (BPA)`
  },
  {
    slug: 'par-performance-assessment-report',
    title: 'Performance Assessment Report',
    abbreviation: 'PAR',
    category: 'Contracts',
    definition: 'A formal evaluation document recording how well a contractor performed on a contract, used in future source selection decisions.',
    content: `## Overview

The Performance Assessment Report (PAR) is the government's official record of contractor performance on a specific contract. PARs are entered into CPARS (Contractor Performance Assessment Reporting System) and document the contractor's execution against quality, schedule, cost, and other evaluation criteria. These assessments follow contractors across the federal marketplace.

## Why It Matters in GovCon

PARs directly influence your ability to win future work. Agencies retrieve past performance evaluations through CPARS when evaluating proposals, and subpar ratings can eliminate you from consideration. Proactively managing performance and responding to draft PARs within the 30-day comment period is critical for maintaining a strong record.

## Key Details

- **Evaluation Areas**: Quality, schedule, cost control, management, subcontracting (for small business), and business relations.
- **Rating Scale**: Exceptional, Very Good, Satisfactory, Marginal, or Unsatisfactory.
- **Contractor Response**: Contractors have 30 days to submit comments, which are retained with the PAR.
- **Relevance**: Agencies assess how relevant past performance is to the current requirement.

## How GovCon Data Can Help

GovCon Data's bid management tools help you track performance milestones and deliverables, ensuring you meet contract requirements that lead to strong PAR ratings.

## Related Terms

- Past Performance
- Contractor Performance Assessment Reporting System (CPARS)
- Best Value
- Past Performance Information Retrieval System (PPIRS)`
  },
  {
    slug: 'pba-performance-based-acquisition',
    title: 'Performance-Based Acquisition',
    abbreviation: 'PBA',
    category: 'Regulations',
    definition: 'An acquisition approach that emphasizes required outcomes and performance standards rather than prescribing how work must be accomplished.',
    content: `## Overview

Performance-Based Acquisition (PBA) is a procurement methodology mandated by the FAR that shifts the focus from prescribing methods and processes to defining measurable outcomes. The government specifies what it needs achieved and the standards for success, leaving contractors free to propose the most effective approach. PBA supports innovation and reduces government oversight burden.

## Why It Matters in GovCon

PBA fundamentally changes the proposal landscape. Instead of responding to detailed task lists, you propose solutions that achieve stated outcomes. Your technical approach and past performance demonstrating similar outcomes become the differentiators. Understanding PBA principles helps you write proposals that emphasize results over process.

## Key Details

- **FAR Preference**: FAR Part 37 establishes PBA as the preferred approach for service contracts.
- **Key Documents**: Performance Work Statement (PWS), Quality Assurance Surveillance Plan (QASP), and measurable performance standards.
- **Outcome-Focused**: Describes the end state rather than the path to get there.
- **Flexibility**: Contractors have latitude to innovate within the performance framework.

## Related Terms

- Performance Work Statement (PWS)
- Quality Assurance Surveillance Plan (QASP)
- Statement of Work (SOW)
- Performance-Based Incentive (PBI)`
  },
  {
    slug: 'pbi-performance-based-incentive',
    title: 'Performance-Based Incentive',
    abbreviation: 'PBI',
    category: 'Contracts',
    definition: 'A contractual mechanism that rewards contractors for exceeding performance targets or achieving specified outcomes, aligning payment with results.',
    content: `## Overview

A Performance-Based Incentive (PBI) is a contract provision that provides additional fee or payment when the contractor achieves or exceeds defined performance objectives. PBIs align contractor motivation with government goals, creating financial rewards for superior delivery. They are commonly paired with Performance Work Statements in performance-based contracts.

## Why It Matters in GovCon

PBIs can significantly increase contract value for high performers. Understanding the incentive structure during proposal preparation helps you price accordingly and design your approach to maximize the likelihood of earning incentives. Conversely, contracts may include negative incentives or deductions for falling short of standards.

## Key Details

- **Types**: Positive incentives for exceeding targets; negative incentives (deductions) for non-performance.
- **Metrics**: Tied to measurable outcomes such as uptime, response times, customer satisfaction, or cost savings.
- **Fee Structure**: May be a percentage of contract value or fixed dollar amounts per metric.
- **Documentation**: Incentive criteria and measurement methods must be clearly defined in the contract.

## Related Terms

- Performance-Based Acquisition (PBA)
- Performance Work Statement (PWS)
- Quality Assurance Surveillance Plan (QASP)
- Service Level Agreement (SLA)`
  },
  {
    slug: 'piv-personal-identity-verification',
    title: 'Personal Identity Verification',
    abbreviation: 'PIV',
    category: 'Security',
    definition: 'A federal standard for secure credentialing that uses smart cards for physical and logical access to federal facilities and systems.',
    content: `## Overview

Personal Identity Verification (PIV) refers to the federal credentialing standard established by Homeland Security Presidential Directive 12 (HSPD-12). PIV cards are smart cards that combine physical identity verification with cryptographic authentication for access to federal buildings and IT systems. Contractors performing on federal sites often require PIV credentials.

## Why It Matters in GovCon

Many federal contracts, particularly those involving classified or sensitive work, require contractor personnel to obtain PIV cards. The sponsorship and vetting process can take weeks or months. Proposals must account for PIV requirements in staffing plans and schedules. Lack of PIV-ready staff can disqualify otherwise qualified teams.

## Key Details

- **HSPD-12**: Mandates a common identification standard across federal agencies.
- **Sponsorship**: A federal agency must sponsor contractor employees for PIV issuance.
- **Background Check**: Requires a favorable National Agency Check with Inquiries (NACI) or equivalent.
- **Logical Access**: PIV cards enable two-factor authentication for federal IT systems.
- **CAC**: The DoD equivalent is the Common Access Card (CAC).

## Related Terms

- Common Access Card (CAC)
- HSPD-12
- Facility Clearance
- Public Key Infrastructure (PKI)`
  },
  {
    slug: 'poa-m-plan-of-action-and-milestones',
    title: 'Plan of Action and Milestones',
    abbreviation: 'POA&M',
    category: 'Security',
    definition: 'A document that identifies security deficiencies and outlines the steps and timeline for remediating them, required for many federal contractors.',
    content: `## Overview

A Plan of Action and Milestones (POA&M) is a corrective action plan that documents known security weaknesses in a contractor's information system and specifies how and when those weaknesses will be addressed. POA&Ms are a standard requirement for contractors handling federal information under NIST SP 800-171 and CMMC assessments.

## Why It Matters in GovCon

A POA&M allows contractors to receive contracts despite having unresolved security deficiencies, provided they document a credible plan to fix them within an agreed timeline. Agencies evaluate POA&Ms when assessing contractor risk. Unmitigated high-risk items can block contract award or lead to findings during assessments.

## Key Details

- **Required Elements**: Weakness description, remediation steps, resources required, milestones, and completion dates.
- **Risk Levels**: High-risk items typically require faster remediation than moderate or low.
- **Ongoing Updates**: POA&Ms must be updated as weaknesses are addressed or new ones are discovered.
- **CMMC**: POA&Ms are permitted for some controls at CMMC Level 2 but not all.
- **NIST Alignment**: Tied to the 110 controls in NIST SP 800-171.

## How GovCon Data Can Help

GovCon Data's compliance tracking features help you monitor POA&M due dates and ensure security remediation milestones are met before they affect contract eligibility.

## Related Terms

- System Security Plan (SSP)
- NIST SP 800-171
- Cybersecurity Maturity Model Certification (CMMC)
- Risk Management Framework (RMF)`
  },
  {
    slug: 'poc-point-of-contact',
    title: 'Point of Contact',
    abbreviation: 'POC',
    category: 'Regulations',
    definition: 'The designated government or contractor individual responsible for communications regarding a specific procurement or contract.',
    content: `## Overview

A Point of Contact (POC) is the named individual designated to handle questions, submissions, and coordination for a particular acquisition or contract. Solicitations specify a POC for proposal submissions; contracts designate POCs for technical and administrative matters. Clear POC designation ensures efficient communication and avoids unauthorized commitments.

## Why It Matters in GovCon

Knowing the correct POC is essential for submitting questions during the solicitation period, understanding evaluation criteria, and managing contract execution. Contacting the wrong person can delay responses or lead to non-binding information. Contractors should also designate clear POCs in proposals and during performance.

## Key Details

- **Solicitation POC**: Named in the synopsis and RFP; receives questions, amendments, and proposals.
- **Contract POC**: The COR is typically the technical POC; the CO remains the only binding authority.
- **Q&A Deadlines**: Questions to the POC must often be submitted by a cutoff date.
- **Contractor POC**: Proposals usually identify the contractor's proposed POC for program management.

## Related Terms

- Contracting Officer (CO)
- Contracting Officer's Representative (COR)
- Request for Proposal (RFP)
- Amendment`
  },
  {
    slug: 'pco-procuring-contracting-officer',
    title: 'Procuring Contracting Officer',
    abbreviation: 'PCO',
    category: 'Agencies',
    definition: 'The contracting officer responsible for awarding a new contract, as distinguished from the administrative contracting officer who manages it afterward.',
    content: `## Overview

The Procuring Contracting Officer (PCO) is the government official with authority to conduct the procurement and award a new contract. The PCO manages the solicitation, evaluates proposals, conducts discussions, and signs the award. Once the contract is awarded, an Administrative Contracting Officer (ACO) typically assumes responsibility for administration.

## Why It Matters in GovCon

The PCO is your primary point of contact throughout the source selection process. All formal communications — questions, proposal submissions, debriefing requests — go through the PCO. Understanding the PCO's role helps you navigate the procurement correctly and avoid communicating with unauthorized personnel.

## Key Details

- **Award Authority**: Only the PCO can bind the government to a contract.
- **Pre-Award**: Handles solicitations, amendments, discussions, and source selection.
- **Handoff**: Upon award, the ACO takes over for administration, modifications, and closeout.
- **Small Procurements**: For smaller buys, the same person may serve as both PCO and ACO.
- **Warrant**: PCOs hold a contracting warrant specifying their dollar authority limits.

## Related Terms

- Contracting Officer (CO)
- Administrative Contracting Officer (ACO)
- Source Selection Authority (SSA)
- Contract Award`
  },
  {
    slug: 'psc-product-service-code',
    title: 'Product Service Code',
    abbreviation: 'PSC',
    category: 'Classification',
    definition: 'A four-character alphanumeric code used to classify products and services in federal contracting, enabling reporting and opportunity categorization.',
    content: `## Overview

The Product Service Code (PSC) is a standardized classification system used across the federal government to categorize the products and services being procured. PSCs appear on solicitations, contract awards, and spending reports. The system includes thousands of codes covering everything from IT services to construction materials to advisory services.

## Why It Matters in GovCon

PSCs help contractors and agencies alike organize and search the contracting landscape. Federal databases use PSCs for market research and procurement forecasting. Understanding which PSCs align with your capabilities ensures you find relevant opportunities and that your SAM profile accurately reflects what you sell.

## Key Details

- **Structure**: Four-character alphanumeric (e.g., R425 = IT and Telecom - Systems Development).
- **FPDS**: The Federal Procurement Data System uses PSCs for all contract reporting.
- **Multiple PSCs**: A single contract can have multiple PSCs for different CLINs or task areas.
- **PSC to NAICS**: PSCs often map to NAICS codes but serve different purposes — PSC describes what is being bought; NAICS determines size standards.
- **Updates**: GSA maintains the PSC manual; codes are periodically added or revised.

## How GovCon Data Can Help

GovCon Data lets you search and filter opportunities by PSC, ensuring you discover contracts that match your product and service offerings beyond NAICS alone.

## Related Terms

- NAICS Codes
- Federal Procurement Data System (FPDS)
- Special Item Number (SIN)
- Contract Line Item Number (CLIN)`
  },
  {
    slug: 'pm-program-manager',
    title: 'Program Manager',
    abbreviation: 'PM',
    category: 'Programs',
    definition: 'The government official responsible for defining requirements, managing a program budget, and overseeing contractor performance on a specific program.',
    content: `## Overview

The Program Manager (PM) is the government employee who owns the mission need and manages the program from a technical and budgetary perspective. The PM defines what the government needs, works with the contracting office to acquire it, and oversees delivery. PMs are typically part of the requiring activity, not the contracting shop.

## Why It Matters in GovCon

The PM drives requirements and has significant influence over procurement strategy and evaluation criteria. Building relationships with PMs during market research and industry engagement helps you understand their priorities before the RFP drops. The PM's endorsement of an approach can shape the solicitation in ways that favor capable offerors.

## Key Details

- **Requirements Owner**: Defines the statement of work or performance work statement.
- **Budget Authority**: Manages program funding and justifies appropriations.
- **Technical Oversight**: Works with the COR to ensure contractor delivery meets needs.
- **No Contract Authority**: The PM cannot modify contracts or obligate funds; only the CO can.
- **Acquisition Support**: The PM supports the CO with technical evaluation and source selection input.

## Related Terms

- Program Executive Officer (PEO)
- Contracting Officer (CO)
- Contracting Officer's Representative (COR)
- Program of Record (POR)`
  },
  {
    slug: 'por-program-of-record',
    title: 'Program of Record',
    abbreviation: 'POR',
    category: 'Programs',
    definition: 'A formally approved and funded program that has been authorized through the budgeting and congressional appropriation process.',
    content: `## Overview

A Program of Record (POR) is an official program that has received formal approval and dedicated funding through the federal budget process. In DoD and many civilian agencies, PORs have gone through requirements validation, resource allocation, and congressional authorization. They represent sustained, multi-year commitments rather than ad-hoc projects.

## Why It Matters in GovCon

PORs drive predictable, recurring contracting opportunities. Contractors who understand which programs are on the books can anticipate follow-on work, modernization efforts, and support contracts. PORs typically have defined lifecycles with scheduled milestones, creating visibility into future procurement needs.

## Key Details

- **Budget Visibility**: PORs appear in budget justification documents and appropriations.
- **Lifecycle**: May have acquisition phases (development, production, sustainment) with associated contracts.
- **Stability**: More stable than pilot programs or one-off initiatives.
- **DoD Context**: Major defense programs are PORs managed through the formal acquisition process.
- **Related Contracts**: A single POR may have multiple contracts for development, integration, logistics, and operations.

## Related Terms

- Program Manager (PM)
- Program Executive Officer (PEO)
- Indefinite Delivery/Indefinite Quantity (IDIQ)
- Task Order`
  },
  {
    slug: 'peo-program-executive-officer',
    title: 'Program Executive Officer',
    abbreviation: 'PEO',
    category: 'Programs',
    definition: 'A senior government official who oversees multiple related acquisition programs and reports to agency leadership.',
    content: `## Overview

The Program Executive Officer (PEO) is a senior acquisition official responsible for a portfolio of related programs. PEOs provide strategic direction, resolve cross-program issues, and ensure alignment with agency and departmental priorities. The PEO structure is common in DoD, VA, and other large agencies with complex acquisition portfolios.

## Why It Matters in GovCon

PEOs influence acquisition strategy across their portfolio. Understanding the PEO structure helps contractors identify decision-makers, anticipate program direction, and tailor business development efforts. PEO briefings and industry days are key venues for visibility and relationship building.

## Key Details

- **Portfolio Management**: Typically oversees multiple Program Managers and their programs.
- **Senior Level**: Reports to Service Acquisition Executive or agency equivalent.
- **Acquisition Authority**: PEOs often have acquisition decision authority for their programs.
- **DoD Model**: Each military department has PEOs for major domains (e.g., PEO Soldier, PEO C3I).
- **Industry Engagement**: PEOs participate in industry days and may set strategic direction for procurements.

## Related Terms

- Program Manager (PM)
- Program of Record (POR)
- Acquisition
- Contracting Officer (CO)`
  },
  {
    slug: 'pmbok',
    title: 'Project Management Body of Knowledge',
    abbreviation: 'PMBOK',
    category: 'Management',
    definition: 'The PMI-published guide that defines best practices and processes for project management, widely referenced in government contract management.',
    content: `## Overview

The Project Management Body of Knowledge (PMBOK) is the flagship standard published by the Project Management Institute (PMI) that codifies project management best practices. It defines processes, knowledge areas, and terminology used across industries. Many government contracts reference PMBOK-aligned approaches for project management and earned value management.

## Why It Matters in GovCon

Agencies often require contractors to use PMBOK-aligned methodologies, particularly for complex IT and professional services contracts. Proposals that reference PMBOK processes and terminology demonstrate professional rigor. PMBOK's work breakdown structure and earned value concepts align with federal EVM requirements.

## Key Details

- **Knowledge Areas**: Integration, scope, schedule, cost, quality, resource, communications, risk, procurement, and stakeholder management.
- **Process Groups**: Initiating, planning, executing, monitoring and controlling, closing.
- **Updates**: PMBOK Guide is periodically updated; current editions emphasize agile and hybrid approaches.
- **EVM Alignment**: PMBOK's cost and schedule management aligns with ANSI/EIA-748 earned value guidelines.
- **Certification**: PMP certification is based on PMBOK knowledge.

## Related Terms

- Project Management Professional (PMP)
- Work Breakdown Structure (WBS)
- Earned Value Management (EVM)
- Program Manager (PM)`
  },
  {
    slug: 'pmp-project-management-professional',
    title: 'Project Management Professional',
    abbreviation: 'PMP',
    category: 'Personnel',
    definition: 'A globally recognized certification from PMI that validates competency in project management, often required or preferred for key personnel on government contracts.',
    content: `## Overview

The Project Management Professional (PMP) certification is the Project Management Institute's premier credential for project managers. It validates knowledge of PMBOK processes, agile practices, and practical experience. Many government solicitations specify PMP certification as a requirement or evaluation factor for the project manager or key personnel.

## Why It Matters in GovCon

PMP certification strengthens proposal competitiveness. When solicitations require or prefer PMP-certified project managers, having qualified staff is a gating factor. Certifications are often scored in the management or key personnel evaluation factor. Maintaining PMP-certified staff supports bidding on a broad range of professional services and IT contracts.

## Key Details

- **Prerequisites**: Experience and education requirements must be met before sitting for the exam.
- **Exam**: Computer-based test covering predictive, agile, and hybrid approaches.
- **PDUs**: PMPs must earn Professional Development Units to maintain certification (every three years).
- **Proposal Impact**: Resumes highlighting PMP certification satisfy common key personnel requirements.
- **Industry Standard**: Recognized across government and commercial sectors.

## Related Terms

- Project Management Body of Knowledge (PMBOK)
- Key Personnel
- Program Manager (PM)
- Subject Matter Expert (SME)`
  },
  {
    slug: 'pki-public-key-infrastructure',
    title: 'Public Key Infrastructure',
    abbreviation: 'PKI',
    category: 'Security',
    definition: 'A framework of policies and technologies that enable secure authentication and encryption using public-key cryptography, foundational to federal IT security.',
    content: `## Overview

Public Key Infrastructure (PKI) is the system of certificate authorities, digital certificates, and cryptographic keys that enables secure authentication, digital signatures, and encrypted communications. Federal agencies and contractors use PKI for PIV/CAC authentication, encrypted email, secure web connections, and code signing. PKI is a cornerstone of federal cybersecurity.

## Why It Matters in GovCon

Contractors handling federal data often must integrate with federal PKI, including the Federal Bridge Certificate Authority and agency-specific PKI. Proposals for IT and cybersecurity work frequently require PKI expertise. Understanding PKI requirements is essential for compliance with FISMA, NIST standards, and contract security clauses.

## Key Details

- **Certificate Authority (CA)**: Issues and validates digital certificates.
- **Federal PKI**: The U.S. government operates a hierarchy of CAs for federal systems.
- **PIV/CAC**: Smart cards use PKI for two-factor authentication.
- **Encryption**: PKI enables TLS/SSL for secure communications.
- **NIST Standards**: NIST SP 800-57 provides guidance on key management.
- **Contract Requirements**: IT contracts often mandate PKI for accessing federal systems.

## Related Terms

- Personal Identity Verification (PIV)
- Common Access Card (CAC)
- NIST SP 800-171
- System Security Plan (SSP)`
  },
  {
    slug: 'po-purchase-order',
    title: 'Purchase Order',
    abbreviation: 'PO',
    category: 'Finance',
    definition: 'A document issued by the government authorizing a vendor to deliver goods or services and establishing the terms of payment.',
    content: `## Overview

A Purchase Order (PO) is a contractual document the government uses to authorize a contractor to perform work and to establish the obligation of funds. For simplified acquisitions and commercial item purchases, the PO serves as the contract. It specifies the items or services, quantities, prices, delivery terms, and payment instructions.

## Why It Matters in GovCon

POs are the primary contract vehicle for many small and simplified acquisitions. Understanding PO terms — including payment procedures like Wide Area Workflow (WAWF) for DoD — ensures timely invoicing and payment. POs against BPAs and GSA Schedules are common; tracking PO numbers and funding is essential for contract administration.

## Key Details

- **Simplified Acquisitions**: POs are often used for purchases under the simplified acquisition threshold.
- **Commercial Items**: FAR Part 12 allows streamlined PO procedures for commercial buys.
- **Obligation**: The PO obligates government funds when accepted by the contractor.
- **Invoicing**: Contractors reference the PO number on invoices; DoD uses WAWF for submission.
- **Modifications**: PO changes require written modification by the issuing authority.

## Related Terms

- Simplified Acquisition Procedures (SAP)
- Blanket Purchase Agreement (BPA)
- Wide Area Workflow (WAWF)
- Purchase Card (P-Card)`
  },
  {
    slug: 'qpl-qualified-products-list',
    title: 'Qualified Products List',
    abbreviation: 'QPL',
    category: 'Products',
    definition: 'A government-maintained list of products that have been tested and approved as meeting specified standards, enabling streamlined procurement.',
    content: `## Overview

A Qualified Products List (QPL) is a government-maintained listing of products that have been tested and certified as meeting military or federal specifications. Once a product is on the QPL, the government can purchase it without repeating qualification testing. QPLs are used for items where performance, safety, or interoperability must be verified.

## Why It Matters in GovCon

For manufacturers, getting your product on a QPL opens a direct path to government sales. Solicitations often specify "or equal" from the QPL, but QPL-listed products have a proven track record. The qualification process requires investment in testing and documentation, but the payoff is reduced competition and faster procurement for qualified products.

## Key Details

- **Qualification Process**: Products must pass testing per the applicable specification (e.g., MIL-SPEC).
- **Listing Maintenance**: Manufacturers must maintain qualification; some QPLs require periodic re-qualification.
- **Or Equal**: Solicitations may allow "or equal" products, but QPL listing provides assurance.
- **Defense Standardization**: QPLs are common in DoD for components, materials, and equipment.
- **Simplified Acquisition**: QPL products can be procured without full competitive procedures in some cases.

## Related Terms

- Federal Supply Schedule (FSS)
- Solicitation
- Specifications
- Product Service Code (PSC)`
  },
  {
    slug: 'qcp-quality-control-plan',
    title: 'Quality Control Plan',
    abbreviation: 'QCP',
    category: 'Management',
    definition: 'A contractor-developed plan describing how quality will be assured during contract performance, often required in solicitations.',
    content: `## Overview

A Quality Control Plan (QCP) is a document that describes the contractor's approach to ensuring deliverables and services meet contract requirements. It defines quality standards, inspection procedures, roles and responsibilities, and corrective action processes. Many solicitations require a QCP as a deliverable or as part of the management approach.

## Why It Matters in GovCon

The QCP is typically an evaluated proposal element and a contract requirement. A well-designed QCP aligns with the government's Quality Assurance Surveillance Plan (QASP) and demonstrates your commitment to consistent performance. During execution, the QCP guides your internal quality processes and supports positive past performance evaluations.

## Key Details

- **QASP Alignment**: The QCP should address each surveillance area in the government's QASP.
- **Roles**: Defines who is responsible for quality oversight, inspections, and sign-off.
- **Processes**: Describes how defects are identified, documented, and corrected.
- **Deliverables**: May be a contract deliverable due within a set period after award.
- **Continuous Improvement**: Strong QCPs include feedback loops and process improvement.

## Related Terms

- Quality Assurance Surveillance Plan (QASP)
- Performance Work Statement (PWS)
- Key Personnel
- Past Performance`
  },
  {
    slug: 'r-d-research-and-development',
    title: 'Research and Development',
    abbreviation: 'R&D',
    category: 'Products',
    definition: 'Work performed to expand scientific or technical knowledge and apply it to new or improved products, processes, or services — a major category of federal contracting.',
    content: `## Overview

Research and Development (R&D) encompasses activities to create new knowledge or apply existing knowledge in new ways. Federal R&D spending spans basic research, applied research, and development across agencies like DoD, NIH, DOE, DARPA, and NSF. R&D contracts often use cost-reimbursement vehicle types and support innovation programs like SBIR and STTR.

## Why It Matters in GovCon

R&D represents tens of billions in annual federal contract spending. R&D solicitations often seek innovative solutions with higher tolerance for technical risk. Understanding Technology Readiness Levels (TRLs) and proposal requirements for R&D work helps contractors compete. SBIR and STTR provide structured pathways for small businesses into R&D.

## Key Details

- **Contract Types**: R&D commonly uses CPFF, CPIF, or hybrid vehicles due to uncertain scope.
- **TRLs**: Technology Readiness Levels describe maturation from basic research (TRL 1) to operational use (TRL 9).
- **SBIR/STTR**: Small business set-aside programs specifically for R&D.
- **Broad Agency Announcements**: BAAs are often used for R&D procurements.
- **IP Rights**: R&D contracts have specific data rights and patent provisions.

## How GovCon Data Can Help

GovCon Data helps you discover R&D opportunities across federal agencies, filter by SBIR/STTR set-asides, and track Technology Readiness Level requirements to match your capabilities.

## Related Terms

- Small Business Innovation Research (SBIR)
- Small Business Technology Transfer (STTR)
- Technology Readiness Level (TRL)
- Cost Plus Fixed Fee (CPFF)`
  },
  {
    slug: 'rmf-risk-management-framework',
    title: 'Risk Management Framework',
    abbreviation: 'RMF',
    category: 'Management',
    definition: 'The NIST-prescribed process for managing cybersecurity risk to federal systems, comprising six steps from categorization through ongoing monitoring.',
    content: `## Overview

The Risk Management Framework (RMF) is the structured process established by NIST (SP 800-37) for securing federal information systems. It replaces the legacy Certification and Accreditation process with a six-step lifecycle: categorize, select, implement, assess, authorize, and monitor. Contractors operating federal systems or handling federal data must often comply with RMF requirements.

## Why It Matters in GovCon

RMF compliance is a contractual requirement for many IT and cybersecurity contracts. Understanding the RMF steps — and the relationship to NIST SP 800-53 controls, System Security Plans, and POA&Ms — is essential for proposing and performing on federal security work. Authorization to Operate (ATO) is the outcome of successful RMF execution.

## Key Details

- **Six Steps**: Categorize (FIPS 199), Select (SP 800-53), Implement, Assess, Authorize, Monitor.
- **Control Families**: NIST SP 800-53 defines security controls across 20 families.
- **Authorization**: The Authorizing Official issues the ATO based on risk assessment.
- **Continuous Monitoring**: Ongoing assessment replaces point-in-time certification.
- **Contractor Systems**: Contractor systems processing federal data may require RMF compliance.

## How GovCon Data Can Help

GovCon Data's compliance tracking helps you monitor RMF milestones, POA&M due dates, and control assessment schedules to stay on track for authorization.

## Related Terms

- NIST SP 800-171
- System Security Plan (SSP)
- Plan of Action and Milestones (POA&M)
- Cybersecurity Maturity Model Certification (CMMC)`
  },
  {
    slug: 'rom-rough-order-of-magnitude',
    title: 'Rough Order of Magnitude',
    abbreviation: 'ROM',
    category: 'Finance',
    definition: 'A preliminary cost estimate used for planning and budgeting before detailed requirements are known, typically expressed as a range.',
    content: `## Overview

A Rough Order of Magnitude (ROM) estimate is an early-stage cost approximation used when requirements are incomplete or evolving. ROMs are typically expressed as a range (e.g., $1M–$2M) and have wide variance — often ±50% or more. They support budgeting, feasibility analysis, and go/no-go decisions before the government commits to a formal procurement.

## Why It Matters in GovCon

ROMs appear in market research, RFIs, and pre-solicitation phases. Contractors may be asked to provide ROM estimates to help the government scope acquisitions and secure funding. ROMs are non-binding and should be clearly qualified. Understanding when a ROM is appropriate — versus when a firm estimate is needed — avoids overcommitting or under-scoping.

## Key Details

- **Accuracy**: ROMs are intentionally imprecise; ±25% to ±50% variance is typical.
- **Purpose**: Budget planning, appropriation requests, and procurement strategy.
- **Non-Binding**: ROM responses do not commit the contractor to pricing.
- **Evolution**: As requirements mature, ROMs give way to detailed estimates and IGCEs.
- **Proposal Context**: Some solicitations request ROM pricing for options or out-of-scope work.

## Related Terms

- Independent Government Cost Estimate (IGCE)
- Market Research
- Request for Information (RFI)
- Cost Plus Fixed Fee (CPFF)`
  },
  {
    slug: 'sap-simplified-acquisition-procedures',
    title: 'Simplified Acquisition Procedures',
    abbreviation: 'SAP',
    category: 'Acquisition',
    definition: 'Streamlined procurement procedures for acquisitions below the simplified acquisition threshold, reducing administrative burden for low-dollar buys.',
    content: `## Overview

Simplified Acquisition Procedures (SAP) are the streamlined procurement rules that apply to purchases below the Simplified Acquisition Threshold (currently $250,000 for most acquisitions, higher for certain commercial items). SAP reduces competition and documentation requirements compared to full-and-open competition, enabling faster procurement of routine needs.

## Why It Matters in GovCon

SAP represents a high-volume segment of government buying. Small businesses and new entrants often find SAP procurements more accessible — shorter response times, simpler proposals, and lower barriers to entry. Building past performance through SAP awards positions you for larger opportunities. GSA Schedule and BPA orders often use SAP.

## Key Details

- **Threshold**: $250,000 for most acquisitions; $7.5 million for commercial items under certain conditions.
- **FAR Part 13**: Governs simplified acquisition procedures.
- **Competition**: Requires competition "to the maximum extent practicable" but allows flexible approaches.
- **Commercial Items**: FAR Part 12 commercial item procedures are often used with SAP.
- **Micro-Purchase**: Purchases under $10,000 (micro-purchase threshold) have even more flexibility.

## How GovCon Data Can Help

GovCon Data surfaces simplified acquisition opportunities alongside larger procurements, helping you build a diverse pipeline of accessible contracts for revenue and past performance.

## Related Terms

- Simplified Acquisition Threshold
- Request for Quotation (RFQ)
- GSA Schedule
- Purchase Card (P-Card)`
  },
  {
    slug: 'sbir-small-business-innovation-research',
    title: 'Small Business Innovation Research',
    abbreviation: 'SBIR',
    category: 'Programs',
    definition: 'A federal program that reserves a portion of agency R&D budgets for small businesses to conduct innovative research with commercial potential.',
    content: `## Overview

The Small Business Innovation Research (SBIR) program requires federal agencies with substantial R&D budgets to set aside a percentage (typically 3.2%) for small businesses. SBIR provides phased funding — Phase I for feasibility, Phase II for development, Phase III for commercialization — to advance innovative technologies with dual-use potential. Eleven agencies participate, with DoD and NIH being the largest.

## Why It Matters in GovCon

SBIR is a major pathway for small businesses to enter federal R&D contracting. Awards are non-dilutive and can fund technology development that leads to larger contracts. The program has specific eligibility rules, application processes, and topic-based solicitations. Understanding agency priorities and topic structure is key to successful applications.

## Key Details

- **Eligibility**: U.S. small business, for-profit, 500 or fewer employees (with some exceptions).
- **Phases**: Phase I (typically $50K–$250K, 6–12 months), Phase II ($750K–$2M+, 24 months), Phase III (non-SBIR funding for procurement).
- **Topics**: Agencies publish topics; applicants respond to specific topics.
- **Set-Aside**: SBIR is statutorily set aside for small businesses.
- **Data Rights**: SBIR grants extended data rights to the awardee for SBIR-developed data.

## How GovCon Data Can Help

GovCon Data helps you discover SBIR solicitations by agency and topic, track Phase I/II/III opportunities, and filter by your NAICS codes and capabilities.

## Related Terms

- Small Business Technology Transfer (STTR)
- Research and Development (R&D)
- Technology Readiness Level (TRL)
- Small Business Administration (SBA)`
  },
  {
    slug: 'sttr-small-business-technology-transfer',
    title: 'Small Business Technology Transfer',
    abbreviation: 'STTR',
    category: 'Programs',
    definition: 'A federal program that funds cooperative R&D between small businesses and research institutions, requiring substantive collaboration.',
    content: `## Overview

The Small Business Technology Transfer (STTR) program parallels SBIR but requires small businesses to partner with a nonprofit research institution (university, federally funded R&D center, etc.) for a substantial portion of the work. STTR aims to bridge the gap between academic research and commercial application by fostering collaboration. Five agencies participate: DoD, NIH, NSF, DOE, and NASA.

## Why It Matters in GovCon

STTR provides SBIR-like funding with the added requirement of research institution partnership. For small businesses with strong university relationships or access to lab facilities, STTR can be advantageous. The collaboration structure and eligibility differ from SBIR, so understanding both programs expands your R&D opportunity set.

## Key Details

- **Partnership Requirement**: Minimum 40% of Phase I and Phase II work must be performed by the research institution.
- **Eligibility**: Same small business criteria as SBIR; research institution must be U.S.-based and nonprofit.
- **Agency Participation**: Fewer agencies than SBIR (DoD, HHS/NIH, NSF, DOE, NASA).
- **Set-Aside**: Statutorily reserved for small business–research institution partnerships.
- **Application**: Similar structure to SBIR but with collaboration plan and institution commitment.

## How GovCon Data Can Help

GovCon Data surfaces STTR opportunities alongside SBIR, helping you identify topics requiring research partnerships and track agency-specific STTR solicitations.

## Related Terms

- Small Business Innovation Research (SBIR)
- Research and Development (R&D)
- Small Business Administration (SBA)
- Technology Readiness Level (TRL)`
  },
  {
    slug: 'sewp',
    title: 'Solutions for Enterprise-Wide Procurement',
    abbreviation: 'SEWP',
    category: 'Contracts',
    definition: 'A NASA-administered government-wide contract vehicle for IT products and services, used by agencies across the federal government.',
    content: `## Overview

Solutions for Enterprise-Wide Procurement (SEWP) is a NASA-managed, government-wide acquisition contract (GWAC) that provides federal agencies access to IT products, solutions, and services. SEWP offers pre-competed contracts with thousands of vendors across multiple groups. Agencies can order directly through SEWP without conducting their own full competitive procurement.

## Why It Matters in GovCon

SEWP is one of the largest IT contract vehicles in the federal market. Holding a SEWP contract gives you access to orders from virtually every federal agency. SEWP's group structure organizes vendors by product/service type. Competing for SEWP orders requires understanding the vehicle's structure and staying alert to task order opportunities.

## Key Details

- **NASA Administration**: NASA's Goddard Space Flight Center manages SEWP.
- **Groups**: Vendors are grouped by capability (e.g., Group A for IT products, Group B for solutions).
- **Government-Wide**: Any federal agency can order through SEWP.
- **Pre-Competed**: Base contracts are competed; individual orders follow fair opportunity.
- **Duration**: SEWP V has a 10-year ordering period.
- **High Volume**: Billions in annual orders flow through SEWP.

## How GovCon Data Can Help

GovCon Data tracks SEWP task order solicitations and spending trends, helping SEWP holders identify ordering activity and capture new business.

## Related Terms

- Government-Wide Acquisition Contract (GWAC)
- Task Order
- NASA
- Indefinite Delivery/Indefinite Quantity (IDIQ)`
  },
  {
    slug: 'ssn-sources-sought-notice',
    title: 'Sources Sought Notice',
    abbreviation: 'SSN',
    category: 'Solicitations',
    definition: 'A market research notice where the government seeks information on capable contractors before issuing a formal solicitation.',
    content: `## Overview

A Sources Sought Notice (SSN) is a pre-solicitation market research tool the government uses to identify contractors capable of fulfilling a potential requirement. SSNs are not solicitations — they do not result in contract awards. Instead, they help agencies assess market capability, determine set-aside eligibility, and shape procurement strategy before the RFP is released.

## Why It Matters in GovCon

SSNs are early indicators of upcoming opportunities, often appearing months before the formal solicitation. Responding to SSNs positions your company in the agency's awareness and can influence whether the requirement is set aside for small business or competed full and open. Capability statements submitted in response may be referenced during source selection.

## Key Details

- **Purpose**: Market research, capability assessment, set-aside determination (Rule of Two).
- **Response Format**: Usually a capability statement; may request NAICS code feedback, past performance, or technical approach.
- **No Award**: SSN responses do not create any obligation; the government may or may not use the information.
- **Forecasting**: SSNs often signal procurement timelines (e.g., RFP in 6–12 months).
- **SAM.gov**: Posted on SAM.gov under Contract Opportunities.

## How GovCon Data Can Help

GovCon Data aggregates Sources Sought notices alongside active solicitations so you can build your pipeline early, respond with targeted capability statements, and influence set-aside decisions before the RFP drops.

## Related Terms

- Request for Information (RFI)
- Market Research
- Set-Aside
- Request for Proposal (RFP)`
  },
  {
    slug: 'ssa-source-selection-authority',
    title: 'Source Selection Authority',
    abbreviation: 'SSA',
    category: 'Acquisition',
    definition: 'The government official with the final authority to select the winning offeror in a negotiated procurement.',
    content: `## Overview

The Source Selection Authority (SSA) is the designated official who makes the final award decision in a negotiated procurement. The SSA reviews the evaluation team's findings, considers the competitive range determination and evaluation reports, and selects the contractor that best meets the government's needs. The SSA may be the same person as the PCO or a more senior official.

## Why It Matters in GovCon

The SSA's decision is the culmination of the source selection process. While the SSA relies on the evaluation team's analysis, they have discretion to make the award decision. Understanding who holds SSA authority — and the factors they must consider — helps contractors tailor proposals to decision-maker priorities. SSA decisions can be protested to GAO or the Court of Federal Claims.

## Key Details

- **Designation**: Named in the source selection plan; often the PCO or a senior acquisition official.
- **Duties**: Reviews evaluation, ensures compliance with the solicitation, selects winner, documents decision.
- **Discretion**: Must document rationale, especially when selecting other than the lowest-priced offer (best value).
- **Debriefing**: The SSA's decision and rationale are typically discussed in post-award debriefings.
- **Protest**: SSA decisions are subject to bid protest on grounds of improper evaluation or procedure.

## Related Terms

- Procuring Contracting Officer (PCO)
- Best Value
- Competitive Range
- Debriefing`
  },
  {
    slug: 'sin-special-item-number',
    title: 'Special Item Number',
    abbreviation: 'SIN',
    category: 'Contracts',
    definition: 'A categorization code used within GSA Schedules and similar vehicles to classify specific products or services under a broader contract category.',
    content: `## Overview

A Special Item Number (SIN) is a subcategory within GSA's Multiple Award Schedule and similar contract vehicles that specifies the particular products or services a vendor offers. Each large category (e.g., IT Products) contains multiple SINs for more precise classification. SINs help ordering officers find the right vendors and help contractors define their scope.

## Why It Matters in GovCon

SINs determine what you can sell under your GSA Schedule and what orders you are eligible to compete for. Adding or modifying SINs requires a contract modification. Understanding which SINs align with your capabilities and target customers ensures your schedule is properly positioned. SINs also appear in procurement data for market analysis.

## Key Details

- **GSA MAS**: The MAS has hundreds of SINs across large categories like IT, professional services, and products.
- **Scope**: Orders must fall within the scope of your awarded SINs.
- **Modification**: Adding SINs requires negotiation with GSA and a contract mod.
- **Pricing**: Each SIN may have different pricing structures and labor categories.
- **Ordering**: Agencies search and filter by SIN when placing orders.

## How GovCon Data Can Help

GovCon Data lets you search opportunities by SIN and GSA Schedule category, helping you identify relevant task orders and track which SINs are seeing the most activity.

## Related Terms

- GSA Schedule
- Multiple Award Schedule (MAS)
- Product Service Code (PSC)
- Blanket Purchase Agreement (BPA)`
  },
  {
    slug: 'sic-standard-industrial-classification',
    title: 'Standard Industrial Classification',
    abbreviation: 'SIC',
    category: 'Classification',
    definition: 'A legacy U.S. government system for classifying industries by type of economic activity, largely superseded by NAICS but still used in some contexts.',
    content: `## Overview

The Standard Industrial Classification (SIC) system was the U.S. government's primary means of classifying industries from the 1930s until it was largely replaced by NAICS in 1997. SIC codes are four-digit numeric codes. While NAICS is now the standard for federal procurement, SIC codes still appear in some legacy systems, Dun & Bradstreet profiles, and commercial databases.

## Why It Matters in GovCon

NAICS has replaced SIC for federal contracting — solicitations use NAICS, and SAM.gov uses NAICS. However, some market research tools and commercial data sources still use SIC. Understanding the SIC-to-NAICS relationship helps when migrating data or using systems that cross-reference both. SIC may also appear in subcontracting and supplier diversity reporting.

## Key Details

- **Structure**: Four digits; first two indicate major division, additional digits provide detail.
- **Supersession**: NAICS replaced SIC for federal statistical and procurement purposes.
- **Crosswalk**: Conversion tables exist between SIC and NAICS.
- **Legacy Use**: Some FPDS historical data, commercial databases, and state systems may reference SIC.
- **D&B**: Dun & Bradstreet and similar services may use SIC for business classification.

## Related Terms

- NAICS Codes
- Product Service Code (PSC)
- System for Award Management (SAM)
- Federal Procurement Data System (FPDS)`
  },
  {
    slug: 'sop-standard-operating-procedure',
    title: 'Standard Operating Procedure',
    abbreviation: 'SOP',
    category: 'Management',
    definition: 'A documented procedure that defines the steps for performing a recurring task or process, often required for quality and compliance.',
    content: `## Overview

A Standard Operating Procedure (SOP) is a written document that prescribes the specific steps, responsibilities, and standards for performing a particular task or process. SOPs ensure consistency, support training, and provide a basis for quality assurance and audits. Many government contracts require contractors to maintain SOPs for key processes.

## Why It Matters in GovCon

Solicitations frequently require contractors to have — or to develop — SOPs for areas like safety, security, quality control, and project management. Proposals may include SOPs as past performance evidence or as part of the management approach. During contract performance, SOPs support compliance with contract clauses and facilitate audits.

## Key Details

- **Content**: Purpose, scope, responsibilities, procedures, records, and revision history.
- **Contract Requirement**: Common for IT, facilities, lab work, and security-related contracts.
- **Auditability**: SOPs provide a documented basis for verifying that processes are followed.
- **Updates**: SOPs should be version-controlled and updated when processes change.
- **Training**: Personnel must be trained on applicable SOPs.

## Related Terms

- Quality Control Plan (QCP)
- Quality Assurance Surveillance Plan (QASP)
- Key Personnel
- Compliance`
  },
  {
    slug: 'sled-state-local-and-education',
    title: 'State, Local, and Education',
    abbreviation: 'SLED',
    category: 'Acquisition',
    definition: 'A collective term for state governments, local governments, and educational institutions as a distinct government contracting market segment.',
    content: `## Overview

State, Local, and Education (SLED) refers to the government contracting market beyond the federal government — state agencies, cities, counties, school districts, and universities. The SLED market represents hundreds of billions in annual procurement. While each entity has its own procurement rules, they share common characteristics that differentiate them from federal buying.

## Why It Matters in GovCon

SLED offers a vast market with different dynamics than federal — smaller individual deals but high volume, varying procurement processes, and opportunities for regional and national vendors. Many contractors pursue both federal and SLED business. Cooperative purchasing (e.g., through GSA or NASPO) allows SLED entities to leverage federal contracts.

## Key Details

- **Diversity**: 50 states, thousands of local governments, and tens of thousands of school districts and higher-ed institutions.
- **Procurement Rules**: Each state has its own procurement code; local rules vary.
- **Cooperative Purchasing**: GSA MAS, NASPO ValuePoint, and other cooperatives enable streamlined SLED buying.
- **Bid Portals**: States and localities use various systems (e.g., BidNet, Periscope, state-specific portals).
- **Set-Asides**: Many states have their own small business, DBE, and veteran preference programs.

## How GovCon Data Can Help

GovCon Data tracks SLED opportunities alongside federal solicitations, giving you a unified view of government contracting across all levels. Filter by state, entity type, and set-aside to build your SLED pipeline.

## Related Terms

- General Services Administration (GSA)
- Cooperative Purchasing
- Set-Aside
- Request for Proposal (RFP)`
  },
  {
    slug: 'soo-statement-of-objectives',
    title: 'Statement of Objectives',
    abbreviation: 'SOO',
    category: 'Solicitations',
    definition: 'A performance-based document that states the government\'s objectives and desired outcomes, leaving offerors to propose the approach.',
    content: `## Overview

A Statement of Objectives (SOO) is a performance-based requirements document that describes the government's goals and objectives without prescribing how to achieve them. Offerors use the SOO to develop their Technical Approach or Statement of Work, proposing the methods and deliverables that will meet the objectives. The SOO supports innovation and contractor-driven solutions.

## Why It Matters in GovCon

SOOs are common in performance-based acquisitions where the government wants offeror input on approach. Your proposed technical solution — derived from the SOO — becomes a key evaluation factor. Understanding how to map objectives to a compelling, compliant approach is essential. SOOs often accompany or replace traditional SOWs in PBA contracts.

## Key Details

- **Objective-Focused**: States what the government wants accomplished, not how.
- **Offeror Response**: Contractors draft a Technical Approach or SOW that addresses each objective.
- **Evaluation**: The government evaluates how well the proposed approach meets the objectives.
- **Flexibility**: Allows offerors to propose innovative methods and creative solutions.
- **PWS Alternative**: SOOs are used in performance-based contracting; the contractor's response may become the PWS.

## How GovCon Data Can Help

GovCon Data's AI proposal tools help you analyze SOO objectives and structure technical approaches that clearly demonstrate how your solution achieves each objective.

## Related Terms

- Performance Work Statement (PWS)
- Statement of Work (SOW)
- Performance-Based Acquisition (PBA)
- Request for Proposal (RFP)`
  },
  {
    slug: 'sme-subject-matter-expert',
    title: 'Subject Matter Expert',
    abbreviation: 'SME',
    category: 'Personnel',
    definition: 'An individual with specialized knowledge or expertise in a particular area, often proposed as key personnel or consulted during proposal development.',
    content: `## Overview

A Subject Matter Expert (SME) is a person with deep knowledge and experience in a specific domain — technical, functional, or regulatory. In government contracting, SMEs are proposed as key personnel, used to develop proposal content, or retained as consultants. Their expertise supports technical credibility and helps ensure proposals and performance meet complex requirements.

## Why It Matters in GovCon

SMEs strengthen proposals by providing domain authority. Evaluation teams look for evidence that proposed personnel have the right expertise. SMEs also support capture and proposal efforts — translating requirements, identifying risks, and shaping technical approaches. On contract, SMEs may be key personnel or part of the broader team.

## Key Details

- **Key Personnel**: SMEs are often proposed as named key personnel with resumes in the proposal.
- **Proposal Development**: SMEs contribute to technical volumes, past performance, and risk sections.
- **Domain Areas**: IT, cybersecurity, engineering, healthcare, logistics, finance, and regulatory compliance.
- **Availability**: Proposed SMEs must be committed and available; bait-and-switch is prohibited.
- **Certifications**: SME credentials (degrees, certifications, publications) enhance credibility.

## Related Terms

- Key Personnel
- Program Manager (PM)
- Technical Approach
- Past Performance`
  },
  {
    slug: 'ssp-system-security-plan',
    title: 'System Security Plan',
    abbreviation: 'SSP',
    category: 'Security',
    definition: 'A comprehensive document describing how an information system implements required security controls, required for NIST SP 800-171 and CMMC compliance.',
    content: `## Overview

A System Security Plan (SSP) is a detailed document that describes the security controls implemented (or planned) for an information system that processes, stores, or transmits federal information. SSPs are required for NIST SP 800-171 compliance and are central to CMMC assessments. They map each control to the contractor's implementation approach.

## Why It Matters in GovCon

SSPs are a contractual requirement for DoD contractors handling Controlled Unclassified Information (CUI) and increasingly for civilian contractors. An inadequate or outdated SSP can block contract award or trigger findings during assessments. Maintaining an accurate SSP and accompanying POA&M is essential for compliance and competitiveness.

## Key Details

- **NIST SP 800-171**: SSP must address all 110 controls across 14 families.
- **Implementation Description**: For each control, the SSP describes how it is implemented or planned.
- **POA&M Integration**: Unresolved deficiencies are documented in the POA&M with remediation plans.
- **Assessments**: CMMC and customer assessments review the SSP for accuracy and completeness.
- **Updates**: SSPs must be kept current as systems and controls change.
- **SPRS**: NIST SP 800-171 self-assessment scores are reported in the Supplier Performance Risk System.

## How GovCon Data Can Help

GovCon Data's compliance tracking helps you monitor SSP update requirements, POA&M deadlines, and assessment schedules so you stay ready for contract opportunities requiring CUI handling.

## Related Terms

- Plan of Action and Milestones (POA&M)
- NIST SP 800-171
- Cybersecurity Maturity Model Certification (CMMC)
- Controlled Unclassified Information (CUI)`
  },
  {
    slug: 'sla-service-level-agreement',
    title: 'Service Level Agreement',
    abbreviation: 'SLA',
    category: 'Contracts',
    definition: 'A contractual commitment defining the level of service the contractor will provide, including metrics, targets, and remedies for non-performance.',
    content: `## Overview

A Service Level Agreement (SLA) is a contract provision that defines measurable service standards the contractor must meet. SLAs typically specify metrics (e.g., response time, uptime), target levels, measurement methods, and consequences for failing to meet targets. They align contractor performance with government expectations and provide a basis for incentives or deductions.

## Why It Matters in GovCon

SLAs are increasingly common in IT, help desk, cloud, and professional services contracts. They translate performance requirements into concrete, measurable terms. Proposals must demonstrate your ability to meet or exceed SLA targets. During performance, SLA metrics drive quality assurance and may tie directly to fee or payment.

## Key Details

- **Metrics**: Examples include uptime (%), response time (hours), resolution time, customer satisfaction score.
- **Targets**: Specific thresholds (e.g., 99.9% uptime, 4-hour response) that define acceptable performance.
- **Measurement**: How metrics will be collected and reported.
- **Remedies**: Credits, deductions, or cure provisions when targets are missed.
- **Incentives**: Some contracts offer bonuses for exceeding SLA targets.
- **QASP Link**: SLAs often align with the Quality Assurance Surveillance Plan.

## Related Terms

- Performance-Based Incentive (PBI)
- Quality Assurance Surveillance Plan (QASP)
- Performance Work Statement (PWS)
- Contract Line Item Number (CLIN)`
  },
  {
    slug: 't-m-time-and-materials',
    title: 'Time and Materials',
    abbreviation: 'T&M',
    category: 'Contracts',
    definition: 'A contract type where the government pays for labor at fixed hourly rates and materials at cost, with a ceiling on total obligation.',
    content: `## Overview

A Time and Materials (T&M) contract compensates the contractor based on actual labor hours at specified hourly rates plus the cost of materials. A ceiling price limits the government's total obligation. T&M contracts transfer cost risk to the government because the final price depends on actual hours and materials used. They are used when scope cannot be defined sufficiently for a fixed-price contract.

## Why It Matters in GovCon

T&M contracts offer flexibility when requirements are uncertain but carry oversight burden for the government. FAR restricts T&M use — it is appropriate only when no other contract type is suitable. Proposals must include labor categories, hourly rates, and material handling. Contractors must maintain timekeeping and documentation to support billing.

## Key Details

- **Labor Rates**: Each labor category has a fixed hourly rate including burden; no separate fee on labor.
- **Materials**: Reimbursed at cost; may include a materials handling fee (typically a few percent).
- **Ceiling**: The contract specifies a ceiling price that cannot be exceeded without modification.
- **FAR 16.601**: T&M contracts require a ceiling and adequate government surveillance.
- **Restrictions**: Cannot be used when work can be defined for fixed price; inappropriate for long-term or large acquisitions.
- **Audit**: Timekeeping and materials documentation are subject to audit.

## Related Terms

- Labor Hour (LH) Contract
- Firm Fixed Price (FFP)
- Cost Plus Fixed Fee (CPFF)
- Contract Line Item Number (CLIN)`
  },
  {
    slug: 'trl-technology-readiness-level',
    title: 'Technology Readiness Level',
    abbreviation: 'TRL',
    category: 'Acquisition',
    definition: 'A scale from 1 to 9 used to assess the maturity of a technology, from basic research to proven operational use.',
    content: `## Overview

Technology Readiness Level (TRL) is a scale developed by NASA and adopted by DoD and other agencies to measure the maturity of a technology. TRL 1 represents basic research; TRL 9 means the technology has been proven in operational use. TRLs help agencies assess risk, plan acquisition strategy, and evaluate proposals for R&D and technology programs.

## Why It Matters in GovCon

R&D solicitations often specify TRL requirements — e.g., "mature from TRL 4 to TRL 6." Proposals must demonstrate where your technology sits on the scale and how you will advance it. Understanding TRLs helps you position for SBIR, STTR, and other technology-focused opportunities. TRLs also inform contract type selection and risk allocation.

## Key Details

- **Scale**: TRL 1 (basic principles) to TRL 9 (flight/proven operational).
- **TRL 4–6**: "Valley of Death" — technology validated in lab (4), relevant environment (5), demonstration (6).
- **DoD Use**: Technology Readiness Assessment (TRA) uses TRLs for major programs.
- **Proposal Impact**: R&D proposals often include TRL assessment and maturation plans.
- **Risk**: Lower TRLs imply higher technical risk and may warrant cost-reimbursement contract types.

## Related Terms

- Research and Development (R&D)
- Small Business Innovation Research (SBIR)
- Small Business Technology Transfer (STTR)
- Cost Plus Fixed Fee (CPFF)`
  },
  {
    slug: 'uei-unique-entity-identifier',
    title: 'Unique Entity Identifier',
    abbreviation: 'UEI',
    category: 'Registration',
    definition: 'The 12-character identifier assigned by SAM.gov to uniquely identify entities doing business with the federal government, replacing the DUNS number.',
    content: `## Overview

The Unique Entity Identifier (UEI) is the standard identifier for entities registered to do business with the federal government. The UEI is a 12-character alphanumeric code assigned when an entity registers in SAM.gov. It replaced the DUNS number in April 2022 as the primary government entity identifier.

## Why It Matters in GovCon

The UEI is required for SAM.gov registration, and SAM registration is mandatory for federal contractors. Your UEI appears on registrations, contract awards, and federal systems. New entities receive a UEI during SAM registration; existing entities were assigned UEIs during the transition. All federal systems now use the UEI for entity identification.

## Key Details

- **Format**: 12 characters, alphanumeric (e.g., ABC1DEF2GHIJ).
- **Assignment**: Issued by SAM.gov during entity registration; no separate application.
- **Replacement**: UEI replaced DUNS (Dun & Bradstreet) as the federal standard.
- **Universal**: Used across grants, contracts, and assistance; replaces multiple legacy IDs.
- **No Cost**: UEI assignment is free; no third-party fees.
- **Persistence**: UEI remains with the entity; does not change with address or ownership changes (in most cases).

## Related Terms

- System for Award Management (SAM)
- DUNS Number
- Entity Validation
- Registration`
  },
  {
    slug: 'usc-united-states-code',
    title: 'United States Code',
    abbreviation: 'USC',
    category: 'Regulations',
    definition: 'The official codification of federal statutory law, organized by title and section — many acquisition regulations trace to USC authority.',
    content: `## Overview

The United States Code (USC) is the official compilation of federal statutes enacted by Congress. It is organized into 54 titles, each covering a broad subject area. Title 10 (Armed Forces), Title 41 (Procurement), and Title 15 (Commerce) contain many statutes that govern federal acquisition. The FAR and agency supplements implement and elaborate on these underlying laws.

## Why It Matters in GovCon

Understanding the statutory basis for acquisition rules helps contractors interpret regulations and anticipate policy changes. Key statutes include the Competition in Contracting Act (CICA), Small Business Act, and various appropriations provisions. When the FAR cites "law" or "statute," it often references a specific USC section. Legal research in procurement often starts with USC.

## Key Details

- **Structure**: Titles (e.g., 41 USC), chapters, sections (e.g., 41 USC 3301).
- **Title 41**: Federal procurement law, including CICA and procurement integrity.
- **Title 10**: Defense-specific procurement and acquisition.
- **Title 15**: Small Business Act and SBA authorities.
- **Amendments**: Congress amends USC through new laws; the FAR Council then updates the FAR to implement changes.
- **CFR vs. USC**: CFR contains regulations (executive branch); USC contains statutes (Congress).

## Related Terms

- Federal Acquisition Regulation (FAR)
- Code of Federal Regulations (CFR)
- Competition in Contracting Act (CICA)
- Small Business Act`
  },
  {
    slug: 'uca-undefinitized-contract-action',
    title: 'Undefinitized Contract Action',
    abbreviation: 'UCA',
    category: 'Contracts',
    definition: 'A contract or modification for which the contract terms, specifications, or price are not agreed upon at the time of signing, used to allow work to begin before definitization.',
    content: `## Overview

An Undefinitized Contract Action (UCA) is a contract or contract modification where the parties have not yet agreed on one or more essential elements — typically price, quantity, or specifications — at the time of award. UCAs allow the government to authorize work to begin before definitization to meet urgent needs. The FAR and DFARS impose strict limits and procedures on UCAs.

## Why It Matters in GovCon

UCAs are used when there is an urgent need to start work but negotiating final terms would cause unacceptable delay. Contractors performing under UCAs assume risk until definitization — the government's obligation is typically limited to a ceiling. Understanding UCA rules (including fee limitations and definitization timelines) is important when negotiating urgent contracts, especially in DoD.

## Key Details

- **Definitization**: The process of negotiating and documenting the final contract terms; must occur within a specified timeframe (e.g., 180 days).
- **Ceiling**: UCAs include a not-to-exceed amount; obligation is limited until definitization.
- **Fee Limits**: FAR and DFARS limit fee/profit on the undefinitized portion to reduce contractor incentive to delay definitization.
- **DoD**: DFARS 217.74 contains specific UCA procedures for defense acquisitions.
- **Justification**: UCAs require approval and documentation of the urgent need.

## Related Terms

- Contract Modification
- Definitization
- Letter Contract
- Firm Fixed Price (FFP)`
  },
  {
    slug: 've-value-engineering',
    title: 'Value Engineering',
    abbreviation: 'VE',
    category: 'Programs',
    definition: 'A systematic approach to analyzing functions and improving value by reducing cost while maintaining or improving performance.',
    content: `## Overview

Value Engineering (VE) is a structured methodology for improving the value of a product, process, or system by analyzing its functions and finding ways to achieve the same or better performance at lower cost. Federal contracts often include VE clauses that allow — or require — contractors to propose changes that save money while meeting requirements. Contractors may share in the savings.

## Why It Matters in GovCon

VE clauses create opportunities for contractors to propose cost-saving modifications during contract performance. Approved VE changes can extend contract profitability and demonstrate innovation to the customer. Some contracts mandate VE studies at certain milestones. Understanding how to develop and submit VE proposals can differentiate high-performing contractors.

## Key Details

- **VE Clause**: FAR 52.248-1 (Value Engineering) and 52.248-2 (Value Engineering—Construction) define contractor obligations and incentive arrangements.
- **Savings Share**: Contractors typically receive a percentage of net savings from approved VE changes (often 50% for FFP contracts).
- **Process**: Functional analysis, idea generation, evaluation, and proposal to the CO.
- **Approval**: The CO must approve VE proposals before implementation.
- **Mandatory**: Some contracts require VE studies at specified contract value or milestone thresholds.

## Related Terms

- Contract Modification
- Cost Plus Fixed Fee (CPFF)
- Performance-Based Acquisition (PBA)
- Statement of Work (SOW)`
  },
  {
    slug: 'vosb-veteran-owned-small-business',
    title: 'Veteran-Owned Small Business',
    abbreviation: 'VOSB',
    category: 'Programs',
    definition: 'A small business at least 51% owned and controlled by one or more veterans, eligible for certain federal and VA set-aside programs.',
    content: `## Overview

A Veteran-Owned Small Business (VOSB) is a small business at least 51% owned and controlled by one or more veterans. VOSB status provides access to set-aside and sole-source opportunities, particularly at the Department of Veterans Affairs under the Veterans First Contracting Program. VOSB is broader than SDVOSB — it includes all veterans, not just those with a service-connected disability.

## Why It Matters in GovCon

VOSB certification opens doors at the VA and other agencies with veteran-focused goals. The VA gives VOSBs priority in its contracting hierarchy (after SDVOSBs). Federal agencies have goals for veteran-owned business participation. Certification through SBA's VetCert program validates your status for contracting purposes.

## Key Details

- **Eligibility**: 51% owned and controlled by U.S. veterans (as defined by 38 USC 101).
- **Certification**: SBA VetCert program; self-certification was phased out.
- **VA Veterans First**: The VA restricts competition to VOSBs/SDVOSBs for certain requirements.
- **Set-Aside Authority**: VA can set aside contracts for VOSBs and award sole-source contracts up to thresholds.
- **Relationship to SDVOSB**: SDVOSBs are a subset of VOSBs with additional preferences.

## How GovCon Data Can Help

GovCon Data lets you filter opportunities by VOSB and SDVOSB set-aside, with dedicated views for VA Veterans First procurements so you can build a targeted pipeline.

## Related Terms

- Service-Disabled Veteran-Owned Small Business (SDVOSB)
- Small Business Administration (SBA)
- Set-Aside
- Veterans First Contracting Program`
  },
  {
    slug: 'wbs-work-breakdown-structure',
    title: 'Work Breakdown Structure',
    abbreviation: 'WBS',
    category: 'Acquisition',
    definition: 'A hierarchical decomposition of project work into manageable components, used for planning, scheduling, pricing, and earned value management.',
    content: `## Overview

A Work Breakdown Structure (WBS) is a deliverables-oriented hierarchy that breaks project work into smaller, manageable components. Each level represents an increasingly detailed definition of work. The WBS forms the foundation for scheduling, cost estimation, resource allocation, and earned value management. It is required for many DoD and complex civilian contracts.

## Why It Matters in GovCon

WBS development is often a proposal requirement and a contract deliverable. The government may provide a contract WBS (CWBS) that offerors must use, or allow offerors to propose their own. The WBS drives the basis of estimate, labor loading, and EVM reporting. A well-constructed WBS demonstrates understanding of scope and supports accurate pricing.

## Key Details

- **Hierarchy**: Top level = project; lower levels = phases, work packages, activities.
- **100% Rule**: The WBS must capture 100% of scope; nothing falls "outside" the WBS.
- **Work Packages**: Lowest level of the WBS; discrete units of work that can be scheduled and costed.
- **Contract WBS**: Some contracts impose a CWBS; contractors map their work to it.
- **EVM**: The WBS is the framework for assigning planned value and measuring earned value.
- **MIL-STD-881**: DoD has a standard for WBS development on defense programs.

## How GovCon Data Can Help

GovCon Data's AI proposal tools help you structure WBS-aligned technical approaches and pricing that map cleanly to solicitation requirements and evaluation criteria.

## Related Terms

- Earned Value Management (EVM)
- Statement of Work (SOW)
- Contract Line Item Number (CLIN)
- Project Management Body of Knowledge (PMBOK)`
  },
  {
    slug: 'wawf-wide-area-workflow',
    title: 'Wide Area Workflow',
    abbreviation: 'WAWF',
    category: 'Agencies',
    definition: 'The Department of Defense\'s electronic system for submitting invoices and receiving payments, replacing paper invoicing for defense contracts.',
    content: `## Overview

Wide Area Workflow (WAWF) is the DoD's web-based system for electronic invoicing, receipt and acceptance, and payment processing. Contractors submit invoices through WAWF, and government personnel perform receipt and acceptance electronically. WAWF integrates with DoD financial systems to streamline payment and provide visibility into invoice status. It is mandatory for most DoD contracts.

## Why It Matters in GovCon

If you perform on DoD contracts, you will almost certainly use WAWF for invoicing. Understanding WAWF procedures — including correct document numbering, CLIN references, and acceptance workflows — ensures timely submission and payment. Late or incorrect invoices delay payment and can affect cash flow. WAWF training and system access are prerequisites for DoD contract execution.

## Key Details

- **Mandatory**: Required for DoD contracts unless a waiver applies (e.g., some commercial item purchases).
- **Functions**: Invoice submission, receipt, acceptance, and payment status.
- **Document Types**: Standard forms (e.g., DD 250, DD 1155) completed electronically.
- **Integration**: Links to contract data, funding, and payment systems.
- **eBusiness Center**: WAWF is accessed through the DoD eBusiness Center.
- **Training**: Contractors should complete WAWF training before first submission.

## Related Terms

- Contract Line Item Number (CLIN)
- Receipt and Acceptance
- Defense Finance and Accounting Service (DFAS)
- Purchase Order (PO)`
  },
  {
    slug: 'ytd-year-to-date',
    title: 'Year to Date',
    abbreviation: 'YTD',
    category: 'Finance',
    definition: 'A period of time from the start of the current fiscal or calendar year to the present, used for financial and performance reporting.',
    content: `## Overview

Year to Date (YTD) refers to the cumulative total or measure of something from the beginning of the current year (fiscal or calendar) to the present date. YTD figures are used in financial reporting, contract performance reporting, and budget tracking. Government fiscal years run October 1–September 30; contractors may report on calendar or fiscal year.

## Why It Matters in GovCon

YTD metrics appear in contract reporting, business development dashboards, and agency small business reports. Understanding YTD obligations, expenditures, and contract values helps with pipeline analysis and forecasting. Subcontracting reports and small business achievement often use YTD figures. Budget and appropriation discussions reference YTD spend.

## Key Details

- **Fiscal Year**: Federal FY runs October 1–September 30; FY 2025 began October 1, 2024.
- **Reporting**: Contractors may report YTD costs, hours, or deliverables in CPRs and status reports.
- **Budget**: Agencies track YTD obligations and outlays against appropriations.
- **Small Business**: OSDBU and SBA reports often show YTD small business awards.
- **Contract Value**: YTD contract value may refer to cumulative orders under an IDIQ.

## Related Terms

- Fiscal Year
- Contract Performance Report
- Obligation
- Budget`
  },
  {
    slug: 'zbbi-zero-baseline-budget-input',
    title: 'Zero Baseline Budget Input',
    abbreviation: 'ZBBI',
    category: 'Programs',
    definition: 'A budget justification approach that requires programs to justify all funding from zero each cycle rather than assuming continuation of prior-year levels.',
    content: `## Overview

Zero Baseline Budget Input (ZBBI) is a budget formulation approach where program managers must justify their entire requested budget from a zero base, rather than building from the previous year's appropriation. Each dollar requested must be defended as necessary. ZBBI forces periodic re-examination of program value and can shift funding between programs based on current priorities.

## Why It Matters in GovCon

ZBBI affects program stability and procurement predictability. Programs that might have received incremental funding in the past must now compete for every dollar. This can lead to reprioritization, scope changes, or contract adjustments. Understanding whether your customer's programs use ZBBI helps with risk assessment and long-term planning. ZBBI is more common in certain agencies or during budget-constrained periods.

## Key Details

- **Purpose**: Ensures programs regularly demonstrate their value; avoids automatic inflation of baseline budgets.
- **Process**: Programs submit justification for full requested amount; no assumed continuation.
- **Impact**: Can result in funding changes, program restructure, or consolidation.
- **Contractor Effect**: May affect option exercise, scope, or future task orders.
- **Agency-Specific**: Not universally applied; varies by agency and budget climate.
- **PPBE**: In DoD, ZBBI principles may influence the Planning, Programming, Budgeting, and Execution process.

## Related Terms

- Budget
- Program of Record (POR)
- Option Year
- Fiscal Year`
  },
  {
    slug: 'aco-administrative-contracting-officer',
    title: 'Administrative Contracting Officer',
    abbreviation: 'ACO',
    category: 'Security',
    definition: 'An ACO manages day-to-day administration of awarded contracts, including performance surveillance, financial controls, and security compliance.',
    content: `## Overview

The Administrative Contracting Officer (ACO) is a government official delegated authority to manage the post-award administration of contracts. While the Procuring Contracting Officer (PCO) handles the award itself, the ACO takes over ongoing responsibilities such as approving invoices, processing modifications, monitoring contractor performance, and ensuring compliance with contract terms and applicable regulations.

## Why It Matters in GovCon

Contractors interact with the ACO more frequently than any other government official during contract execution. Understanding the ACO's authority and limitations helps contractors resolve issues efficiently, submit proper requests, and maintain strong working relationships throughout the period of performance.

## Key Details

- **Delegation**: ACOs receive a formal delegation letter from the PCO specifying the scope of their administrative authority.
- **Functions**: Common duties include processing invoices, negotiating forward pricing rates, approving purchasing systems, and managing property administration.
- **Location**: ACOs are often housed at DCMA or the cognizant contract administration office (CAO).
- **Limitations**: ACOs cannot change scope, extend periods of performance, or increase contract value without PCO authorization.
- **Multiple Contracts**: A single ACO may administer dozens of contracts across multiple contractors.

## Related Terms

- Contracting Officer (CO)
- Contract Administration Office (CAO)
- Defense Contract Management Agency (DCMA)
- Procuring Contracting Officer (PCO)`
  },
  {
    slug: 'apb-acquisition-program-baseline',
    title: 'Acquisition Program Baseline',
    abbreviation: 'APB',
    category: 'Contracts',
    definition: 'A documented set of cost, schedule, and performance parameters that establishes approved objectives for a major defense acquisition program.',
    content: `## Overview

The Acquisition Program Baseline (APB) is a formal agreement between the Program Manager and the Milestone Decision Authority that documents the cost, schedule, and performance thresholds and objectives for a defense acquisition program. It serves as the benchmark against which program progress is measured and reported to Congress and senior leadership.

## Why It Matters in GovCon

APB parameters directly shape contract requirements, delivery timelines, and budget availability. Contractors working on Major Defense Acquisition Programs (MDAPs) should understand how APB thresholds affect program stability. A breach of an APB threshold can trigger a Nunn-McCurdy review, potentially resulting in program restructuring or cancellation.

## Key Details

- **Thresholds vs. Objectives**: Objectives are desired targets; thresholds are minimum acceptable values that trigger reporting if breached.
- **Nunn-McCurdy**: A cost breach of 25% or more over the current APB baseline triggers a critical Nunn-McCurdy notification to Congress.
- **Updates**: APBs are revised at each milestone decision point as programs mature and estimates are refined.
- **Content**: Covers unit cost, total acquisition cost, schedule milestones, and key performance parameters (KPPs).
- **Oversight**: The Under Secretary of Defense for Acquisition and Sustainment (USD(A&S)) approves APBs for major programs.

## Related Terms

- Major Defense Acquisition Program (MDAP)
- Milestone Decision Authority (MDA)
- Program of Record (POR)
- Key Performance Parameter (KPP)`
  },
  {
    slug: 'ato-authority-to-operate',
    title: 'Authority to Operate',
    abbreviation: 'ATO',
    category: 'Security',
    definition: 'The official authorization granted by a designated approving authority allowing an information system to operate in a specific environment.',
    content: `## Overview

An Authority to Operate (ATO) is a formal declaration by an Authorizing Official that a system meets security requirements and the residual risk is acceptable. Governed by the Risk Management Framework (RMF) and NIST SP 800-37, the ATO process involves security categorization, control selection and implementation, assessment, and continuous monitoring.

## Why It Matters in GovCon

IT contractors delivering systems to federal agencies cannot deploy those systems into production without an ATO. The ATO process often takes months and can delay contract deliverables, making it critical for contractors to plan for security documentation, testing, and remediation early in the development lifecycle.

## Key Details

- **Duration**: ATOs are typically granted for three years, after which reauthorization is required.
- **Types**: Full ATO, Interim ATO (IATO) for limited-duration use, and Authorization to Test (ATT) for evaluation environments.
- **Requirements**: A complete security package including System Security Plan (SSP), Security Assessment Report (SAR), and Plan of Action and Milestones (POA&M).
- **FedRAMP**: Cloud services require a FedRAMP ATO, which can be leveraged across agencies.
- **Continuous Monitoring**: Modern approaches emphasize ongoing authorization rather than point-in-time assessments.

## Related Terms

- Risk Management Framework (RMF)
- FedRAMP
- System Security Plan (SSP)
- Cybersecurity Maturity Model Certification (CMMC)
- Controlled Unclassified Information (CUI)`
  },
  {
    slug: 'a-e-architectural-and-engineering-services',
    title: 'Architectural and Engineering Services',
    abbreviation: 'A&E',
    category: 'Management',
    definition: 'Professional services for design, planning, feasibility studies, and construction support, typically awarded under the Brooks Act using qualifications-based selection.',
    content: `## Overview

Architectural and Engineering (A&E) services encompass professional design, surveying, mapping, construction management, and related disciplines procured by federal agencies. Under the Brooks Act (40 U.S.C. §1101-1104), agencies must select A&E firms based on demonstrated competence and qualifications rather than price competition, then negotiate a fair and reasonable fee with the most qualified firm.

## Why It Matters in GovCon

A&E procurement follows a unique qualifications-based selection (QBS) process that differs from standard competitive bidding. Firms competing for A&E work must invest heavily in their Standard Form 330 (SF 330) submissions, showcasing relevant project experience, key personnel credentials, and past performance rather than competing primarily on price.

## Key Details

- **Brooks Act**: Requires qualifications-based selection for A&E services; price is negotiated after selection, not used as an evaluation factor.
- **SF 330**: The standard form used to submit qualifications for A&E contracts.
- **Scope**: Includes architecture, engineering, surveying, mapping, environmental studies, and construction management services.
- **Agencies**: USACE, NAVFAC, GSA, and VA are major A&E procurers.
- **Small Business**: Many A&E contracts are set aside for small businesses, particularly SDVOSB at the VA.

## Related Terms

- Brooks Act
- Standard Form 330 (SF 330)
- Indefinite Delivery/Indefinite Quantity (IDIQ)
- Set-Aside`
  },
  {
    slug: 'boa-basic-ordering-agreement',
    title: 'Basic Ordering Agreement',
    abbreviation: 'BOA',
    category: 'Contracts',
    definition: 'A written instrument containing terms and clauses for future purchases that does not obligate the government until specific orders are issued.',
    content: `## Overview

A Basic Ordering Agreement (BOA) is a pre-negotiated arrangement between a contracting agency and a contractor that establishes terms, conditions, and pricing methods for future orders. Unlike a contract, a BOA itself does not obligate funds or commit the government to any purchases. It streamlines procurement by eliminating the need to negotiate standard terms each time an order is placed.

## Why It Matters in GovCon

BOAs reduce procurement lead times and administrative burden for both agencies and contractors. Contractors with BOAs in place are positioned to receive orders quickly when requirements arise, providing a competitive advantage especially for spare parts, maintenance services, and technical support where rapid response is valued.

## Key Details

- **Not a Contract**: A BOA is an agreement, not a contract. No funds are obligated until individual orders are issued against it.
- **Terms and Conditions**: Contains negotiated clauses, delivery terms, inspection criteria, and pricing arrangements applicable to future orders.
- **FAR Reference**: Governed by FAR 16.703, which outlines requirements for establishing and using BOAs.
- **Orders**: Each order placed under a BOA is a separate contract action with its own funding and delivery requirements.
- **Common Uses**: Frequently used for spare parts, repair services, and recurring supply needs.

## Related Terms

- Blanket Purchase Agreement (BPA)
- Indefinite Delivery/Indefinite Quantity (IDIQ)
- Task Order
- Federal Acquisition Regulation (FAR)`
  },
  {
    slug: 'bafo-best-and-final-offer',
    title: 'Best and Final Offer',
    abbreviation: 'BAFO',
    category: 'Solicitations',
    definition: 'The last proposal revision opportunity provided to offerors in a negotiated procurement after discussions with the government.',
    content: `## Overview

The Best and Final Offer (BAFO) is the final proposal revision submitted by offerors in a negotiated procurement after the government conducts discussions. While the FAR now uses the term "Final Proposal Revisions" (FPR), BAFO remains widely used in the industry. This is the contractor's last chance to refine pricing, technical approach, and other proposal elements before the government makes its award decision.

## Why It Matters in GovCon

BAFOs represent a critical inflection point in competitive procurements. The feedback received during discussions indicates areas where the government sees weaknesses or concerns. Contractors who effectively address these areas in their BAFO significantly improve their chances of winning. Missing the BAFO deadline or failing to adequately respond to discussion points can eliminate an otherwise strong competitor.

## Key Details

- **Terminology**: FAR Part 15 now refers to "Final Proposal Revisions" rather than BAFO, though BAFO is still commonly used.
- **Timing**: Issued after the government completes discussions with offerors in the competitive range.
- **Scope**: Offerors may revise any aspect of their proposal, not just areas discussed.
- **Deadline**: Strict submission deadlines apply; late BAFOs are typically not accepted.
- **One Round**: Generally only one round of BAFOs is requested, though agencies can request additional rounds.

## Related Terms

- Request for Proposal (RFP)
- Competitive Range
- Source Selection
- Final Proposal Revision (FPR)
- Discussions`
  },
  {
    slug: 'bid-protest',
    title: 'Bid Protest',
    category: 'Contracts',
    definition: 'A formal challenge to a contract award or procurement process, typically filed with GAO, the Court of Federal Claims, or the agency.',
    content: `## Overview

A bid protest is a legal challenge by an interested party contesting the terms of a solicitation or a contract award decision. Protests can be filed at three venues: the Government Accountability Office (GAO), the U.S. Court of Federal Claims (COFC), or directly with the contracting agency. Each venue has different procedures, timelines, and remedies available.

## Why It Matters in GovCon

Bid protests serve as a critical check on the federal procurement system, ensuring agencies follow regulations and treat offerors fairly. For contractors, understanding protest rights and procedures is essential — both to protect their own interests when they believe an award was improper and to defend against protests filed by competitors after a win.

## Key Details

- **GAO Protests**: Must be filed within 10 days of knowing the basis for protest; GAO issues a decision within 100 days. An automatic stay of performance (CICA stay) applies.
- **COFC Protests**: Filed in federal court with broader discovery rights but no automatic stay; can result in injunctive relief.
- **Agency-Level**: Filed directly with the contracting officer; fastest resolution but narrower remedies.
- **Sustain Rate**: GAO sustains approximately 12-15% of protests that reach a decision on the merits.
- **Corrective Action**: Agencies may voluntarily take corrective action before GAO issues a decision, mooting the protest.

## Related Terms

- Government Accountability Office (GAO)
- Source Selection
- Contracting Officer (CO)
- Debriefing`
  },
  {
    slug: 'bom-bill-of-materials',
    title: 'Bill of Materials',
    abbreviation: 'BOM',
    category: 'Products',
    definition: 'A comprehensive list of raw materials, components, and quantities needed to manufacture or assemble a product for a government contract.',
    content: `## Overview

A Bill of Materials (BOM) is an itemized inventory of all raw materials, parts, subassemblies, and quantities required to manufacture a finished product. In government contracting, BOMs support cost proposals, production planning, supply chain management, and compliance with domestic sourcing requirements such as the Buy American Act and Trade Agreements Act.

## Why It Matters in GovCon

Accurate BOMs are essential for cost estimating, contract compliance, and supply chain transparency. Agencies may require detailed BOMs to verify pricing reasonableness, assess domestic content percentages, and ensure contractors can sustain production throughout the contract period. Errors in BOMs can lead to cost overruns, delivery delays, and compliance violations.

## Key Details

- **Structure**: BOMs can be single-level (flat list) or multi-level (hierarchical with subassemblies), depending on product complexity.
- **Cost Proposals**: BOMs form the foundation of material cost estimates in contract proposals and are scrutinized during cost audits.
- **Domestic Content**: Required to demonstrate compliance with Buy American Act percentages for manufactured goods.
- **Configuration Management**: BOMs must be maintained and updated as engineering changes occur during production.
- **DFARS Requirements**: Defense contracts may require BOM data as part of CDRL deliverables.

## Related Terms

- Buy American Act (BAA)
- Trade Agreements Act (TAA)
- Contract Data Requirements List (CDRL)
- Cost Proposal`
  },
  {
    slug: 'baa-broad-agency-announcement',
    title: 'Broad Agency Announcement',
    abbreviation: 'BAA',
    category: 'Solicitations',
    definition: 'A general solicitation for research proposals that allows agencies to receive innovative ideas without a formal RFP structure.',
    content: `## Overview

A Broad Agency Announcement (BAA) is a competitive solicitation used by federal agencies to solicit proposals for basic and applied research. Unlike a traditional RFP with rigid specifications, a BAA describes areas of research interest and invites white papers or proposals that offer innovative approaches. Selections are based on scientific merit and relevance rather than lowest price.

## Why It Matters in GovCon

BAAs offer a pathway for small businesses, startups, and research organizations to win federal contracts based on innovation rather than incumbency or lowest cost. They are especially common at DoD research agencies like DARPA, AFRL, and ONR, and can lead to prototype development, follow-on production contracts, and long-term research partnerships.

## Key Details

- **FAR Authority**: Governed by FAR 6.102(d)(2) and FAR 35.016, BAAs are a form of full and open competition for research.
- **Open Duration**: BAAs often remain open for extended periods (sometimes years), accepting proposals on a rolling basis.
- **White Papers**: Many BAAs request an initial white paper before inviting a full proposal, saving effort for both parties.
- **Evaluation**: Proposals are evaluated by scientific review panels based on technical merit, innovation, and relevance.
- **Award Types**: BAAs can result in contracts, grants, cooperative agreements, or Other Transaction Agreements (OTAs).

## How GovCon Data Can Help

GovCon Data tracks active BAAs across federal agencies, helping researchers and innovators discover funding opportunities aligned with their capabilities before deadlines pass.

## Related Terms

- Defense Advanced Research Projects Agency (DARPA)
- Other Transaction Agreement (OTA)
- Research and Development (R&D)
- Small Business Innovation Research (SBIR)`
  },
  {
    slug: 'cage-code',
    title: 'CAGE Code',
    category: 'Registration',
    definition: 'A five-character alphanumeric identifier assigned to entities doing business with the federal government, used across solicitations and logistics systems.',
    content: `## Overview

The Commercial and Government Entity (CAGE) Code is a five-character identifier assigned by the Defense Logistics Agency (DLA) to companies, government agencies, and other organizations. CAGE codes are used throughout defense logistics, procurement, and supply chain systems to uniquely identify entities involved in government contracting activities worldwide.

## Why It Matters in GovCon

A CAGE code is required for registration in the System for Award Management (SAM.gov) and is referenced on contracts, solicitations, invoices, and shipping documents. Without a valid CAGE code, a company cannot receive federal contract awards. It is also used in FPDS, WAWF, and other government systems to track contractor activity.

## Key Details

- **Assignment**: U.S. entities receive a CAGE code automatically when registering in SAM.gov. International entities receive NATO CAGE (NCAGE) codes.
- **Format**: Five characters — U.S. codes use numbers and letters (excluding I and O); international NCAGE codes follow a different pattern.
- **Managed By**: The Defense Logistics Agency (DLA) maintains the CAGE code database.
- **Uses**: Referenced in contracts, DD Form 250s, shipping labels, technical data packages, and logistics systems.
- **Updates**: CAGE code information must be kept current in SAM.gov; expired registrations can prevent contract awards and payments.

## How GovCon Data Can Help

GovCon Data uses CAGE codes to link contractor profiles with contract award histories, helping you research competitors and potential teaming partners.

## Related Terms

- System for Award Management (SAM)
- Defense Logistics Agency (DLA)
- UEI (Unique Entity Identifier)
- DUNS Number`
  },
  {
    slug: 'cmmi-capability-maturity-model-integration',
    title: 'Capability Maturity Model Integration',
    abbreviation: 'CMMI',
    category: 'Management',
    definition: 'A process improvement framework that rates organizations from Level 1 to Level 5 on their capability in development, service delivery, and acquisition.',
    content: `## Overview

Capability Maturity Model Integration (CMMI) is a process and behavioral model developed by the CMMI Institute (now part of ISACA) that helps organizations improve performance. CMMI provides a structured framework for assessing organizational maturity across development, services, supplier management, and people domains, with ratings from Level 1 (Initial) to Level 5 (Optimizing).

## Why It Matters in GovCon

Many federal solicitations, particularly for software development and IT services, require or prefer contractors appraised at CMMI Level 3 or higher. A CMMI appraisal demonstrates that a contractor has defined, repeatable processes — reducing risk for the government and differentiating the contractor from competitors without such credentials.

## Key Details

- **Levels**: Level 1 (Initial), Level 2 (Managed), Level 3 (Defined), Level 4 (Quantitatively Managed), Level 5 (Optimizing).
- **CMMI V2.0**: The current version simplifies the model and adds performance categories including capability, performance, and sustainability.
- **Appraisals**: SCAMPI (Standard CMMI Appraisal Method for Process Improvement) is the formal assessment methodology.
- **Cost**: Achieving and maintaining CMMI certification requires significant investment in process documentation, training, and appraisal fees.
- **Solicitation Requirements**: DoD and civilian agency IT contracts commonly reference CMMI Level 3 as a minimum requirement.

## Related Terms

- ISO 9001
- Quality Management System (QMS)
- Software Development Life Cycle (SDLC)
- Cybersecurity Maturity Model Certification (CMMC)`
  },
  {
    slug: 'cio-chief-information-officer',
    title: 'Chief Information Officer',
    abbreviation: 'CIO',
    category: 'Personnel',
    definition: "The senior executive responsible for an organization's IT strategy, systems, and operations, overseeing IT investments and cybersecurity.",
    content: `## Overview

The Chief Information Officer (CIO) is the senior executive responsible for an organization's information technology strategy, infrastructure, and governance. In the federal government, agency CIOs play a critical role under FITARA (Federal IT Acquisition Reform Act) in approving IT acquisitions, managing enterprise architecture, and ensuring cybersecurity compliance across their agencies.

## Why It Matters in GovCon

Federal CIOs influence which IT investments get funded and how technology is procured. Under FITARA, agency CIOs must approve or review all IT acquisitions, giving them significant authority over contract vehicles, cloud migration strategies, and modernization initiatives. Contractors selling IT solutions benefit from understanding each agency CIO's priorities and technology roadmap.

## Key Details

- **FITARA**: Grants agency CIOs authority over IT budgets and acquisition approvals, with annual Congressional scorecards grading agency performance.
- **Federal CIO**: The U.S. Federal CIO within OMB sets government-wide IT policy and coordinates cross-agency initiatives.
- **Cybersecurity**: CIOs oversee implementation of security frameworks including FISMA, NIST, and Zero Trust Architecture mandates.
- **Cloud Strategy**: CIOs drive agency cloud migration aligned with the Cloud Smart policy.
- **CIO Council**: The Federal CIO Council coordinates IT policy across agencies and shares best practices.

## Related Terms

- Federal IT Acquisition Reform Act (FITARA)
- FedRAMP
- Authority to Operate (ATO)
- Chief Technology Officer (CTO)`
  },
  {
    slug: 'cio-sp3',
    title: 'CIO-SP3',
    category: 'Contracts',
    definition: 'A government-wide IT services GWAC administered by NITAAC, providing federal agencies access to IT solutions including health IT, cybersecurity, and cloud.',
    content: `## Overview

CIO-SP3 (Chief Information Officer – Solutions and Partners 3) is a government-wide acquisition contract (GWAC) managed by the National Institutes of Health Information Technology Acquisition and Assessment Center (NITAAC). With a ceiling of $20 billion, it provides federal agencies a streamlined vehicle for acquiring IT services and solutions across ten task areas, with strong emphasis on small business participation.

## Why It Matters in GovCon

CIO-SP3 is one of the most widely used IT GWACs in the federal government. Contractors holding CIO-SP3 positions gain access to task orders from virtually any federal agency, making it a high-value contract vehicle for IT services firms. Competition for task orders is limited to GWAC holders, reducing the competitive field significantly.

## Key Details

- **Scope**: Covers ten task areas including health IT, cybersecurity, cloud computing, software development, IT operations, and digital government.
- **Small Business**: Includes pools for small business, 8(a), SDVOSB, WOSB, and HUBZone firms alongside an unrestricted pool.
- **Ordering**: Agencies issue task orders using fair opportunity procedures; minimum orders are $150,000.
- **Successor**: CIO-SP4 is the planned follow-on vehicle with an expanded scope and higher ceiling.
- **NITAAC**: The contract is administered by NITAAC, part of NIH within the Department of Health and Human Services.

## Related Terms

- Government-Wide Acquisition Contract (GWAC)
- NITAAC
- Task Order
- Alliant 2`
  },
  {
    slug: 'cots-commercial-off-the-shelf',
    title: 'Commercial Off-The-Shelf',
    abbreviation: 'COTS',
    category: 'Contracts',
    definition: 'Products or software readily available in the commercial marketplace, sold without modification and procured under streamlined FAR Part 12 procedures.',
    content: `## Overview

Commercial Off-The-Shelf (COTS) refers to products, software, or technology that are commercially available to the general public without modification. The federal government's preference for COTS solutions — codified in the Federal Acquisition Streamlining Act (FASA) and FAR Part 12 — aims to reduce development costs, leverage market innovation, and simplify procurement by using proven commercial items rather than custom-built alternatives.

## Why It Matters in GovCon

The government's COTS-first policy means agencies are required to consider commercial solutions before developing custom ones. This creates opportunities for commercial technology companies to sell existing products to the government under simplified procedures, while also pressuring traditional defense contractors to incorporate commercial components into their solutions.

## Key Details

- **FAR Part 12**: Streamlined procedures for acquiring commercial items reduce administrative burden and procurement timelines.
- **COTS vs. GOTS**: COTS is commercially developed; Government Off-The-Shelf (GOTS) is developed by or for the government.
- **Preference**: Agencies must conduct market research and consider COTS solutions before pursuing custom development.
- **Modifications**: Minimally modified COTS products may still qualify as commercial items if modifications are customary in the marketplace.
- **Examples**: Commonly includes enterprise software (Microsoft, Oracle, ServiceNow), networking equipment, and cloud services.

## Related Terms

- Commercial Item
- FAR Part 12
- Market Research
- Government Off-The-Shelf (GOTS)`
  },
  {
    slug: 'cac-common-access-card',
    title: 'Common Access Card',
    abbreviation: 'CAC',
    category: 'Security',
    definition: 'The standard smart card identification credential for DoD personnel and eligible contractors, providing physical and logical access to facilities and networks.',
    content: `## Overview

The Common Access Card (CAC) is a credit-card-sized smart card issued by the Department of Defense to active-duty military, civilian employees, and eligible contractor personnel. It serves as the primary identification credential for physical access to installations and buildings, and for logical access to DoD computer networks, email, and secure websites through embedded PKI certificates.

## Why It Matters in GovCon

Contractors working on DoD contracts frequently require CAC cards to access military facilities, DoD networks, and secure systems. The CAC issuance process requires background investigation adjudication and sponsorship by a government organization, which can take weeks to months — a timeline that must be factored into project planning and staffing.

## Key Details

- **PKI Certificates**: Each CAC contains digital certificates for identity authentication, email encryption, and digital signatures.
- **Eligibility**: Contractors must be sponsored by a DoD organization and have a favorable background investigation (typically NACI or Tier 1).
- **DEERS**: CAC holders are registered in the Defense Enrollment Eligibility Reporting System.
- **Expiration**: CACs typically expire after three years or when the contract or affiliation ends, whichever comes first.
- **Alternatives**: Non-DoD agencies use PIV (Personal Identity Verification) cards under HSPD-12 for similar purposes.

## Related Terms

- Personal Identity Verification (PIV)
- HSPD-12
- Security Clearance
- Defense Information Systems Agency (DISA)`
  },
  {
    slug: 'conops-concept-of-operations',
    title: 'Concept of Operations',
    abbreviation: 'CONOPS',
    category: 'Acquisition',
    definition: "A document describing how a system or capability will be used from the user's perspective, defining roles, workflows, and operational scenarios.",
    content: `## Overview

A Concept of Operations (CONOPS) is a user-oriented document that describes how a proposed system or capability will function in its intended operational environment. It bridges the gap between high-level mission needs and detailed technical requirements by describing operational scenarios, user roles, workflows, and the interactions between people, processes, and technology.

## Why It Matters in GovCon

CONOPS documents shape the requirements that flow into solicitations and contracts. Contractors who understand the CONOPS can write more responsive proposals because they grasp not just what the government wants built, but how it will be used operationally. For systems integrators, the CONOPS often becomes the foundation for developing use cases and test scenarios.

## Key Details

- **Purpose**: Communicates the intended operational use of a system to stakeholders, developers, and decision-makers before detailed design begins.
- **Content**: Typically includes mission context, operational scenarios, user roles, system interfaces, constraints, and transition plans.
- **Standards**: IEEE 1362 provides a standard format for CONOPS documents.
- **Lifecycle**: Usually developed during the early acquisition phases (Material Solution Analysis or Technology Maturation).
- **Deliverable**: May be required as a CDRL in defense contracts, particularly for ACAT I/II programs.

## Related Terms

- Requirements Document
- Systems Engineering
- Contract Data Requirements List (CDRL)
- Statement of Work (SOW)`
  },
  {
    slug: 'coop-continuity-of-operations-plan',
    title: 'Continuity of Operations Plan',
    abbreviation: 'COOP',
    category: 'Management',
    definition: 'A plan ensuring that essential functions can continue during and after a disruption, addressing alternate facilities, succession, and recovery procedures.',
    content: `## Overview

A Continuity of Operations Plan (COOP) is a comprehensive plan that ensures an organization can maintain or rapidly resume its mission-essential functions during emergencies such as natural disasters, pandemics, cyberattacks, or infrastructure failures. Federal agencies are required to maintain COOP plans under Presidential Policy Directive 40 (PPD-40) and Federal Continuity Directives.

## Why It Matters in GovCon

Contractors providing mission-critical services to federal agencies may be required to develop and maintain their own COOP plans or integrate into agency COOP frameworks. Contracts for IT infrastructure, facility management, and essential support services frequently include COOP requirements as deliverables or performance standards.

## Key Details

- **Essential Functions**: COOP plans identify and prioritize the organization's mission-essential functions that must continue during disruptions.
- **Alternate Sites**: Plans designate alternate operating facilities and the resources needed to operate from them.
- **Orders of Succession**: Define who assumes authority if key leaders are unavailable.
- **Recovery Time**: Agencies must be able to perform essential functions within 12 hours of activation and sustain operations for up to 30 days.
- **Testing**: COOP plans must be tested and exercised regularly, with lessons learned incorporated into plan updates.

## Related Terms

- Disaster Recovery Plan (DRP)
- Business Continuity Plan (BCP)
- Federal Continuity Directive (FCD)
- Mission Essential Functions (MEF)`
  },
  {
    slug: 'cao-contract-administration-office',
    title: 'Contract Administration Office',
    abbreviation: 'CAO',
    category: 'Agencies',
    definition: 'The organization responsible for administering contracts on behalf of the government, performing post-award functions including quality assurance and payment oversight.',
    content: `## Overview

A Contract Administration Office (CAO) is the government organization assigned to perform post-award contract administration on behalf of the contracting agency. For defense contracts, the Defense Contract Management Agency (DCMA) typically serves as the CAO. Civilian agencies may designate their own CAOs or delegate administration to another organization. The CAO handles day-to-day contract management functions that the awarding office does not retain.

## Why It Matters in GovCon

Contractors work closely with the CAO throughout the life of a contract. The CAO's quality assurance representatives inspect deliverables, property administrators manage government-furnished equipment, and ACOs process invoices and modifications. Understanding which functions are delegated to the CAO versus retained by the procuring agency helps contractors route requests correctly.

## Key Details

- **DCMA**: The primary CAO for DoD contracts, with offices throughout the United States and internationally.
- **Functions**: Include quality assurance, property administration, payment oversight, safety surveillance, and industrial security.
- **Delegation**: A Letter of Delegation specifies which administrative functions are assigned to the CAO.
- **DCMA eTools**: Contractors interact with DCMA through systems like DCMA eTools for surveillance, shipping, and acceptance.
- **Civilian Agencies**: Agencies like NASA and DOE may operate their own contract administration organizations.

## Related Terms

- Defense Contract Management Agency (DCMA)
- Administrative Contracting Officer (ACO)
- Quality Assurance Representative (QAR)
- Contracting Officer (CO)`
  },
  {
    slug: 'cdrl-contract-data-requirements-list',
    title: 'Contract Data Requirements List',
    abbreviation: 'CDRL',
    category: 'Contracts',
    definition: 'The contract attachment specifying data deliverables, formats, and schedules, with each item referencing a Data Item Description or contractor-unique format.',
    content: `## Overview

The Contract Data Requirements List (CDRL), documented on DD Form 1423, is the standard method for specifying data deliverables in Department of Defense contracts. Each CDRL line item identifies a required deliverable, its format (typically by referencing a Data Item Description), distribution requirements, submission frequency, and due dates. CDRLs ensure both parties have a clear understanding of documentation obligations.

## Why It Matters in GovCon

CDRLs directly impact contract cost and effort. Each deliverable requires resources to produce, review, and deliver. During proposal development, contractors must carefully assess CDRL requirements to accurately estimate labor and ensure they can meet format and schedule demands. Failure to deliver CDRLs on time and in the required format can result in contract performance issues.

## Key Details

- **DD Form 1423**: The standard form used to list each data deliverable, with blocks for title, DID reference, frequency, distribution, and remarks.
- **Data Item Description (DID)**: A document that defines the content, format, and preparation instructions for each CDRL deliverable.
- **Tailoring**: Contractors and the government can negotiate tailoring of DID requirements to reduce unnecessary documentation burden.
- **Categories**: CDRLs may include technical reports, test plans, status reports, manuals, drawings, and software documentation.
- **Review Cycle**: Government reviewers typically have 30 days to review and approve or return CDRL submissions with comments.

## Related Terms

- Data Item Description (DID)
- DD Form 1423
- Statement of Work (SOW)
- Contract Deliverable`
  },
  {
    slug: 'cotr-contracting-officers-technical-representative',
    title: "Contracting Officer's Technical Representative",
    abbreviation: 'COTR',
    category: 'Agencies',
    definition: 'A government designee who provides technical oversight and accepts deliverables on behalf of the contracting officer, without authority to modify the contract.',
    content: `## Overview

The Contracting Officer's Technical Representative (COTR), also known as the Contracting Officer's Representative (COR), is a government employee appointed by the Contracting Officer to provide technical direction and monitor contractor performance. The COTR serves as the primary day-to-day point of contact between the government and the contractor, but does not have authority to make contractual changes, obligate funds, or modify terms.

## Why It Matters in GovCon

The COTR is often the contractor's most frequent government contact. Building a productive working relationship with the COTR is essential for smooth contract execution. However, contractors must understand that the COTR cannot authorize scope changes or additional work — only the Contracting Officer has that authority. Acting on unauthorized COTR direction can create cost and compliance risks.

## Key Details

- **Appointment**: COTRs are formally appointed in writing by the Contracting Officer, with specific duties and limitations defined in the appointment letter.
- **Training**: FAC-COR certification requires completion of training modules in contract management, ethics, and oversight techniques.
- **Authority Limits**: Cannot change contract scope, price, delivery schedules, or any other contract terms.
- **Responsibilities**: Monitors performance, reviews deliverables, approves invoices for technical accuracy, and documents contractor performance.
- **Terminology**: "COR" (Contracting Officer's Representative) is now the preferred term under current FAR guidance, though COTR remains widely used.

## Related Terms

- Contracting Officer (CO)
- Administrative Contracting Officer (ACO)
- Quality Assurance Surveillance Plan (QASP)
- Performance Work Statement (PWS)`
  },
  {
    slug: 'cui-controlled-unclassified-information',
    title: 'Controlled Unclassified Information',
    abbreviation: 'CUI',
    category: 'Security',
    definition: 'Information requiring safeguarding or dissemination controls pursuant to law or policy, but not classified. Contractors handling CUI must implement NIST SP 800-171.',
    content: `## Overview

Controlled Unclassified Information (CUI) is a category of government information that requires safeguarding but does not meet the threshold for classification as Confidential, Secret, or Top Secret. Established by Executive Order 13556, the CUI program standardizes how agencies mark, handle, and protect sensitive but unclassified information, replacing the patchwork of agency-specific designations like FOUO, SBU, and LES.

## Why It Matters in GovCon

Contractors handling CUI must implement the 110 security controls specified in NIST SP 800-171. With the rollout of CMMC, the ability to demonstrate CUI protection through third-party assessment is becoming a prerequisite for many DoD contracts. Non-compliance can result in contract loss, False Claims Act liability, and exclusion from future awards.

## Key Details

- **NIST SP 800-171**: The primary security standard for protecting CUI in non-federal systems, with 110 controls across 14 families.
- **CMMC**: The Cybersecurity Maturity Model Certification requires verified implementation of CUI protections for DoD contractors.
- **CUI Registry**: The National Archives maintains the CUI Registry listing all approved CUI categories and subcategories.
- **Marking**: CUI must be marked with appropriate banners and category designations on documents and emails.
- **DFARS 252.204-7012**: The DFARS clause requiring contractors to provide adequate security for CUI and report cyber incidents within 72 hours.

## Related Terms

- NIST SP 800-171
- Cybersecurity Maturity Model Certification (CMMC)
- Federal Contract Information (FCI)
- DFARS 252.204-7012`
  },
  {
    slug: 'cap-corrective-action-plan',
    title: 'Corrective Action Plan',
    abbreviation: 'CAP',
    category: 'Acquisition',
    definition: 'A formal plan to address deficiencies or audit findings, documenting root cause, corrective actions, responsible parties, and completion timelines.',
    content: `## Overview

A Corrective Action Plan (CAP) is a structured document that outlines how an organization will address identified deficiencies, non-conformances, or audit findings. CAPs are used throughout government contracting in contexts ranging from quality system failures and security vulnerabilities to contract performance deficiencies and financial audit findings. An effective CAP identifies root causes, defines specific corrective actions, assigns accountability, and sets measurable completion milestones.

## Why It Matters in GovCon

Contractors who respond to deficiency findings with well-structured CAPs demonstrate professionalism and commitment to continuous improvement. Agencies, auditors, and oversight bodies (DCAA, DCMA, OIG) expect timely and thorough CAPs when issues are identified. Failure to produce adequate corrective action can escalate to cure notices, show cause notices, or contract termination.

## Key Details

- **Root Cause Analysis**: Effective CAPs go beyond symptoms to identify underlying causes of deficiencies.
- **SMART Actions**: Corrective actions should be Specific, Measurable, Achievable, Relevant, and Time-bound.
- **Tracking**: CAPs typically include milestones, responsible parties, and status tracking mechanisms.
- **Audit Context**: DCAA and Inspector General audits frequently result in CAP requirements for financial, accounting, or compliance findings.
- **Performance**: Contracting officers may require CAPs in response to Contractor Performance Assessment Reports (CPAR) with unsatisfactory ratings.

## Related Terms

- Cure Notice
- Show Cause Notice
- Contractor Performance Assessment Reporting System (CPARS)
- Defense Contract Audit Agency (DCAA)`
  },
  {
    slug: 'cas-cost-accounting-standards',
    title: 'Cost Accounting Standards',
    abbreviation: 'CAS',
    category: 'Contracts',
    definition: 'Federal rules governing consistency and allocability of costs on covered contracts, applying to larger negotiated contracts above specified thresholds.',
    content: `## Overview

Cost Accounting Standards (CAS) are a set of 19 standards issued by the CAS Board that govern how contractors measure, assign, and allocate costs to government contracts. CAS ensures consistency in cost accounting practices across covered contracts and prevents contractors from shifting costs between government and commercial work in ways that disadvantage the government.

## Why It Matters in GovCon

CAS compliance is a major undertaking that affects a contractor's accounting system design, indirect rate structures, and disclosure practices. Contractors with CAS-covered contracts must submit a Disclosure Statement (DS-1 or DS-2) detailing their cost accounting practices, and changes to those practices can trigger cost adjustments. Non-compliance can result in contract price adjustments, withholding of payments, or termination.

## Key Details

- **Thresholds**: Full CAS coverage applies to contractors receiving $50 million or more in CAS-covered awards; modified coverage applies to individual contracts over $7.5 million.
- **19 Standards**: Cover topics including consistency of cost estimation and accumulation, allocation of home office expenses, pension costs, and depreciation.
- **Disclosure Statement**: CAS-covered contractors must file and maintain a Disclosure Statement describing their accounting practices.
- **DCAA Audits**: DCAA audits contractor CAS compliance and may recommend cost adjustments for non-compliance.
- **Exemptions**: Small businesses, sealed bid contracts, and contracts under the threshold are exempt from CAS.

## Related Terms

- Defense Contract Audit Agency (DCAA)
- Indirect Rates
- Disclosure Statement
- Allowable Cost`
  },
  {
    slug: 'cpif-cost-plus-incentive-fee',
    title: 'Cost Plus Incentive Fee',
    abbreviation: 'CPIF',
    category: 'Contracts',
    definition: 'A contract type that reimburses allowable costs and includes a fee that varies based on performance against cost and/or technical targets.',
    content: `## Overview

A Cost Plus Incentive Fee (CPIF) contract reimburses the contractor for allowable costs and pays a fee that adjusts based on the relationship of actual costs to a target cost. The fee formula includes a target fee, minimum and maximum fee limits, and a share ratio that determines how cost overruns or underruns are split between the government and contractor. This structure motivates cost control while recognizing the uncertainty inherent in complex development work.

## Why It Matters in GovCon

CPIF contracts are commonly used for development and R&D programs where costs are uncertain but the government wants to incentivize efficiency. Contractors must understand the fee adjustment mechanics — coming in under target cost increases the fee, while overruns reduce it. Strong cost management directly impacts profitability on CPIF contracts.

## Key Details

- **Share Ratio**: A typical share ratio might be 70/30 (government/contractor), meaning the contractor bears 30% of overruns and retains 30% of underruns.
- **Fee Range**: The contract specifies minimum and maximum fee percentages that cap the contractor's fee regardless of cost performance.
- **Target Cost**: The negotiated cost estimate used as the baseline for fee adjustments.
- **Multiple Incentives**: Some CPIF contracts also include incentives for schedule or technical performance in addition to cost.
- **FAR Reference**: Governed by FAR 16.405-1.

## Related Terms

- Cost Plus Fixed Fee (CPFF)
- Cost Plus Award Fee (CPAF)
- Firm Fixed Price (FFP)
- Incentive Fee`
  },
  {
    slug: 'cmmc-cybersecurity-maturity-model-certification',
    title: 'Cybersecurity Maturity Model Certification',
    abbreviation: 'CMMC',
    category: 'Security',
    definition: 'A DoD framework assessing contractor cybersecurity practices to protect CUI and FCI, replacing self-attestation with third-party assessment for many contracts.',
    content: `## Overview

The Cybersecurity Maturity Model Certification (CMMC) is a DoD program that verifies defense contractors have implemented adequate cybersecurity practices to protect Federal Contract Information (FCI) and Controlled Unclassified Information (CUI). CMMC 2.0 defines three levels: Level 1 (Foundational, 17 practices with self-assessment), Level 2 (Advanced, 110 NIST SP 800-171 controls with third-party assessment), and Level 3 (Expert, additional controls with government assessment).

## Why It Matters in GovCon

CMMC is transforming the defense industrial base by making cybersecurity a contract eligibility requirement rather than a self-attested checkbox. Contractors who fail to achieve the required CMMC level cannot bid on or perform contracts requiring that level. The investment in compliance — infrastructure, documentation, and assessment — is significant but increasingly non-negotiable for defense work.

## Key Details

- **Three Levels**: Level 1 (self-assessment for FCI), Level 2 (third-party assessment for CUI), Level 3 (government-led assessment for critical programs).
- **NIST SP 800-171**: CMMC Level 2 aligns directly with the 110 controls in NIST SP 800-171 Rev 2.
- **C3PAOs**: Certified Third-Party Assessment Organizations conduct Level 2 assessments; accredited by the Cyber AB.
- **Phased Rollout**: CMMC requirements are being phased into contracts through DFARS rulemaking starting in 2025.
- **Subcontractors**: Flow-down requirements mean subcontractors handling CUI must also achieve appropriate CMMC levels.

## How GovCon Data Can Help

GovCon Data identifies solicitations with CMMC requirements, helping contractors prioritize compliance investments based on the opportunities they want to pursue.

## Related Terms

- NIST SP 800-171
- Controlled Unclassified Information (CUI)
- Federal Contract Information (FCI)
- DFARS 252.204-7012
- Authority to Operate (ATO)`
  },
  {
    slug: 'did-data-item-description',
    title: 'Data Item Description',
    abbreviation: 'DID',
    category: 'Contracts',
    definition: 'A document defining the content, format, and delivery requirements for a specific data deliverable referenced in CDRLs.',
    content: `## Overview

A Data Item Description (DID) is a standardized document that specifies the content, format, structure, and preparation instructions for a data deliverable required under a DoD contract. DIDs are referenced by CDRL line items (DD Form 1423) and provide the detailed guidance contractors need to produce compliant deliverables such as test plans, technical manuals, status reports, and engineering drawings.

## Why It Matters in GovCon

DIDs define exactly what the government expects in each deliverable. Contractors who carefully review applicable DIDs during proposal preparation can more accurately estimate the effort required and avoid costly rework from non-compliant submissions. Tailoring DIDs — negotiating which sections are required — is a common practice that can reduce documentation burden.

## Key Details

- **ASSIST Database**: DIDs are maintained in the Acquisition Streamlining and Standardization Information System (ASSIST) and identified by DI- prefix numbers.
- **Tailoring**: Government and contractors can negotiate which DID sections apply, with tailoring documented on the CDRL form.
- **Content**: Each DID specifies document title, content outline, format requirements, and any referenced standards.
- **Contractor Format**: When no DID exists, the CDRL may specify "contractor format," giving the contractor flexibility in document structure.
- **Updates**: DIDs are periodically updated; the contract references a specific version that remains in effect for the life of the contract.

## Related Terms

- Contract Data Requirements List (CDRL)
- DD Form 1423
- MIL-STD
- Statement of Work (SOW)`
  },
  {
    slug: 'dau-defense-acquisition-university',
    title: 'Defense Acquisition University',
    abbreviation: 'DAU',
    category: 'Agencies',
    definition: 'The DoD corporate university providing training and credentialing for the acquisition workforce in contracting, program management, and engineering.',
    content: `## Overview

The Defense Acquisition University (DAU) is the DoD institution responsible for training and developing the defense acquisition workforce. DAU provides courses, certifications, and continuous learning resources across acquisition career fields including contracting, program management, systems engineering, logistics, test and evaluation, and financial management. Its curriculum directly shapes how government acquisition professionals plan, award, and manage defense contracts.

## Why It Matters in GovCon

Understanding DAU training and frameworks helps contractors speak the government's language. DAU courses define the processes, terminology, and decision frameworks that government program managers and contracting officers use daily. Contractors who understand these frameworks can write more responsive proposals and communicate more effectively with government counterparts.

## Key Details

- **Career Fields**: DAU supports certification in contracting, program management, systems engineering, logistics, T&E, business-financial management, and others.
- **DAWIA**: The Defense Acquisition Workforce Improvement Act established the certification framework that DAU supports.
- **Free Resources**: Many DAU courses, guides, and tools (like the Adaptive Acquisition Framework) are publicly available online.
- **Continuous Learning**: Acquisition professionals must earn continuous learning points to maintain their certifications.
- **AAF**: DAU maintains guidance on the Adaptive Acquisition Framework, which replaced the traditional linear acquisition model.

## Related Terms

- Defense Acquisition Workforce Improvement Act (DAWIA)
- Adaptive Acquisition Framework (AAF)
- Contracting Officer (CO)
- Program Manager (PM)`
  },
  {
    slug: 'darpa-defense-advanced-research-projects-agency',
    title: 'Defense Advanced Research Projects Agency',
    abbreviation: 'DARPA',
    category: 'Agencies',
    definition: 'The DoD agency responsible for funding breakthrough research and development, pursuing high-risk, high-reward projects with universities, startups, and contractors.',
    content: `## Overview

The Defense Advanced Research Projects Agency (DARPA) is the DoD agency charged with developing breakthrough technologies for national security. Since its founding in 1958, DARPA has been responsible for pioneering innovations including the internet (ARPANET), GPS, stealth technology, and mRNA vaccine platforms. DARPA operates with a flat organizational structure, empowering program managers to take calculated risks on transformative research.

## Why It Matters in GovCon

DARPA contracts represent some of the most innovative and technically challenging work in government contracting. Unlike traditional defense procurement, DARPA actively seeks non-traditional contractors, startups, and academic institutions. DARPA programs often use Broad Agency Announcements (BAAs) and Other Transaction Agreements (OTAs) rather than traditional FAR-based contracts, lowering barriers to entry.

## Key Details

- **Budget**: Approximately $4 billion annually, invested across roughly 250 programs in six technical offices.
- **Program Managers**: DARPA PMs serve limited terms (typically 3-5 years) and have significant autonomy in defining and executing programs.
- **BAAs**: Primary solicitation mechanism; proposals are evaluated on technical innovation and potential impact rather than lowest cost.
- **OTAs**: Frequently uses Other Transaction Agreements to engage non-traditional defense contractors without full FAR compliance requirements.
- **Technology Transition**: Successful DARPA programs transition to military services or agencies for further development and fielding.

## Related Terms

- Broad Agency Announcement (BAA)
- Other Transaction Agreement (OTA)
- Research and Development (R&D)
- Small Business Innovation Research (SBIR)`
  },
  {
    slug: 'dcma-defense-contract-management-agency',
    title: 'Defense Contract Management Agency',
    abbreviation: 'DCMA',
    category: 'Agencies',
    definition: 'The DoD component responsible for contract administration services including oversight, quality assurance, payment oversight, and industrial security.',
    content: `## Overview

The Defense Contract Management Agency (DCMA) is the DoD agency that provides contract administration services for defense and federal contracts. With offices at major contractor facilities and regional locations worldwide, DCMA performs quality assurance, delivery surveillance, payment oversight, property administration, and industrial safety functions on behalf of military services and other contracting agencies.

## Why It Matters in GovCon

DCMA is a daily presence at many defense contractor facilities. Their quality assurance representatives (QARs) inspect products, their ACOs manage contract administration, and their auditors coordinate with DCAA on financial matters. Contractors must maintain productive relationships with DCMA personnel and understand their oversight processes to avoid performance issues and ensure timely payments.

## Key Details

- **Presence**: DCMA has approximately 12,000 employees at over 700 locations, including embedded teams at major contractor plants.
- **Functions**: Covers 17 functional areas including quality assurance, engineering, pricing, delivery, property, safety, and software.
- **eTools**: Contractors interact with DCMA through eTools for acceptance, receiving reports, and contract administration data.
- **CPARS Input**: DCMA provides input to Contractor Performance Assessment Reports based on their direct oversight of contractor performance.
- **International**: DCMA International manages contract administration for foreign military sales (FMS) and NATO contracts.

## Related Terms

- Contract Administration Office (CAO)
- Administrative Contracting Officer (ACO)
- Quality Assurance Representative (QAR)
- Defense Contract Audit Agency (DCAA)`
  },
  {
    slug: 'dfas-defense-finance-and-accounting-service',
    title: 'Defense Finance and Accounting Service',
    abbreviation: 'DFAS',
    category: 'Agencies',
    definition: 'The agency providing finance and accounting services for DoD, including contract payment processing through WAWF and other invoicing systems.',
    content: `## Overview

The Defense Finance and Accounting Service (DFAS) is the DoD agency responsible for all finance and accounting operations across the Department of Defense. For government contractors, DFAS processes contract payments, manages progress payment requests, handles final contract closeout settlements, and operates the invoicing systems (primarily WAWF/iRAPT) through which contractors submit invoices and receive payment.

## Why It Matters in GovCon

DFAS is where the money flows. Contractors must submit properly formatted invoices through DFAS systems to receive payment. Understanding DFAS payment processes, timelines, and requirements is critical for cash flow management. Payment delays often trace back to invoicing errors, missing documentation, or mismatches between invoice data and contract records in DFAS systems.

## Key Details

- **WAWF/iRAPT**: Wide Area Workflow (now iRAPT) is the primary electronic invoicing and receiving system for DoD contracts.
- **Payment Timelines**: The Prompt Payment Act requires DoD to pay proper invoices within 30 days; interest accrues on late payments.
- **Progress Payments**: DFAS processes progress payment requests for large contracts, typically at 80% of incurred costs for large businesses and 85% for small businesses.
- **Final Payment**: Contract closeout and final payment processing can take months to years depending on audit requirements.
- **Locations**: DFAS operates from major centers in Columbus, Ohio; Indianapolis, Indiana; and Cleveland, Ohio.

## Related Terms

- Wide Area Workflow (WAWF)
- Prompt Payment Act
- Progress Payments
- Invoice Processing`
  },
  {
    slug: 'dib-defense-industrial-base',
    title: 'Defense Industrial Base',
    abbreviation: 'DIB',
    category: 'Regulations',
    definition: 'The industrial capabilities and supply chains supporting national defense, including prime contractors, subcontractors, and suppliers producing defense equipment and services.',
    content: `## Overview

The Defense Industrial Base (DIB) encompasses the worldwide industrial complex that enables research, development, production, and sustainment of military weapons systems, subsystems, and components. It includes government and private sector facilities, companies of all sizes from major primes to small machine shops, and the workforce and supply chains that support them. The health of the DIB is considered a national security imperative.

## Why It Matters in GovCon

Every defense contractor is part of the DIB. Government policies aimed at strengthening the DIB — including domestic sourcing requirements, industrial base assessments, and cybersecurity mandates like CMMC — directly affect how contractors operate. Understanding DIB initiatives helps contractors anticipate policy changes, identify growth opportunities, and position themselves as critical contributors to national security.

## Key Details

- **Scope**: Includes over 100,000 companies across all tiers of the defense supply chain, from prime contractors to raw material suppliers.
- **Fragility**: DoD regularly assesses the DIB for single points of failure, sole-source dependencies, and foreign ownership risks.
- **Cybersecurity**: DIB companies are required to implement NIST SP 800-171 and will need CMMC certification to protect the defense supply chain.
- **DIB Cybersecurity Program**: A voluntary threat information sharing program between DoD and cleared defense contractors.
- **Industrial Base Policy**: The Office of the Under Secretary of Defense for Acquisition and Sustainment oversees DIB health and resilience initiatives.

## Related Terms

- Cybersecurity Maturity Model Certification (CMMC)
- Defense Production Act (DPA)
- Supply Chain Risk Management (SCRM)
- National Security`
  },
  {
    slug: 'disa-defense-information-systems-agency',
    title: 'Defense Information Systems Agency',
    abbreviation: 'DISA',
    category: 'Agencies',
    definition: 'The DoD agency providing IT and communications infrastructure for national security users, operating networks, data centers, and enterprise services.',
    content: `## Overview

The Defense Information Systems Agency (DISA) is the DoD combat support agency that provides, operates, and defends the IT and communications infrastructure for the Department of Defense and national security community. DISA manages the Defense Information Systems Network (DISN), Joint Regional Security Stacks (JRSS), milCloud, and enterprise services used across all military services and combatant commands.

## Why It Matters in GovCon

DISA is a major buyer of IT services, cybersecurity solutions, cloud infrastructure, and telecommunications. Contractors supporting DISA work on some of the most critical and secure networks in the world. DISA also sets security standards (through the Security Technical Implementation Guides, or STIGs) that affect how contractors configure and harden systems across all DoD contracts.

## Key Details

- **STIGs**: DISA publishes Security Technical Implementation Guides that define security configuration standards for DoD systems.
- **milCloud**: DISA operates DoD's cloud hosting environment for Impact Level 4 and 5 workloads.
- **ENCORE III**: A major DISA IDIQ contract vehicle for IT solutions and services.
- **Joint Warfighter**: DISA provides command, control, and communications infrastructure to combatant commands worldwide.
- **Cybersecurity**: DISA operates the DoD Cyber Security Service Provider (CSSP) program and defends DoD networks.

## Related Terms

- Security Technical Implementation Guide (STIG)
- Department of Defense (DoD)
- FedRAMP
- Impact Level`
  },
  {
    slug: 'dla-defense-logistics-agency',
    title: 'Defense Logistics Agency',
    abbreviation: 'DLA',
    category: 'Agencies',
    definition: 'A DoD combat support agency that procures and distributes supplies and services for the military, managing supply chains for fuel, parts, medical supplies, and more.',
    content: `## Overview

The Defense Logistics Agency (DLA) is the DoD combat support agency responsible for sourcing and distributing supplies and services to the military services and other federal agencies. DLA manages global supply chains for fuel, food, clothing, medical supplies, construction materials, spare parts, and industrial hardware. With an annual budget exceeding $40 billion, DLA is one of the largest purchasing organizations in the federal government.

## Why It Matters in GovCon

DLA represents a massive market opportunity for suppliers of all sizes. DLA actively uses long-term contracts, reverse auctions, and the FedMall/DLA Internet Bid Board System (DIBBS) to procure millions of supply items. Small businesses win a significant share of DLA contracts, particularly for spare parts, clothing, and subsistence items.

## Key Details

- **Major Commodities**: DLA manages supply chains across energy (DLA Energy), troop support (food, clothing, medical), land and maritime (spare parts), and aviation parts.
- **DIBBS**: The DLA Internet Bid Board System is the primary platform for soliciting quotes on supply items.
- **FedMall**: DLA's online marketplace where military and federal customers order supplies.
- **Volume**: DLA processes approximately 100,000 requisitions per day and manages over 5 million stock items.
- **Small Business**: DLA consistently exceeds small business contracting goals, making it an attractive agency for small suppliers.

## How GovCon Data Can Help

GovCon Data tracks DLA solicitations and award patterns, helping suppliers identify recurring procurement opportunities and price their offerings competitively.

## Related Terms

- CAGE Code
- Federal Supply Schedule (FSS)
- National Stock Number (NSN)
- Department of Defense (DoD)`
  },
  {
    slug: 'dpa-defense-production-act',
    title: 'Defense Production Act',
    abbreviation: 'DPA',
    category: 'Security',
    definition: 'A law authorizing the President to require preferential acceptance of defense contracts, allocate materials, and expand industrial capacity for national defense.',
    content: `## Overview

The Defense Production Act (DPA) of 1950 is a federal law that grants the President broad authorities to ensure the timely availability of critical materials and industrial capacity for national defense. The DPA allows the government to prioritize defense contracts over commercial orders (Title I), provide financial incentives to expand production capacity (Title III), and coordinate voluntary agreements with industry (Title VII).

## Why It Matters in GovCon

DPA authorities can directly affect contractors in multiple ways. A DPA-rated order (DO or DX rating) requires contractors to prioritize that order over unrated commercial work. During supply chain disruptions, DPA Title III investments can create new production capacity that reshapes competitive dynamics. Understanding DPA ratings is essential for manufacturers and suppliers who receive rated orders.

## Key Details

- **Priority Ratings**: DO (priority) and DX (highest priority) ratings require contractors to accept and prioritize rated orders ahead of unrated work.
- **Title I**: Authorizes the President to require preferential acceptance and performance of contracts supporting national defense.
- **Title III**: Provides loans, loan guarantees, and purchase commitments to expand domestic production capacity for critical materials.
- **DPAS**: The Defense Priorities and Allocations System (DPAS) implements DPA Title I through regulations in 15 CFR 700.
- **Broad Definition**: "National defense" under the DPA includes military, energy, homeland security, and critical infrastructure needs.

## Related Terms

- Defense Industrial Base (DIB)
- Defense Priorities and Allocations System (DPAS)
- National Security
- Supply Chain`
  },
  {
    slug: 'dod-department-of-defense',
    title: 'Department of Defense',
    abbreviation: 'DoD',
    category: 'Agencies',
    definition: 'The federal department responsible for the military and national security, and the largest federal contracting agency by spending.',
    content: `## Overview

The Department of Defense (DoD) is the federal executive department responsible for coordinating and supervising all functions related to national security and the United States Armed Forces. Headquartered at the Pentagon, DoD encompasses the Army, Navy, Air Force, Marine Corps, Space Force, and numerous defense agencies. With annual contract spending exceeding $400 billion, DoD is by far the largest buyer in the federal marketplace.

## Why It Matters in GovCon

DoD contracts represent the largest segment of the federal contracting market. The department's acquisition system — governed by DFARS, the Adaptive Acquisition Framework, and extensive oversight mechanisms — sets the standard for complexity in government procurement. Contractors serving DoD must navigate unique requirements including security clearances, CMMC, ITAR, and specialized cost accounting standards.

## Key Details

- **Spending**: DoD accounts for roughly two-thirds of all federal contract spending, exceeding $400 billion annually.
- **DFARS**: The Defense Federal Acquisition Regulation Supplement adds DoD-specific requirements on top of the FAR.
- **Military Services**: Each service (Army, Navy, Air Force, Space Force, Marines) has its own acquisition organizations and contracting offices.
- **Defense Agencies**: DARPA, DISA, DLA, DCMA, DCAA, MDA, and others perform specialized functions across the enterprise.
- **Small Business**: DoD has aggressive small business contracting goals and utilizes mentor-protégé, set-asides, and subcontracting requirements.

## How GovCon Data Can Help

GovCon Data provides comprehensive tracking of DoD solicitations across all services and agencies, with AI-powered matching to help contractors find relevant opportunities.

## Related Terms

- Defense Federal Acquisition Regulation Supplement (DFARS)
- Federal Acquisition Regulation (FAR)
- Defense Contract Management Agency (DCMA)
- Defense Advanced Research Projects Agency (DARPA)`
  },
  {
    slug: 'dhs-department-of-homeland-security',
    title: 'Department of Homeland Security',
    abbreviation: 'DHS',
    category: 'Agencies',
    definition: 'The federal department securing the nation from threats including terrorism, natural disasters, and cyber attacks, procuring technology and services for its mission.',
    content: `## Overview

The Department of Homeland Security (DHS) is the federal department responsible for counterterrorism, border security, immigration enforcement, cybersecurity, disaster resilience, and transportation security. Established in 2002 following the September 11 attacks, DHS consolidated 22 agencies including FEMA, CBP, ICE, TSA, USCIS, CISA, and the Coast Guard. DHS is the third-largest federal department by contract spending.

## Why It Matters in GovCon

DHS is a major market for IT, cybersecurity, professional services, construction, and technology contractors. Its component agencies each have distinct missions and procurement patterns. CISA drives cybersecurity requirements across the federal government, CBP procures border technology and surveillance systems, and FEMA manages disaster response contracts that can scale rapidly during emergencies.

## Key Details

- **Components**: Major contracting components include CBP, ICE, TSA, FEMA, CISA, USCG, and USCIS.
- **EAGLE II / PACTS III**: DHS uses large IDIQ vehicles for IT services and professional services.
- **CISA**: The Cybersecurity and Infrastructure Security Agency drives federal cybersecurity policy and manages CDM and EINSTEIN programs.
- **Small Business**: DHS has strong small business utilization goals, with many contracts set aside across components.
- **Emergency Contracting**: FEMA uses emergency contracting authorities during disasters, creating rapid procurement opportunities.

## Related Terms

- Cybersecurity and Infrastructure Security Agency (CISA)
- Federal Emergency Management Agency (FEMA)
- Department of Defense (DoD)
- Federal Acquisition Regulation (FAR)`
  },
  {
    slug: 'hhs-department-of-health-and-human-services',
    title: 'Department of Health and Human Services',
    abbreviation: 'HHS',
    category: 'Agencies',
    definition: 'The federal department responsible for public health, healthcare programs, and biomedical research, including NIH, CDC, FDA, and CMS.',
    content: `## Overview

The Department of Health and Human Services (HHS) is the federal department responsible for protecting public health and providing essential human services. HHS encompasses major agencies including the National Institutes of Health (NIH), Centers for Disease Control and Prevention (CDC), Food and Drug Administration (FDA), Centers for Medicare & Medicaid Services (CMS), and the Administration for Children and Families (ACF). HHS is one of the largest federal departments by total budget.

## Why It Matters in GovCon

HHS and its operating divisions are major procurers of IT systems, biomedical research, public health services, and healthcare technology. NIH alone awards billions in contracts and grants for research. The CIO-SP3 GWAC (administered by NIH's NITAAC) is one of the most widely used IT contract vehicles in government. Contractors in health IT, life sciences, and data analytics find significant opportunities across HHS.

## Key Details

- **NIH**: The world's largest funder of biomedical research, awarding contracts and grants across 27 institutes and centers.
- **NITAAC**: NIH's IT acquisition center administers GWACs including CIO-SP3 and CIO-SP4.
- **CDC**: Procures public health surveillance systems, laboratory services, and emergency preparedness capabilities.
- **FDA**: Contracts for regulatory science, IT modernization, and medical product review support.
- **CMS**: Manages Medicare and Medicaid IT systems, including Healthcare.gov infrastructure.

## Related Terms

- CIO-SP3
- NITAAC
- Government-Wide Acquisition Contract (GWAC)
- National Institutes of Health (NIH)`
  },
  {
    slug: 'va-department-of-veterans-affairs',
    title: 'Department of Veterans Affairs',
    abbreviation: 'VA',
    category: 'Agencies',
    definition: 'The department providing healthcare, benefits, and memorial services to veterans, with strong preferences for SDVOSB in many acquisitions.',
    content: `## Overview

The Department of Veterans Affairs (VA) is the federal department responsible for providing healthcare, disability compensation, education benefits, home loan guarantees, and memorial services to military veterans and their families. The VA operates the nation's largest integrated healthcare system with over 1,300 facilities and is the second-largest federal department by budget.

## Why It Matters in GovCon

The VA is a major contracting opportunity, particularly for Service-Disabled Veteran-Owned Small Businesses (SDVOSBs) and Veteran-Owned Small Businesses (VOSBs). Under the Veterans First Contracting Program, the VA gives priority to verified SDVOSBs and VOSBs through set-asides and sole-source awards. The VA also procures extensively through the Federal Supply Schedule and its own T4NG and other IDIQ vehicles.

## Key Details

- **Veterans First**: The VA's mandatory set-aside hierarchy prioritizes SDVOSBs and VOSBs before considering other small business categories.
- **VetCert**: The SBA's Veterans Small Business Certification (VetCert) program verifies SDVOSB and VOSB status for VA contracting.
- **Healthcare IT**: The VA's electronic health record modernization (Oracle Health/Cerner) represents one of the largest federal IT programs.
- **Construction**: VA construction projects for medical centers and national cemeteries create A&E and construction contracting opportunities.
- **FSS**: The VA's Federal Supply Schedule program is widely used for medical equipment, pharmaceuticals, and IT products.

## How GovCon Data Can Help

GovCon Data highlights VA opportunities with SDVOSB and VOSB set-asides, helping veteran-owned businesses find contracts tailored to their eligibility.

## Related Terms

- Service-Disabled Veteran-Owned Small Business (SDVOSB)
- Veteran-Owned Small Business (VOSB)
- Veterans First Contracting Program
- Federal Supply Schedule (FSS)`
  },
  {
    slug: 'dbe-disadvantaged-business-enterprise',
    title: 'Disadvantaged Business Enterprise',
    abbreviation: 'DBE',
    category: 'Programs',
    definition: 'A small business owned by socially and economically disadvantaged individuals, primarily used in DOT-assisted contracts for transit, highways, and aviation.',
    content: `## Overview

The Disadvantaged Business Enterprise (DBE) program is a U.S. Department of Transportation (DOT) initiative ensuring that small businesses owned by socially and economically disadvantaged individuals have equitable access to federally assisted transportation contracts. Unlike the SBA's 8(a) program, the DBE program is specifically tied to DOT-funded projects in highways, transit, and airports, with certification managed by state and local transportation agencies.

## Why It Matters in GovCon

For contractors in transportation infrastructure, the DBE program creates both requirements and opportunities. Prime contractors on DOT-assisted projects must typically meet DBE subcontracting goals, creating demand for certified DBE firms. DBE certification opens doors to subcontracting relationships with major primes on highway, transit, and aviation projects funded by federal transportation dollars.

## Key Details

- **Certification**: Managed by state DOTs and transit agencies through the Unified Certification Program (UCP); not the same as SBA 8(a) certification.
- **Eligibility**: Business must be at least 51% owned by socially and economically disadvantaged individuals with a personal net worth below $1.32 million.
- **Goals**: Recipients of DOT funds set annual DBE participation goals; prime contractors must demonstrate good faith efforts to meet subcontracting goals.
- **Sectors**: Applies to FHWA highway construction, FTA transit projects, and FAA airport improvement programs.
- **49 CFR Part 26**: The federal regulation governing the DBE program.

## Related Terms

- 8(a) Business Development Program
- Small Disadvantaged Business (SDB)
- Department of Transportation (DOT)
- Subcontracting Plan`
  },
  {
    slug: 'dba-doing-business-as',
    title: 'Doing Business As',
    abbreviation: 'DBA',
    category: 'Business',
    definition: 'A trade name under which a business operates that differs from its legal name, which must be registered and may need to be disclosed in SAM.gov.',
    content: `## Overview

"Doing Business As" (DBA) refers to a trade name or fictitious business name under which a company conducts business that differs from its legal entity name. In government contracting, DBA names must be properly registered with state and local authorities and disclosed in System for Award Management (SAM.gov) registration. The legal entity name — not the DBA — is typically used on contracts, though the DBA may appear in marketing and correspondence.

## Why It Matters in GovCon

Proper DBA disclosure is important for government contracting compliance. SAM.gov registration requires both the legal entity name and any DBA names. Inconsistencies between the legal name, DBA, and names used on proposals or invoices can cause payment delays, registration issues, and questions about organizational identity during responsibility determinations.

## Key Details

- **Registration**: DBA names must be registered with the appropriate state or county authority where the business operates.
- **SAM.gov**: Both the legal entity name and DBA name(s) should be included in the SAM.gov entity registration.
- **Contracts**: Government contracts are typically issued under the legal entity name, not the DBA.
- **Proposals**: Offerors should use their legal name on proposal cover pages and ensure consistency with SAM.gov records.
- **Name Changes**: Changes to legal names or DBAs must be updated in SAM.gov and may require contract modifications.

## Related Terms

- System for Award Management (SAM)
- UEI (Unique Entity Identifier)
- CAGE Code
- Entity Registration`
  },
  {
    slug: 'drfp-draft-request-for-proposal',
    title: 'Draft Request for Proposal',
    abbreviation: 'DRFP',
    category: 'Solicitations',
    definition: 'A pre-release version of an RFP issued for industry feedback before the final solicitation, allowing potential offerors to comment on requirements.',
    content: `## Overview

A Draft Request for Proposal (DRFP) is a preliminary version of an RFP released by a contracting agency to solicit industry feedback before issuing the final solicitation. DRFPs allow the government to refine requirements, evaluation criteria, contract structure, and terms based on input from potential offerors, reducing the risk of misaligned expectations and unnecessary protests after the final RFP is released.

## Why It Matters in GovCon

DRFPs represent a critical opportunity for contractors to shape the final solicitation. Thoughtful, well-reasoned comments on a DRFP can influence requirements, evaluation criteria, and contract structure in ways that better align with a contractor's strengths. Ignoring DRFPs means accepting whatever the government decides without input — a missed strategic opportunity in competitive procurements.

## Key Details

- **Purpose**: Allows the government to receive industry feedback on draft requirements, terms, and evaluation criteria before finalizing the solicitation.
- **Comments**: Offerors submit written comments identifying concerns, ambiguities, or suggestions; the government is not obligated to accept any comments.
- **Timeline**: DRFPs are typically issued weeks to months before the final RFP, with a defined comment period (often 15-30 days).
- **No Commitment**: Responding to a DRFP does not obligate a company to submit a proposal on the final RFP.
- **Industry Days**: DRFPs are often accompanied by industry days or pre-solicitation conferences for additional dialogue.

## How GovCon Data Can Help

GovCon Data tracks DRFPs alongside final solicitations, alerting contractors to upcoming opportunities early enough to provide meaningful input and begin capture planning.

## Related Terms

- Request for Proposal (RFP)
- Request for Information (RFI)
- Sources Sought Notice
- Pre-Solicitation Conference`
  },
  {
    slug: 'cra-contract-risk-assessment',
    title: 'Contract Risk Assessment',
    abbreviation: 'CRA',
    category: 'Contracts',
    definition: 'A process for identifying and evaluating potential risks in a contract or procurement action to inform mitigation strategies.',
    content: `## Overview

Contract Risk Assessment (CRA) is a structured process agencies and contractors use to identify, evaluate, and prioritize risks associated with a procurement action. The assessment considers technical complexity, cost uncertainty, schedule pressure, vendor capability, and regulatory compliance to produce a risk profile that guides decision-making.

## Why It Matters in GovCon

Understanding the risk landscape of a contract helps both the government and offerors price work appropriately and allocate resources where failures are most likely. Contractors who demonstrate mature risk management processes in their proposals often score higher on evaluation criteria and build stronger agency relationships.

## Key Details

- **Risk Categories**: Common categories include technical, schedule, cost, programmatic, and supply chain risk.
- **Risk Matrix**: Risks are typically rated on a probability-impact matrix (low, moderate, high).
- **Mitigation Plans**: Each identified risk should have an associated mitigation strategy with clear ownership.
- **Continuous Process**: CRA is not a one-time event—it should be revisited throughout contract performance.
- **FAR Reference**: FAR Part 7.105 requires risk considerations in acquisition plans.

## Related Terms

- Acquisition Plan
- Earned Value Management (EVM)
- Performance Work Statement (PWS)
- Quality Assurance Surveillance Plan (QASP)`
  },
  {
    slug: 'cba-cost-benefit-analysis',
    title: 'Cost Benefit Analysis',
    abbreviation: 'CBA',
    category: 'Finance',
    definition: 'A systematic approach comparing costs and benefits of a decision, project, or procurement to determine the optimal course of action.',
    content: `## Overview

Cost Benefit Analysis (CBA) is a decision-support tool used across government to evaluate whether the anticipated benefits of a procurement, program, or policy justify its projected costs. Analysts quantify both tangible and intangible factors, discount future values, and present findings to leadership for informed resource allocation.

## Why It Matters in GovCon

Federal agencies rely on CBAs to justify funding requests and select between competing solutions. Contractors who understand CBA methodology can frame their proposals in terms of return on investment, total cost of ownership, and value delivered—language that resonates with acquisition professionals.

## Key Details

- **OMB Circular A-94**: Provides federal guidelines for conducting benefit-cost and cost-effectiveness analysis.
- **Net Present Value**: Future costs and benefits are discounted to present value for fair comparison.
- **Sensitivity Analysis**: Tests how changes in key assumptions affect the outcome.
- **Alternatives Comparison**: A CBA typically evaluates multiple options, including a status-quo baseline.
- **Non-Monetary Benefits**: Factors like improved safety, public trust, or environmental outcomes are often included qualitatively.

## Related Terms

- Life Cycle Cost Estimate (LCCE)
- Best Value Determination
- Independent Government Cost Estimate (IGCE)
- Budget Justification`
  },
  {
    slug: 'edi-electronic-data-interchange',
    title: 'Electronic Data Interchange',
    abbreviation: 'EDI',
    category: 'Classification',
    definition: 'The computer-to-computer exchange of business documents and data in a standardized electronic format between trading partners.',
    content: `## Overview

Electronic Data Interchange (EDI) replaces paper-based business documents—such as purchase orders, invoices, and shipping notices—with standardized electronic messages exchanged directly between computer systems. In federal procurement, EDI streamlines supply chain operations and reduces processing time by eliminating manual data entry.

## Why It Matters in GovCon

Many DoD and civilian logistics systems require EDI-capable suppliers. Contractors participating in supply-chain-heavy contracts must integrate with government EDI standards to receive orders, submit invoices, and report shipment status efficiently.

## Key Details

- **Standards**: Common standards include ANSI X12 (used in the U.S.) and UN/EDIFACT (international).
- **WAWF**: The Wide Area Workflow system uses EDI principles for electronic invoicing and receipt in DoD contracts.
- **Transaction Sets**: Each business document type has a designated transaction set number (e.g., 850 for purchase orders).
- **VAN**: Many organizations use a Value-Added Network as an intermediary to route EDI messages securely.
- **Compliance**: Federal contracts may specify EDI requirements in DFARS or contract-specific clauses.

## Related Terms

- Wide Area Workflow (WAWF)
- Federal Supply Class (FSC)
- Invoicing and Payment
- DFARS`
  },
  {
    slug: 'eis-environmental-impact-statement',
    title: 'Environmental Impact Statement',
    abbreviation: 'EIS',
    category: 'Regulations',
    definition: 'A document required by NEPA for federal actions significantly affecting the environment, detailing impacts and alternatives.',
    content: `## Overview

An Environmental Impact Statement (EIS) is a comprehensive document mandated by the National Environmental Policy Act (NEPA) whenever a proposed federal action—such as construction, land use, or major procurement—may significantly affect the human environment. It analyzes the proposed action, reasonable alternatives, and their environmental consequences.

## Why It Matters in GovCon

Contractors supporting federal construction, infrastructure, or facilities management must understand the EIS process because it can heavily influence project timelines, scope, and compliance obligations. Environmental consulting firms frequently compete for contracts to prepare or review EIS documents on behalf of agencies.

## Key Details

- **NEPA Requirement**: Required for major federal actions significantly affecting the quality of the human environment.
- **Public Comment**: Draft EIS documents are published for public review and comment, typically for 45 days.
- **Record of Decision**: The agency issues a Record of Decision after the final EIS, stating the chosen alternative.
- **Categorical Exclusions**: Minor actions may qualify for exclusion from the full EIS process.
- **Lead Agency**: One agency takes the lead when multiple agencies are involved in the action.

## Related Terms

- National Environmental Policy Act (NEPA)
- Federal Acquisition Regulation (FAR)
- Compliance and Regulatory Requirements
- Statement of Work (SOW)`
  },
  {
    slug: 'faa-federal-aviation-administration',
    title: 'Federal Aviation Administration',
    abbreviation: 'FAA',
    category: 'Agencies',
    definition: 'An agency under DoT responsible for civil aviation safety, air traffic control, and aviation infrastructure.',
    content: `## Overview

The Federal Aviation Administration (FAA) is an agency within the Department of Transportation responsible for regulating civil aviation, managing the National Airspace System, and overseeing safety standards for aircraft, airports, and pilots. It is one of the largest contracting agencies for technology and infrastructure modernization.

## Why It Matters in GovCon

The FAA procures billions in contracts annually for air traffic control modernization, IT systems, airport infrastructure, and unmanned aircraft systems (UAS) integration. Contractors in aerospace, IT, and engineering frequently pursue FAA opportunities through vehicles like eFAST and NextGen programs.

## Key Details

- **Budget**: The FAA operates with an annual budget exceeding $18 billion, with significant portions allocated to procurement.
- **eFAST**: The FAA's primary small business contract vehicle for advisory and assistance services.
- **NextGen**: A multi-billion-dollar initiative to modernize the National Airspace System with satellite-based navigation.
- **Certifications**: Contractors may need aviation-specific certifications or clearances for FAA work.
- **Safety Focus**: All FAA procurements prioritize safety and compliance with aviation regulations.

## How GovCon Data Can Help

GovCon Data lets you filter opportunities by agency, making it simple to track FAA solicitations and identify patterns in their procurement cycles.

## Related Terms

- Department of Transportation (DoT)
- Federal Supply Schedule (FSS)
- Contract Vehicle
- NextGen`
  },
  {
    slug: 'fema-federal-emergency-management-agency',
    title: 'Federal Emergency Management Agency',
    abbreviation: 'FEMA',
    category: 'Agencies',
    definition: 'An agency under DHS coordinating disaster response, recovery, and mitigation efforts nationwide.',
    content: `## Overview

The Federal Emergency Management Agency (FEMA) operates under the Department of Homeland Security and is responsible for coordinating the federal government's response to natural and man-made disasters. FEMA manages pre-disaster mitigation grants, emergency response logistics, temporary housing, and long-term recovery programs.

## Why It Matters in GovCon

FEMA's contracting activity surges during disaster seasons, creating urgent demand for logistics, temporary housing, debris removal, communications, and IT support. Contractors positioned on FEMA vehicles or pre-qualified through SAM.gov can mobilize quickly when disasters strike.

## Key Details

- **Advance Contracts**: FEMA pre-awards contracts for commodities and services needed during disasters so they can be activated immediately.
- **FEMA Qualifications System**: Certain FEMA contracts require contractor personnel to meet specific qualification levels.
- **Disaster Relief Fund**: The primary funding source for FEMA's disaster response and recovery activities.
- **Hazard Mitigation**: FEMA also funds pre-disaster mitigation projects through grants to state and local governments.
- **Stafford Act**: The primary legislation authorizing federal disaster assistance and FEMA's operational authority.

## How GovCon Data Can Help

GovCon Data tracks FEMA solicitations and awards in real time, enabling contractors to identify advance contract opportunities and respond rapidly when disaster-related needs arise.

## Related Terms

- Department of Homeland Security (DHS)
- Emergency Procurement
- Sole-Source Contract
- Stafford Act`
  },
  {
    slug: 'fsc-federal-supply-class',
    title: 'Federal Supply Class',
    abbreviation: 'FSC',
    category: 'Products',
    definition: 'A four-digit coding system used to group similar items of supply for cataloging and procurement purposes.',
    content: `## Overview

Federal Supply Class (FSC) is a four-digit numeric code within the Federal Cataloging System used to classify items of supply into logical groups. Each FSC groups products with similar characteristics, functions, or end uses, facilitating standardized procurement, inventory management, and logistics across the federal government.

## Why It Matters in GovCon

FSC codes appear on federal solicitations, contract line items, and market research documents. Contractors who understand how their products map to FSC codes can more effectively search for relevant opportunities and ensure their offerings are properly categorized in SAM.gov and GSA Advantage.

## Key Details

- **Structure**: FSC codes are four digits within a broader Federal Supply Group (FSG), which is defined by the first two digits.
- **Coverage**: There are approximately 645 FSCs organized into 78 FSGs covering everything from weapons to office supplies.
- **Relationship to PSC**: FSC codes classify products, while Product and Service Codes (PSC) cover both products and services.
- **NATO Codification**: FSC is aligned with the NATO Codification System for interoperability with allied nations.
- **SAM.gov**: Contractors register their FSC capabilities in SAM.gov to be found during market research.

## Related Terms

- Product and Service Code (PSC)
- NAICS Code
- Federal Supply Schedule (FSS)
- SAM.gov Registration`
  },
  {
    slug: 'gfi-government-furnished-information',
    title: 'Government Furnished Information',
    abbreviation: 'GFI',
    category: 'Contracts',
    definition: 'Information provided by the government to a contractor for use in contract performance, such as technical data, specifications, or drawings.',
    content: `## Overview

Government Furnished Information (GFI) refers to any data, documentation, or information that the government provides to a contractor to support contract execution. This can include technical drawings, specifications, research data, software documentation, or operational procedures that the contractor needs but cannot independently produce.

## Why It Matters in GovCon

Timely delivery of GFI is critical to contract performance. Delays in receiving government-furnished information are among the most common causes of schedule slippage, and contractors must document GFI dependencies in their proposals and project plans to protect themselves from responsibility for resulting delays.

## Key Details

- **FAR 45.101**: Defines government-furnished property, which encompasses GFI as a subset.
- **Contract Clause**: GFI requirements are typically specified in contract sections detailing government obligations.
- **Accountability**: Contractors must safeguard GFI and return or destroy it as directed at contract completion.
- **Delay Risk**: If GFI is delivered late, contractors may be entitled to schedule relief or equitable adjustments.
- **Classification**: GFI may include controlled unclassified information (CUI) or classified data requiring special handling.

## Related Terms

- Government Furnished Equipment (GFE)
- Government Furnished Property (GFP)
- Controlled Unclassified Information (CUI)
- Equitable Adjustment`
  },
  {
    slug: 'gpo-group-purchasing-organization',
    title: 'Group Purchasing Organization',
    abbreviation: 'GPO',
    category: 'Registration',
    definition: 'An entity that leverages collective buying power of members to negotiate discounts from suppliers and manufacturers.',
    content: `## Overview

A Group Purchasing Organization (GPO) aggregates the purchasing volume of multiple organizations to negotiate better pricing, terms, and conditions from suppliers. In the public sector, GPOs serve state, local, and educational (SLED) entities by providing competitively solicited contracts that members can access without conducting their own procurements.

## Why It Matters in GovCon

GPOs represent a significant channel for SLED market access. Contractors who secure positions on GPO contracts can reach thousands of government buyers simultaneously, bypassing the need for individual competitive procurements at each agency.

## Key Details

- **Cooperative Contracts**: GPO contracts are competitively solicited and available to all member organizations.
- **Membership**: Government entities join GPOs voluntarily, typically at no cost to the buyer.
- **Revenue Model**: GPOs typically earn administrative fees from suppliers, not from member organizations.
- **Compliance**: GPO contracts must comply with state procurement laws, which vary by jurisdiction.
- **Major GPOs**: Examples include Sourcewell, OMNIA Partners, and the National Cooperative Purchasing Alliance (NCPA).

## How GovCon Data Can Help

GovCon Data includes SLED opportunities sourced through cooperative purchasing vehicles, helping contractors identify GPO-related contract opportunities alongside federal solicitations.

## Related Terms

- Cooperative Purchasing
- SLED (State, Local, and Education)
- National Cooperative Purchasing Alliance (NCPA)
- Federal Supply Schedule (FSS)`
  },
  {
    slug: 'hcats',
    title: 'HCaTS',
    category: 'Contracts',
    definition: 'Human Capital and Training Solutions, a GSA contract vehicle for customized training, human capital strategy, and organizational performance improvement.',
    content: `## Overview

HCaTS (Human Capital and Training Solutions) is a GSA-managed Best-in-Class governmentwide contract vehicle that provides agencies access to customized human capital management, training, and organizational development services. It replaced the prior STARS vehicle for training services and is available in both unrestricted and small business pools.

## Why It Matters in GovCon

HCaTS is the primary governmentwide vehicle for human capital services, covering everything from leadership development and workforce planning to learning management systems. Contractors on HCaTS benefit from a streamlined path to task order competitions across all federal agencies.

## Key Details

- **Contract Type**: Governmentwide IDIQ with both unrestricted and small business (SB) pools.
- **Scope**: Covers 14 functional areas including workforce development, coaching, organizational performance, and e-learning.
- **Ceiling**: Multi-billion-dollar ceiling with individual task order flexibility.
- **BIC Designation**: Designated as Best-in-Class by OMB, meaning agencies are encouraged to use HCaTS before creating new contracts.
- **Ordering**: Task orders are competed among HCaTS holders using fair opportunity procedures.

## Related Terms

- Best-in-Class (BIC)
- IDIQ Contract
- Task Order
- GSA Contract Vehicle`
  },
  {
    slug: 'ibr-integrated-baseline-review',
    title: 'Integrated Baseline Review',
    abbreviation: 'IBR',
    category: 'Regulations',
    definition: 'A joint government-contractor review ensuring alignment among requirements, resources, and execution plans at the start of a contract.',
    content: `## Overview

An Integrated Baseline Review (IBR) is a formal joint assessment conducted by the government and contractor early in contract execution to verify that the performance measurement baseline accurately reflects the technical scope, schedule, and budget. It ensures both parties share a common understanding of the work to be performed.

## Why It Matters in GovCon

IBRs are critical for large contracts with Earned Value Management requirements. A successful IBR establishes mutual confidence in the contractor's plan and provides a solid foundation for measuring progress. Poor baseline alignment discovered later can lead to costly re-planning and strained customer relationships.

## Key Details

- **Timing**: Typically conducted within 180 days of contract award or within 60 days of establishing the PMB.
- **Scope**: Reviews work breakdown structure, schedule logic, resource loading, risk assessments, and management processes.
- **ANSI/EIA-748**: The standard governing Earned Value Management Systems that underpins IBR requirements.
- **Risk Focus**: IBRs specifically examine where risk exists and whether mitigation plans are adequate.
- **Action Items**: Findings are documented and tracked to closure with agreed-upon corrective actions.

## Related Terms

- Earned Value Management (EVM)
- Performance Measurement Baseline (PMB)
- Work Breakdown Structure (WBS)
- Contract Data Requirements List (CDRL)`
  },
  {
    slug: 'ioc-initial-operational-capability',
    title: 'Initial Operational Capability',
    abbreviation: 'IOC',
    category: 'Products',
    definition: 'The first attainment of the capability to effectively employ a new weapon system, equipment, or platform in an operational context.',
    content: `## Overview

Initial Operational Capability (IOC) marks the milestone when a system, platform, or capability has been sufficiently developed, tested, and fielded to be employed in operational missions. It does not require full production or deployment—rather, it signifies that a minimum useful capability is in the hands of operational users.

## Why It Matters in GovCon

IOC is a critical acquisition milestone that drives contract timelines, funding decisions, and performance evaluations. Contractors supporting systems approaching IOC face intense scrutiny on delivery schedules, testing outcomes, and training readiness. Missing IOC targets can trigger program reviews and funding impacts.

## Key Details

- **DoD Milestone**: IOC typically follows Milestone C (production and deployment decision) in the Defense Acquisition Framework.
- **Criteria**: IOC is declared when trained personnel, equipment, and support are in place for minimum operational use.
- **FOC Follows**: Full Operational Capability (FOC) is achieved when the system is fully deployed and operational across all planned units.
- **Testing**: Operational Test and Evaluation (OT&E) results inform the IOC declaration.
- **Congressional Reporting**: Major programs must report IOC dates in Selected Acquisition Reports (SARs).

## Related Terms

- Full Operational Capability (FOC)
- Milestone Decision Authority (MDA)
- Operational Test and Evaluation (OT&E)
- Selected Acquisition Report (SAR)`
  },
  {
    slug: 'iv-v-independent-verification-and-validation',
    title: 'Independent Verification and Validation',
    abbreviation: 'IV&V',
    category: 'Products',
    definition: 'An independent assessment of software or systems to verify correctness and validate that requirements are met, performed by a team separate from development.',
    content: `## Overview

Independent Verification and Validation (IV&V) is a systems engineering process in which an organization separate from the development team evaluates whether a product meets its specifications (verification) and fulfills its intended purpose (validation). IV&V is widely used for mission-critical and safety-critical software in federal programs.

## Why It Matters in GovCon

Federal agencies frequently award separate IV&V contracts to ensure objective quality oversight of high-risk systems. For contractors, IV&V work represents a distinct market segment—but it also means that development contractors must build transparent, well-documented systems that can withstand independent scrutiny.

## Key Details

- **Independence**: The IV&V team must be organizationally and financially independent from the development contractor.
- **IEEE 1012**: The primary standard governing IV&V processes for software systems.
- **Risk-Based**: IV&V effort is typically scoped based on the criticality and complexity of the system.
- **Continuous**: IV&V activities run concurrently with development, not just at the end.
- **NASA and DoD**: Both agencies mandate IV&V for mission-critical and safety-critical systems.

## Related Terms

- Quality Assurance Surveillance Plan (QASP)
- Systems Engineering
- Software Development Life Cycle (SDLC)
- Technology Readiness Assessment (TRA)`
  },
  {
    slug: 'lcce-life-cycle-cost-estimate',
    title: 'Life Cycle Cost Estimate',
    abbreviation: 'LCCE',
    category: 'Finance',
    definition: 'An estimate of total costs of a system or program over its entire life cycle, from development through disposal.',
    content: `## Overview

A Life Cycle Cost Estimate (LCCE) captures the total projected cost of a program or system from initial concept and development through production, operation, sustainment, and eventual disposal. It provides decision-makers with a comprehensive financial picture that extends well beyond the initial acquisition cost.

## Why It Matters in GovCon

Agencies use LCCEs to compare alternatives, justify budgets, and make milestone decisions. Contractors who can demonstrate lower life cycle costs—not just lower acquisition prices—often gain a competitive advantage in best-value procurements where total ownership cost is an evaluation factor.

## Key Details

- **GAO Cost Guide**: The GAO Cost Estimating and Assessment Guide provides the federal standard methodology for developing LCCEs.
- **Cost Elements**: Includes research and development, procurement, operations and support, and disposal costs.
- **Inflation and Discounting**: LCCEs account for inflation using appropriate indices and may apply discount rates per OMB guidance.
- **Uncertainty Analysis**: Credible LCCEs include sensitivity and risk analysis showing the range of possible outcomes.
- **Milestone Reviews**: Updated LCCEs are required at each major acquisition milestone in the DoD 5000 framework.

## Related Terms

- Cost Benefit Analysis (CBA)
- Independent Government Cost Estimate (IGCE)
- Total Cost of Ownership
- Earned Value Management (EVM)`
  },
  {
    slug: 'loa-letter-of-agreement',
    title: 'Letter of Agreement',
    abbreviation: 'LOA',
    category: 'Acquisition',
    definition: 'A document outlining terms and conditions of an agreement between two or more parties, often used in teaming and subcontracting arrangements.',
    content: `## Overview

A Letter of Agreement (LOA) is a written document that establishes the terms, responsibilities, and expectations between two or more parties before a formal contract is executed. In government contracting, LOAs are commonly used in teaming arrangements, subcontracting relationships, and inter-agency agreements to memorialize commitments during the capture and proposal phase.

## Why It Matters in GovCon

LOAs provide a critical foundation for teaming arrangements, ensuring all parties understand their roles, work share, and financial terms before investing significant bid and proposal resources. While not always legally binding to the same degree as a contract, they establish good-faith commitments that facilitate collaboration.

## Key Details

- **Teaming Use**: LOAs define work share, pricing agreements, and exclusivity terms between prime and subcontractor partners.
- **Pre-Award**: Typically executed during capture or proposal development, before a contract is awarded.
- **Key Terms**: Usually cover scope of work, period of performance, intellectual property rights, and termination provisions.
- **Non-Binding vs. Binding**: Some LOAs are non-binding letters of intent, while others create enforceable obligations depending on language.
- **Transition**: LOAs typically transition into formal subcontracts or teaming agreements upon contract award.

## Related Terms

- Teaming Agreement
- Subcontracting Plan
- Memorandum of Understanding (MOU)
- Joint Venture`
  },
  {
    slug: 'mipr-military-interdepartmental-purchase-request',
    title: 'Military Interdepartmental Purchase Request',
    abbreviation: 'MIPR',
    category: 'Agencies',
    definition: 'A document used by DoD to transfer funds between components for procuring goods or services on behalf of another organization.',
    content: `## Overview

A Military Interdepartmental Purchase Request (MIPR) is a funding document used within the Department of Defense to transfer appropriated funds from one DoD component to another so that the receiving organization can procure goods or services on behalf of the requesting entity. MIPRs enable one agency to leverage another's contracting expertise or existing contract vehicles.

## Why It Matters in GovCon

MIPRs are the mechanism behind many cross-service procurements. A contractor may be awarded work by the Army on a Navy contract vehicle, funded through a MIPR. Understanding this funding flow helps contractors navigate multi-service opportunities and anticipate where contracting authority actually resides.

## Key Details

- **DD Form 448**: The standard form used to initiate a MIPR between DoD organizations.
- **Direct vs. Reimbursable**: MIPRs can be processed as direct citations (funds cited on the contract) or reimbursable orders.
- **Acceptance**: The receiving organization must accept the MIPR before obligating funds.
- **Interagency**: Similar mechanisms exist for transfers to non-DoD agencies under the Economy Act.
- **Tracking**: Both the requesting and performing organizations track MIPR funds separately in their financial systems.

## Related Terms

- Economy Act Orders
- Interagency Agreement
- Funding Document
- Contracting Officer`
  },
  {
    slug: 'ncpa-national-cooperative-purchasing-alliance',
    title: 'National Cooperative Purchasing Alliance',
    abbreviation: 'NCPA',
    category: 'Programs',
    definition: 'A national cooperative purchasing program that facilitates competitively solicited contracts for state and local government entities.',
    content: `## Overview

The National Cooperative Purchasing Alliance (NCPA) is a leading government purchasing cooperative that works with a lead public agency to competitively solicit and award contracts that any participating public entity can access. NCPA streamlines procurement for state, local, and educational agencies by eliminating the need for individual competitive solicitations.

## Why It Matters in GovCon

For contractors targeting the SLED market, an NCPA contract opens doors to thousands of government buyers nationwide without requiring a separate bid process at each entity. NCPA contracts carry the competitive solicitation compliance that public agencies need to satisfy their procurement regulations.

## Key Details

- **Lead Agency Model**: A single public agency conducts the competitive solicitation; the resulting contract is available to all NCPA members.
- **Membership**: Over 90,000 public agencies participate in NCPA at no membership cost.
- **Contract Scope**: NCPA contracts cover a wide range of products and services including IT, facilities, and professional services.
- **Compliance**: Contracts satisfy competitive bidding requirements in most jurisdictions.
- **Administrative Fee**: Vendors pay a small administrative fee on sales made through NCPA contracts.

## How GovCon Data Can Help

GovCon Data includes cooperative purchasing opportunities in its SLED data, helping contractors identify NCPA and similar vehicles alongside traditional federal solicitations.

## Related Terms

- Group Purchasing Organization (GPO)
- Cooperative Purchasing
- SLED (State, Local, and Education)
- Sourcewell`
  },
  {
    slug: 'navy-seaport-nxg',
    title: 'Navy SeaPort-NxG',
    category: 'Contracts',
    definition: 'The Navy next-generation contract vehicle for engineering, technical, and programmatic support services across 23 functional areas.',
    content: `## Overview

Navy SeaPort-NxG (Next Generation) is a multiple-award IDIQ contract vehicle managed by the Naval Sea Systems Command (NAVSEA) that provides engineering, technical, and programmatic support services to the Navy and Marine Corps. It replaced the original SeaPort-e vehicle and features a rolling admissions process allowing new entrants throughout its life.

## Why It Matters in GovCon

SeaPort-NxG is one of the largest Navy contract vehicles by number of task orders and total value. It is the primary mechanism for Navy commands to acquire professional support services, making it essential for contractors pursuing naval engineering and technical work.

## Key Details

- **Functional Areas**: Covers 23 functional areas including systems engineering, software development, logistics, and financial management.
- **Rolling Admissions**: Unlike most GWACs, SeaPort-NxG allows new contractors to on-ramp periodically.
- **Geographic Zones**: Work is organized across seven geographic zones, and contractors compete within their qualified zones.
- **Small Business**: Includes dedicated small business pools to meet Navy small business goals.
- **Task Order Competition**: Individual task orders are competed among qualified SeaPort-NxG holders using fair opportunity.

## How GovCon Data Can Help

GovCon Data tracks SeaPort-NxG task order solicitations, helping contractors monitor opportunities across functional areas and geographic zones relevant to their capabilities.

## Related Terms

- IDIQ Contract
- Task Order
- NAVSEA
- Contract Vehicle`
  },
  {
    slug: 'palt-procurement-administrative-lead-time',
    title: 'Procurement Administrative Lead Time',
    abbreviation: 'PALT',
    category: 'Acquisition',
    definition: 'The time between the initiation of a procurement action and the contract award, a key metric for acquisition efficiency.',
    content: `## Overview

Procurement Administrative Lead Time (PALT) measures the elapsed time from when a procurement request is initiated to when the contract is awarded. It is a key performance indicator for acquisition organizations, reflecting the efficiency of the procurement process including requirements development, solicitation, evaluation, and award.

## Why It Matters in GovCon

Understanding PALT helps contractors set realistic expectations for when awards will be made after a solicitation closes. Agencies with long PALTs may experience funding challenges or require contractors to extend proposal validity periods. PALT awareness also helps in capture planning and cash flow forecasting.

## Key Details

- **NDAA Requirement**: Section 886 of the FY2018 NDAA established PALT reporting requirements for DoD.
- **Benchmarks**: DoD targets vary by dollar threshold, with simplified acquisitions expected to award in under 90 days.
- **Phases**: PALT includes pre-solicitation, solicitation, evaluation, and award phases.
- **Tracking**: Agencies track PALT in procurement management systems like FPDS and agency-specific tools.
- **Improvement Initiatives**: Reducing PALT is a recurring priority in federal acquisition reform efforts.

## Related Terms

- Acquisition Plan
- Federal Procurement Data System (FPDS)
- Contract Award
- Solicitation`
  },
  {
    slug: 'pmb-performance-measurement-baseline',
    title: 'Performance Measurement Baseline',
    abbreviation: 'PMB',
    category: 'Programs',
    definition: 'The time-phased budget plan for accomplishing all work, serving as the baseline for earned value management measurement.',
    content: `## Overview

The Performance Measurement Baseline (PMB) is the time-phased budget plan against which contract performance is measured in an Earned Value Management System. It integrates the scope of work, schedule, and budget into a single baseline that enables objective measurement of cost and schedule variances throughout contract execution.

## Why It Matters in GovCon

The PMB is the foundation of earned value reporting on major contracts. Contractors must establish a credible PMB that withstands government scrutiny during Integrated Baseline Reviews. An unrealistic baseline undermines the value of EVM data and erodes agency confidence in program execution.

## Key Details

- **Components**: The PMB equals the sum of all work package budgets plus planning packages, distributed over time.
- **Management Reserve**: Budget held outside the PMB for unforeseen within-scope work; not included in the baseline.
- **Change Control**: Formal baseline change requests are required to modify the PMB after IBR acceptance.
- **ANSI/EIA-748**: The standard defining EVM system requirements, including baseline management.
- **Variance Thresholds**: Agencies set thresholds (typically ±10%) for cost and schedule variances that trigger reporting.

## Related Terms

- Earned Value Management (EVM)
- Integrated Baseline Review (IBR)
- Work Breakdown Structure (WBS)
- Management Reserve`
  },
  {
    slug: 'ppbe-planning-programming-budgeting-and-execution',
    title: 'Planning, Programming, Budgeting, and Execution',
    abbreviation: 'PPBE',
    category: 'Agencies',
    definition: 'The DoD resource allocation process linking strategy, programming, budgeting, and execution into an integrated decision cycle.',
    content: `## Overview

Planning, Programming, Budgeting, and Execution (PPBE) is the Department of Defense's primary resource management process. It translates national security strategy into specific programs and budgets across a multi-year planning horizon, producing the Future Years Defense Program (FYDP) and annual budget submissions to Congress.

## Why It Matters in GovCon

Understanding the PPBE cycle helps contractors anticipate when agencies will have funding for new programs, when budget decisions create or eliminate opportunities, and how long it takes for a requirement to move from concept to funded contract. Aligning business development with PPBE timelines is essential for effective capture planning.

## Key Details

- **Planning Phase**: Assesses threats and defines strategic guidance, typically spanning a 10-20 year horizon.
- **Programming Phase**: Translates strategy into specific programs and resource allocations across the FYDP (5 years).
- **Budgeting Phase**: Converts programs into detailed budget estimates for the President's Budget submission.
- **Execution Phase**: Monitors and reports on the obligation and expenditure of appropriated funds.
- **Cycle Timing**: The PPBE cycle runs annually, with each phase overlapping across different fiscal years.

## Related Terms

- Future Years Defense Program (FYDP)
- Program Objective Memorandum (POM)
- President's Budget
- Appropriations`
  },
  {
    slug: 'pss-professional-services-schedule',
    title: 'Professional Services Schedule',
    abbreviation: 'PSS',
    category: 'Contracts',
    definition: 'A GSA contract vehicle providing access to professional services such as consulting, management, and technical support at pre-negotiated rates.',
    content: `## Overview

The Professional Services Schedule (PSS) is a category within the GSA Multiple Award Schedule (MAS) program that provides federal agencies streamlined access to professional and consulting services at pre-negotiated rates. It covers a broad range of disciplines including management consulting, financial advisory, engineering, and environmental services.

## Why It Matters in GovCon

Holding a PSS contract gives service firms a direct channel to federal buyers who prefer the efficiency of ordering from an established schedule. It reduces procurement lead time for agencies and gives contractors visibility to thousands of potential customers through GSA Advantage and eBuy.

## Key Details

- **MAS Consolidation**: PSS is now part of the consolidated GSA MAS under Large Category services SINs.
- **Pre-Negotiated Rates**: Labor rates are negotiated with GSA and posted on GSA Advantage for agency comparison.
- **Ordering Procedures**: Agencies can place orders using simplified procedures for lower-value requirements or competitive quotes for larger needs.
- **Contract Period**: MAS contracts have a 20-year ordering period with ongoing terms and conditions updates.
- **Price Reductions**: Contractors must offer the government their most favored customer pricing or better.

## Related Terms

- GSA Multiple Award Schedule (MAS)
- GSA Advantage
- Special Item Number (SIN)
- Federal Supply Schedule (FSS)`
  },
  {
    slug: 'sbe-small-business-enterprise',
    title: 'Small Business Enterprise',
    abbreviation: 'SBE',
    category: 'Programs',
    definition: 'A classification for businesses meeting specific size standards, used by state and local governments for preference programs similar to SBA designations.',
    content: `## Overview

Small Business Enterprise (SBE) is a designation used primarily by state and local governments to identify businesses that meet jurisdiction-specific size standards for preferential treatment in public procurement. While conceptually similar to federal SBA small business designations, SBE criteria vary by state, county, or municipality.

## Why It Matters in GovCon

Contractors targeting the SLED market must navigate a patchwork of SBE definitions and certification processes. An SBE certification in one jurisdiction may not transfer to another, so businesses must research and apply for certifications in each market they wish to pursue.

## Key Details

- **Jurisdiction-Specific**: SBE size standards are set by individual state or local governments, not the SBA.
- **Certification**: Businesses typically must apply and provide documentation of revenue, employees, and ownership.
- **Set-Asides**: Many jurisdictions reserve a percentage of contracts for certified SBE firms.
- **Price Preferences**: Some jurisdictions offer SBE firms a percentage price preference rather than a full set-aside.
- **Reciprocity**: A few states and localities recognize SBE certifications from other jurisdictions, but this is not universal.

## How GovCon Data Can Help

GovCon Data includes SLED opportunities with set-aside designations, helping SBE-certified contractors identify relevant solicitations in their certified jurisdictions.

## Related Terms

- Small Business Administration (SBA)
- Set-Aside
- SLED (State, Local, and Education)
- Small Disadvantaged Business (SDB)`
  },
  {
    slug: 'sar-selected-acquisition-report',
    title: 'Selected Acquisition Report',
    abbreviation: 'SAR',
    category: 'Contracts',
    definition: 'A comprehensive annual report providing cost, schedule, and performance status for major defense acquisition programs submitted to Congress.',
    content: `## Overview

The Selected Acquisition Report (SAR) is a congressionally mandated annual report that provides detailed cost, schedule, and performance information on Major Defense Acquisition Programs (MDAPs). SARs give Congress and the public visibility into how well the Department of Defense is managing its largest and most expensive programs.

## Why It Matters in GovCon

SARs are a valuable source of competitive intelligence for defense contractors. They reveal program cost growth, schedule delays, and performance issues that may signal recompetition opportunities, additional work scope, or contract restructuring. Understanding SAR data helps contractors make informed pursuit decisions.

## Key Details

- **Threshold**: SARs are required for programs exceeding MDAP dollar thresholds (currently $525 million in RDT&E or $3.065 billion in procurement).
- **Frequency**: Submitted annually to Congress, with quarterly exception reports for significant changes.
- **Content**: Includes Acquisition Program Baseline data, unit cost tables, schedule milestones, and performance summaries.
- **Nunn-McCurdy**: Programs with unit cost growth exceeding 25% trigger a Nunn-McCurdy breach requiring certification to continue.
- **Public Access**: Unclassified SARs are published and available through the DoD Comptroller's website.

## Related Terms

- Major Defense Acquisition Program (MDAP)
- Nunn-McCurdy Breach
- Acquisition Program Baseline
- Defense Acquisition System`
  },
  {
    slug: 'scif-sensitive-compartmented-information-facility',
    title: 'Sensitive Compartmented Information Facility',
    abbreviation: 'SCIF',
    category: 'Security',
    definition: 'A secure room or data center designed to prevent electronic surveillance and protect sensitive compartmented information from unauthorized access.',
    content: `## Overview

A Sensitive Compartmented Information Facility (SCIF) is a physically secured area—ranging from a single room to an entire building—constructed and accredited to process, store, and discuss Sensitive Compartmented Information (SCI). SCIFs employ physical security measures, technical countermeasures, and access controls to prevent unauthorized disclosure.

## Why It Matters in GovCon

Many intelligence community and DoD contracts require work to be performed in accredited SCIFs. Contractors must either maintain their own SCIF or secure access to a government facility, which involves significant investment in construction, accreditation, and ongoing compliance. SCIF capability is a barrier to entry that can also be a competitive differentiator.

## Key Details

- **ICD 705**: Intelligence Community Directive 705 establishes the standards for SCIF construction, accreditation, and operation.
- **Accreditation**: SCIFs must be inspected and accredited by the cognizant security authority before use.
- **Physical Security**: Requirements include reinforced walls, alarmed access points, RF shielding, and sound masking.
- **Access Control**: Only personnel with appropriate SCI clearances and need-to-know may enter a SCIF.
- **Continuous Monitoring**: SCIFs require ongoing security inspections and compliance with operational procedures.

## Related Terms

- Facility Security Clearance (FCL)
- Sensitive Compartmented Information (SCI)
- Personnel Security Clearance
- Industrial Security`
  },
  {
    slug: 'scp-security-control-plan',
    title: 'Security Control Plan',
    abbreviation: 'SCP',
    category: 'Security',
    definition: 'A document outlining the security controls and safeguards implemented for a specific information system or environment.',
    content: `## Overview

A Security Control Plan (SCP) documents the specific security controls selected and implemented for an information system, including technical, operational, and management safeguards. It serves as both a planning document during system development and a compliance artifact during security assessments and authorizations.

## Why It Matters in GovCon

Contractors operating information systems on behalf of federal agencies must develop and maintain SCPs as part of the Risk Management Framework (RMF) process. A well-crafted SCP demonstrates compliance with NIST SP 800-53 controls and is essential for obtaining an Authority to Operate (ATO).

## Key Details

- **NIST SP 800-53**: Provides the catalog of security controls from which SCP selections are made.
- **Tailoring**: Controls are tailored based on the system's categorization level (low, moderate, or high impact).
- **Implementation Status**: Each control is documented as implemented, planned, partially implemented, or not applicable.
- **Continuous Monitoring**: SCPs are living documents updated as the system evolves and new threats emerge.
- **Assessment**: SCPs are evaluated during Security Control Assessments (SCAs) to verify control effectiveness.

## Related Terms

- Authority to Operate (ATO)
- Risk Management Framework (RMF)
- NIST SP 800-53
- System Security Plan (SSP)`
  },
  {
    slug: 'stig-security-technical-implementation-guide',
    title: 'Security Technical Implementation Guide',
    abbreviation: 'STIG',
    category: 'Security',
    definition: 'Configuration standards published by DISA for securing information systems and software against known vulnerabilities.',
    content: `## Overview

Security Technical Implementation Guides (STIGs) are configuration standards developed by the Defense Information Systems Agency (DISA) that define how to securely configure operating systems, applications, network devices, and other IT components. STIGs provide specific, actionable hardening guidance to reduce the attack surface of DoD information systems.

## Why It Matters in GovCon

Contractors delivering or managing IT systems for DoD must comply with applicable STIGs as a condition of obtaining an Authority to Operate. STIG compliance is verified through automated scanning tools and manual checklists, and non-compliance findings can delay system deployment or result in security waivers.

## Key Details

- **DISA Published**: DISA maintains and publishes STIGs for hundreds of products, from Windows Server to Oracle databases.
- **CAT Levels**: Findings are categorized as CAT I (high severity), CAT II (medium), or CAT III (low) based on risk.
- **SCAP**: Many STIGs have companion Security Content Automation Protocol benchmarks for automated scanning.
- **Waivers**: Organizations can request waivers for STIG requirements that cannot be met due to operational necessity.
- **Update Frequency**: STIGs are updated quarterly to address new vulnerabilities and product versions.

## Related Terms

- Defense Information Systems Agency (DISA)
- Authority to Operate (ATO)
- Risk Management Framework (RMF)
- NIST SP 800-53`
  },
  {
    slug: 'ssac-source-selection-advisory-council',
    title: 'Source Selection Advisory Council',
    abbreviation: 'SSAC',
    category: 'Contracts',
    definition: 'An advisory body that reviews evaluation results and recommends award decisions to the Source Selection Authority.',
    content: `## Overview

The Source Selection Advisory Council (SSAC) is a group of senior government officials who review the findings and recommendations of the Source Selection Evaluation Board (SSEB) and advise the Source Selection Authority (SSA) on the award decision. The SSAC provides an independent check on the evaluation process and ensures consistency with the solicitation's stated criteria.

## Why It Matters in GovCon

The SSAC's role adds a layer of oversight that can influence award decisions, particularly in competitive procurements where evaluators' ratings are close. Understanding the source selection hierarchy helps contractors appreciate who ultimately makes the award decision and how evaluation results flow through the organization.

## Key Details

- **Advisory Role**: The SSAC advises the SSA but does not make the final award decision.
- **Composition**: Typically includes senior program, technical, and contracting officials.
- **FAR 15.303**: Establishes the source selection organizational structure including the SSAC role.
- **Comparative Analysis**: The SSAC compares proposals against evaluation criteria and each other to form its recommendation.
- **Documentation**: SSAC deliberations and recommendations are documented in the source selection decision document.

## Related Terms

- Source Selection Authority (SSA)
- Source Selection Evaluation Board (SSEB)
- Best Value Determination
- Competitive Range`
  },
  {
    slug: 'stars-iii',
    title: 'STARS III',
    category: 'Contracts',
    definition: 'An 8(a) GWAC providing federal agencies with customized IT services and solutions from 8(a)-certified small businesses.',
    content: `## Overview

STARS III (8(a) Streamlined Technology Acquisition Resources for Services) is a governmentwide acquisition contract (GWAC) administered by GSA that provides federal agencies access to IT services and solutions from 8(a)-certified small businesses. It is a successor to STARS II and supports the government's small business contracting goals while delivering technology capabilities across agencies.

## Why It Matters in GovCon

STARS III is the premier IT vehicle for 8(a) firms, offering access to task order competitions across all federal agencies. For 8(a)-certified businesses, holding a STARS III position is a significant competitive advantage, providing a governmentwide platform to build past performance and revenue.

## Key Details

- **Eligibility**: Only SBA-certified 8(a) small businesses can hold STARS III contracts.
- **Scope**: Covers a comprehensive range of IT services including cloud computing, cybersecurity, software development, and IT management.
- **GWAC**: Designated as a Best-in-Class vehicle, encouraging agencies to use it for IT acquisitions.
- **Constellations**: Organized into technology-focused groupings to match contractor capabilities with agency needs.
- **Task Orders**: Individual requirements are competed among qualified STARS III holders.

## How GovCon Data Can Help

GovCon Data tracks STARS III task order opportunities, helping 8(a) firms identify and pursue relevant IT solicitations across federal agencies.

## Related Terms

- 8(a) Business Development Program
- Governmentwide Acquisition Contract (GWAC)
- Best-in-Class (BIC)
- Alliant 2`
  },
  {
    slug: 't4ng',
    title: 'T4NG',
    category: 'Contracts',
    definition: 'Transformation Twenty-One Total Technology Next Generation, a VA IDIQ contract vehicle for IT services and solutions.',
    content: `## Overview

T4NG (Transformation Twenty-One Total Technology Next Generation) is the Department of Veterans Affairs' primary IDIQ contract vehicle for acquiring IT services and solutions. It supports the VA's mission to modernize technology infrastructure, improve veteran services, and enhance healthcare delivery through information technology.

## Why It Matters in GovCon

T4NG is the gateway to VA IT work, one of the largest federal IT budgets. With the VA undergoing significant digital transformation—including electronic health record modernization—T4NG holders have access to substantial task order opportunities across the VA enterprise.

## Key Details

- **Scope**: Covers IT services including application development, cloud migration, cybersecurity, data analytics, and infrastructure management.
- **Contract Type**: Multiple-award IDIQ with both full-and-open and SDVOSB pools.
- **VA Specific**: Unlike governmentwide vehicles, T4NG is specific to the Department of Veterans Affairs.
- **Ceiling**: Multi-billion-dollar ceiling reflecting the scale of VA IT modernization needs.
- **Fair Opportunity**: Task orders are competed among T4NG holders with evaluation based on technical approach, past performance, and price.

## Related Terms

- Department of Veterans Affairs (VA)
- IDIQ Contract
- Service-Disabled Veteran-Owned Small Business (SDVOSB)
- IT Modernization`
  },
  {
    slug: 'tra-technology-readiness-assessment',
    title: 'Technology Readiness Assessment',
    abbreviation: 'TRA',
    category: 'Programs',
    definition: 'An evaluation of the maturity and risk of critical technologies in a program, used to inform acquisition milestone decisions.',
    content: `## Overview

A Technology Readiness Assessment (TRA) is a systematic evaluation of the maturity level of critical technologies within an acquisition program. Using the Technology Readiness Level (TRL) scale of 1 through 9, TRAs determine whether technologies are sufficiently mature to proceed through acquisition milestones or require additional development and risk mitigation.

## Why It Matters in GovCon

TRAs directly influence milestone decisions that determine whether programs advance to the next acquisition phase. Contractors developing or integrating new technologies must demonstrate technology maturity through testing and documentation. Programs with low TRL scores may face delays, additional funding requirements, or scope reductions.

## Key Details

- **TRL Scale**: Ranges from TRL 1 (basic principles observed) to TRL 9 (proven in operational environment).
- **DoD Policy**: DoDI 5000.02 requires TRAs at key acquisition milestones, particularly Milestone B.
- **Critical Technologies**: TRAs focus on technologies that are new, modified, or being applied in a novel way.
- **Independent Assessment**: TRAs are often conducted by independent teams to ensure objectivity.
- **GAO Reviews**: The GAO routinely evaluates technology maturity in its annual weapons systems assessments.

## Related Terms

- Technology Readiness Level (TRL)
- Milestone Decision Authority (MDA)
- Initial Operational Capability (IOC)
- Independent Verification and Validation (IV&V)`
  },
  {
    slug: 'tor-terms-of-reference',
    title: 'Terms of Reference',
    abbreviation: 'TOR',
    category: 'Management',
    definition: 'A document defining the purpose, scope, objectives, and deliverables of a project, study, or advisory engagement.',
    content: `## Overview

Terms of Reference (TOR) is a document that establishes the purpose, scope, membership, authority, and expected deliverables for a project, committee, study, or advisory engagement. In government contracting, TORs are frequently used for advisory boards, program reviews, and international development projects to define what is expected and how success will be measured.

## Why It Matters in GovCon

TORs provide the framework for many consulting, advisory, and international development engagements. Contractors responding to solicitations that reference a TOR must align their proposals precisely with its defined scope and deliverables. Misunderstanding the TOR can lead to non-responsive proposals.

## Key Details

- **Common Elements**: Includes background, objectives, scope, methodology, deliverables, timeline, and reporting requirements.
- **International Use**: TORs are extensively used by USAID, World Bank, and UN agencies for development projects.
- **Advisory Boards**: Federal advisory committees operate under TORs that define their charter and operating procedures.
- **Flexibility**: TORs can be updated as project needs evolve, with appropriate approval from stakeholders.
- **Distinction from SOW**: While similar, TORs tend to be higher-level and more outcome-focused than Statements of Work.

## Related Terms

- Statement of Work (SOW)
- Performance Work Statement (PWS)
- Statement of Objectives (SOO)
- Project Management Plan`
  },
  {
    slug: 'crada-cooperative-research-and-development-agreement',
    title: 'Cooperative Research and Development Agreement',
    abbreviation: 'CRADA',
    category: 'Agencies',
    definition: 'A partnership agreement between a government laboratory and private entity to collaborate on research and development without exchanging funds.',
    content: `## Overview

A Cooperative Research and Development Agreement (CRADA) is a partnership mechanism authorized by the Federal Technology Transfer Act that enables federal laboratories to collaborate with private companies, universities, or other organizations on research and development. Notably, CRADAs do not involve the exchange of government funds to the partner—each party contributes its own resources.

## Why It Matters in GovCon

CRADAs offer a unique path to work with government laboratories outside the traditional procurement process. Companies can access government facilities, equipment, and expertise while retaining intellectual property rights to inventions made under the agreement. Successful CRADAs can also position firms for future procurement opportunities.

## Key Details

- **No Funds Exchange**: The government does not pay the private partner; instead, both parties contribute personnel, equipment, or materials.
- **IP Protection**: Partners may negotiate exclusive or non-exclusive licenses to inventions arising from the collaboration.
- **Data Protection**: CRADA information provided by partners can be protected from public disclosure for up to five years.
- **Lab Participation**: Over 300 federal laboratories across agencies like DoD, DOE, and NIH actively participate in CRADAs.
- **Authority**: Authorized under 15 U.S.C. § 3710a as part of the Stevenson-Wydler Technology Innovation Act as amended.

## Related Terms

- Technology Transfer
- Federal Laboratory
- Other Transaction Authority (OTA)
- Small Business Innovation Research (SBIR)`
  },
  {
    slug: 'unspsc',
    title: 'UNSPSC',
    category: 'Classification',
    definition: 'United Nations Standard Products and Services Code, a global classification system used by governments and organizations to categorize products and services.',
    content: `## Overview

The United Nations Standard Products and Services Code (UNSPSC) is a hierarchical classification system that provides a standardized way to categorize products and services across industries and borders. Used by governments, international organizations, and commercial enterprises worldwide, it facilitates procurement analysis, spend management, and market research through a common taxonomy.

## Why It Matters in GovCon

While U.S. federal procurement primarily uses NAICS and PSC codes, UNSPSC appears in state and local government procurement systems and international contracting. Contractors pursuing SLED or international development opportunities must understand how their offerings map to UNSPSC codes for proper visibility in procurement databases.

## Key Details

- **Structure**: An eight-digit hierarchical code organized into Segment, Family, Class, and Commodity levels.
- **Coverage**: Over 55,000 codes covering virtually all products and services globally.
- **Managed by**: Maintained by GS1 US under license from the United Nations Development Programme (UNDP).
- **SLED Usage**: Many state and local procurement systems use UNSPSC to classify solicitations and vendor registrations.
- **Version Updates**: Updated regularly to reflect new products, services, and technologies.

## Related Terms

- NAICS Code
- Product and Service Code (PSC)
- Federal Supply Class (FSC)
- SAM.gov Registration`
  },
  {
    slug: 'usaid',
    title: 'U.S. Agency for International Development',
    abbreviation: 'USAID',
    category: 'Agencies',
    definition: 'An independent agency administering civilian foreign aid and development assistance programs worldwide.',
    content: `## Overview

The U.S. Agency for International Development (USAID) is the principal U.S. agency for administering civilian foreign aid and international development assistance. Operating in over 100 countries, USAID funds programs in global health, democracy and governance, economic growth, education, environment, and humanitarian assistance through contracts, grants, and cooperative agreements.

## Why It Matters in GovCon

USAID is one of the largest civilian contracting agencies, spending billions annually on development programs implemented by contractors and NGOs. The USAID market requires specialized expertise in international development, including field operations capability, local partner networks, and experience with USAID-specific regulations and reporting requirements.

## Key Details

- **Contract Vehicles**: Major vehicles include IDIQ contracts for specific sectors and regions, as well as broad development support contracts.
- **Grants and Cooperative Agreements**: A significant portion of USAID funding flows through grants rather than contracts.
- **AIDAR**: The USAID Acquisition Regulation supplements the FAR with agency-specific policies.
- **Country Missions**: Procurement is conducted both from Washington and by individual country missions worldwide.
- **New Partnerships Initiative**: USAID actively seeks new and non-traditional partners, including small businesses and local organizations.

## How GovCon Data Can Help

GovCon Data tracks USAID solicitations alongside other federal opportunities, enabling international development contractors to monitor new procurements and forecast upcoming requirements.

## Related Terms

- Foreign Assistance
- Grants and Cooperative Agreements
- AIDAR (USAID Acquisition Regulation)
- International Development`
  },
  {
    slug: 'wd-wage-determination',
    title: 'Wage Determination',
    abbreviation: 'WD',
    category: 'Agencies',
    definition: 'A determination of prevailing wage rates and fringe benefits for a specific locality, issued by the Department of Labor for covered contracts.',
    content: `## Overview

A Wage Determination (WD) is a schedule of minimum hourly wage rates and fringe benefit requirements issued by the Department of Labor for workers on federal contracts. Wage determinations apply under the Service Contract Act for service workers and the Davis-Bacon Act for construction workers, establishing floor compensation levels by locality and occupation.

## Why It Matters in GovCon

Wage determinations directly affect contract pricing for service and construction contracts. Contractors must incorporate applicable WD rates into their cost proposals, and failure to pay prevailing wages can result in contract termination, debarment, and back-pay liability. WD rates vary significantly by location, making accurate locality identification essential.

## Key Details

- **SAM.gov/WD**: Wage determinations are published electronically on SAM.gov (formerly the WDOL website).
- **Service Contract Act**: Applies to service contracts exceeding $2,500, covering occupations like janitors, guards, and IT support staff.
- **Davis-Bacon Act**: Applies to construction contracts exceeding $2,000, covering trades like electricians, carpenters, and laborers.
- **Locality Specific**: Rates are determined by county or metropolitan area, reflecting local labor market conditions.
- **Incorporation**: The applicable WD is incorporated into the contract and must be posted at the work site.

## Related Terms

- Service Contract Act (SCA)
- Davis-Bacon Act
- Department of Labor (DOL)
- Fringe Benefits`
  },
  {
    slug: 'ppp-public-private-partnership',
    title: 'Public-Private Partnership',
    abbreviation: 'PPP',
    category: 'Business Development',
    definition: 'A cooperative arrangement between government and private sector organizations for long-term projects, typically involving infrastructure or service delivery.',
    content: `## Overview

A Public-Private Partnership (PPP or P3) is a long-term contractual arrangement between a government entity and a private sector company to finance, build, operate, or maintain public infrastructure or deliver services. PPPs allocate risk and reward between partners based on each party's strengths, often enabling projects that neither sector could efficiently undertake alone.

## Why It Matters in GovCon

PPPs represent a growing segment of government contracting, particularly for infrastructure, energy, and facilities projects. They offer contractors long-term revenue streams but require different financial structures, risk profiles, and operational capabilities than traditional government contracts.

## Key Details

- **Risk Transfer**: PPPs shift design, construction, financing, and operational risks to the private partner in exchange for long-term revenue.
- **Revenue Models**: Partners may be compensated through availability payments, user fees, or a combination.
- **Project Types**: Common in transportation, utilities, military housing, energy, and healthcare facility projects.
- **Duration**: PPP agreements typically span 20-30 years or more to allow recovery of private investment.
- **Federal Examples**: The Military Housing Privatization Initiative is one of the largest federal PPP programs.

## How GovCon Data Can Help

GovCon Data tracks infrastructure and facilities solicitations that may include PPP structures, helping contractors identify long-term partnership opportunities with government entities.

## Related Terms

- Design-Build Contract
- Military Housing Privatization Initiative (MHPI)
- Infrastructure Investment
- Concession Agreement`
  },
  {
    slug: 'sae-service-acquisition-executive',
    title: 'Service Acquisition Executive',
    abbreviation: 'SAE',
    category: 'Personnel',
    definition: 'The senior civilian official responsible for all acquisition matters within a particular military service branch.',
    content: `## Overview

The Service Acquisition Executive (SAE) is the senior civilian acquisition official within each military department—the Army, Navy, and Air Force. The SAE serves as the department's senior procurement executive and typically holds the title of Assistant Secretary for Acquisition. The SAE has authority over acquisition policy, oversight, and major program decisions within their service.

## Why It Matters in GovCon

The SAE is the ultimate decision-maker for major acquisition programs within a military service. Understanding the SAE's priorities, acquisition reform initiatives, and programmatic decisions helps contractors align their strategies with the service's direction and anticipate changes in procurement policy.

## Key Details

- **Authority**: Serves as the Milestone Decision Authority for ACAT I programs unless authority is delegated.
- **Oversight**: Responsible for acquisition workforce management, policy implementation, and program performance.
- **Title**: Typically the Assistant Secretary of the Army/Navy/Air Force for Acquisition, Logistics, and Technology.
- **Reporting**: Reports to the Under Secretary of Defense for Acquisition and Sustainment for defense-wide acquisition matters.
- **Delegation**: May delegate MDA authority to Program Executive Officers for lower-ACAT programs.

## Related Terms

- Milestone Decision Authority (MDA)
- Program Executive Officer (PEO)
- Acquisition Category (ACAT)
- Defense Acquisition System`
  },
  {
    slug: 'coop-cooperative-purchasing',
    title: 'Cooperative Purchasing',
    category: 'Contracts',
    definition: 'The sharing of contracts between government entities to increase purchasing efficiency and leverage volume discounts.',
    content: `## Overview

Cooperative Purchasing is a procurement strategy where multiple government entities share access to competitively awarded contracts, enabling them to leverage collective buying power and reduce administrative costs. One entity conducts the competitive solicitation, and the resulting contract is made available to other participating jurisdictions under agreed terms.

## Why It Matters in GovCon

Cooperative purchasing dramatically expands the reach of a single contract award. For contractors, winning a cooperative contract with one agency can unlock sales to hundreds or thousands of additional government entities without separate competitive procurements at each location.

## Key Details

- **GSA Cooperative Purchasing**: GSA Schedule contracts are available to state and local governments for certain categories under cooperative purchasing authority.
- **Lead Agency**: One government entity conducts the competitive solicitation on behalf of all participants.
- **Legal Authority**: Each participating entity must have legal authority under their state or local procurement laws to use cooperative contracts.
- **Major Programs**: Sourcewell, OMNIA Partners, NCPA, and NASPO ValuePoint are leading cooperative purchasing organizations.
- **Piggyback Clauses**: Contracts must include language specifically authorizing use by other entities.

## How GovCon Data Can Help

GovCon Data aggregates cooperative purchasing opportunities alongside traditional solicitations, giving contractors visibility into contracts that serve multiple government buyers.

## Related Terms

- Group Purchasing Organization (GPO)
- National Cooperative Purchasing Alliance (NCPA)
- GSA Multiple Award Schedule (MAS)
- SLED (State, Local, and Education)`
  },
  {
    slug: 'fob-destination',
    title: 'FOB Destination',
    category: 'Acquisition',
    definition: 'A shipping term where the seller retains risk of loss until goods arrive at the buyer\'s location, common in government supply contracts.',
    content: `## Overview

FOB Destination (Free on Board Destination) is a shipping and delivery term specifying that the seller retains title and risk of loss for goods until they are delivered to the buyer's designated location. In government contracting, FOB Destination is the default delivery term under FAR Part 47, meaning the contractor is responsible for shipping costs, insurance, and any loss or damage during transit.

## Why It Matters in GovCon

The FOB term directly affects contract pricing, risk allocation, and inspection rights. Under FOB Destination, contractors must factor shipping, handling, and insurance costs into their pricing. Understanding this term is essential for accurate cost proposals on supply contracts.

## Key Details

- **FAR 52.247-34**: The standard clause establishing FOB Destination terms in federal contracts.
- **Risk Transfer**: Title and risk pass to the government only upon delivery and acceptance at the destination.
- **Inspection**: Under FOB Destination, the government inspects and accepts goods at the delivery point.
- **Shipping Costs**: The contractor bears all transportation costs to the destination.
- **FOB Origin Alternative**: Under FOB Origin, the government assumes risk when goods are loaded onto the carrier at the shipping point.

## Related Terms

- FAR Part 47 (Transportation)
- Delivery Order
- Inspection and Acceptance
- Contract Line Item Number (CLIN)`
  },
  {
    slug: 'ngen',
    title: 'Next Generation Enterprise Network',
    abbreviation: 'NGEN',
    category: 'Contracts',
    definition: 'A Navy contract vehicle providing enterprise IT services including end-user devices, network operations, and cybersecurity for Navy and Marine Corps.',
    content: `## Overview

The Next Generation Enterprise Network (NGEN) is the Navy's enterprise IT services contract providing network infrastructure, end-user hardware and software, cybersecurity, and service desk support for hundreds of thousands of Navy and Marine Corps users worldwide. NGEN replaced the original Navy-Marine Corps Intranet (NMCI) contract and represents one of the largest IT services contracts in the federal government.

## Why It Matters in GovCon

NGEN is among the most valuable IT contracts in the DoD portfolio, generating billions in revenue for its prime contractors and subcontractor ecosystem. The contract's scope touches virtually every aspect of enterprise IT, making it a bellwether for Navy IT strategy and a major opportunity for technology providers at all tiers.

## Key Details

- **Scope**: Covers end-user devices, network transport, data center operations, cybersecurity, collaboration tools, and service desk support.
- **Users**: Serves approximately 800,000 Navy and Marine Corps users across CONUS and OCONUS locations.
- **Multi-Award**: NGEN is structured as multiple contract awards covering different functional areas (e.g., Service Management, Integration, and Transport).
- **Cybersecurity Focus**: Includes significant cybersecurity operations and compliance with DoD cybersecurity standards.
- **Transition**: Each NGEN recompetition involves complex transition planning to maintain continuity of IT services.

## Related Terms

- Navy-Marine Corps Intranet (NMCI)
- Enterprise IT Services
- IDIQ Contract
- Navy SeaPort-NxG`
  },
  {
    slug: 'rfa-request-for-application',
    title: 'Request for Application',
    abbreviation: 'RFA',
    category: 'Solicitations',
    definition: 'A solicitation for applications, commonly used in the context of grants and cooperative agreements rather than traditional contracts.',
    content: `## Overview

A Request for Application (RFA) is a solicitation document used primarily in the grants and cooperative agreements arena, inviting qualified organizations to submit applications for funding. Unlike an RFP which seeks proposals for contracted services, an RFA typically funds research, demonstration projects, or public benefit programs where the government provides financial assistance rather than purchasing a deliverable.

## Why It Matters in GovCon

Many organizations that compete for contracts also pursue grants and cooperative agreements. Understanding the RFA process expands a firm's pipeline beyond traditional procurement to include research funding, capacity building, and program implementation opportunities from agencies like NIH, NSF, USAID, and DOE.

## Key Details

- **Grants vs. Contracts**: RFAs result in grants or cooperative agreements, not contracts; the legal and financial frameworks differ significantly.
- **Merit Review**: Applications are typically evaluated through peer or merit review panels rather than source selection boards.
- **Cost Share**: Many RFAs require applicants to contribute matching funds or cost sharing.
- **Grants.gov**: Federal RFAs are posted on Grants.gov, the central portal for federal grant opportunities.
- **Reporting**: Award recipients submit technical and financial reports but have more autonomy in how they accomplish objectives.

## How GovCon Data Can Help

GovCon Data helps contractors identify opportunities across both contract and grant channels, providing a comprehensive view of federal funding that includes RFAs alongside traditional solicitations.

## Related Terms

- Request for Proposal (RFP)
- Grants and Cooperative Agreements
- Grants.gov
- Notice of Funding Opportunity (NOFO)`
  },
  {
    slug: 'psa-procurement-services-agreement',
    title: 'Procurement Services Agreement',
    abbreviation: 'PSA',
    category: 'Acquisition',
    definition: 'An agreement establishing the terms and conditions under which procurement services will be provided between organizations.',
    content: `## Overview

A Procurement Services Agreement (PSA) is a formal arrangement between two organizations—often a government entity and a contracting services provider—that establishes the terms, fees, and procedures for conducting procurement activities on behalf of the requesting organization. PSAs enable organizations to leverage the procurement expertise and contract vehicles of another entity.

## Why It Matters in GovCon

PSAs are the mechanism behind many assisted acquisition and shared services arrangements in government. When an agency lacks contracting capacity or needs access to specialized contract vehicles, it enters a PSA with a servicing agency. Contractors should understand PSAs because they affect who holds contracting authority and which regulations apply.

## Key Details

- **Assisted Acquisitions**: PSAs formalize the relationship when one agency performs acquisitions on behalf of another.
- **Fee Structure**: The servicing organization typically charges an administrative fee, often a percentage of the contract value.
- **Roles and Responsibilities**: PSAs define which organization manages requirements, evaluation, award, and administration.
- **Legal Authority**: Assisted acquisitions under PSAs must comply with the Economy Act or other specific statutory authority.
- **Examples**: GSA's assisted acquisition services and DoD's Defense Contract Management Agency operate under PSA frameworks.

## Related Terms

- Assisted Acquisition
- Economy Act Orders
- Interagency Agreement
- Contracting Officer`
  },
]

export function getAllTerms(): GlossaryTerm[] {
  return glossaryTerms
}

export function getTermBySlug(slug: string): GlossaryTerm | undefined {
  return glossaryTerms.find((term) => term.slug === slug)
}

export function getTermsByCategory(category: string): GlossaryTerm[] {
  return glossaryTerms.filter((term) => term.category === category)
}

export function getTermsByLetter(letter: string): GlossaryTerm[] {
  const upperLetter = letter.toUpperCase()
  return glossaryTerms.filter((term) => {
    const firstChar = term.title.charAt(0).toUpperCase()
    if (upperLetter === '#') {
      return /[^A-Z]/.test(firstChar)
    }
    return firstChar === upperLetter
  })
}

export function searchTerms(query: string): GlossaryTerm[] {
  const lower = query.toLowerCase()
  return glossaryTerms.filter(
    (term) =>
      term.title.toLowerCase().includes(lower) ||
      (term.abbreviation && term.abbreviation.toLowerCase().includes(lower)) ||
      term.definition.toLowerCase().includes(lower)
  )
}

export function getAvailableLetters(): string[] {
  const letters = new Set<string>()
  glossaryTerms.forEach((term) => {
    const first = term.title.charAt(0).toUpperCase()
    if (/[A-Z]/.test(first)) {
      letters.add(first)
    } else {
      letters.add('#')
    }
  })
  return Array.from(letters).sort((a, b) => {
    if (a === '#') return -1
    if (b === '#') return 1
    return a.localeCompare(b)
  })
}
